/**
 * CaseDetail.jsx
 *
 * Full case detail view using the AE Tracker design system.
 * Matches the structure from case.html.
 */

import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { getCase, getCaseEvents, getComments, patchCase, deleteAttachment, deleteCase, restoreCase, uploadCaseAttachment, getReferenceStaff, addCaseRequirement, deleteCaseRequirement, completeCaseRequirement, getCaseShares, shareCaseWith, revokeCaseShare, waiveRequirement, unwaiveRequirement, getCaseSla, slaAction } from '../api/client.js';
import { getTherapieMapsData } from '../api/blobClient.js';
import { clinicMeta } from '../utils/clinicOptions.jsx';
// Direct single-icon import (not the barrel) so only this glyph ships in the bundle.
import { NotificationIcon } from '@phosphor-icons/react/dist/csr/Notification';
import { BellIcon } from '@phosphor-icons/react/dist/csr/Bell';
import { TagIcon } from '@phosphor-icons/react/dist/csr/Tag';
import { FoldersIcon } from '@phosphor-icons/react/dist/csr/Folders';
import { ProhibitIcon } from '@phosphor-icons/react/dist/csr/Prohibit';
import { ArrowCounterClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowCounterClockwise';
import { TimerIcon } from '@phosphor-icons/react/dist/csr/Timer';
import { PauseCircleIcon } from '@phosphor-icons/react/dist/csr/PauseCircle';
import { PlayCircleIcon } from '@phosphor-icons/react/dist/csr/PlayCircle';
import { CheckCircleIcon } from '@phosphor-icons/react/dist/csr/CheckCircle';
import { WarningIcon } from '@phosphor-icons/react/dist/csr/Warning';
import { BellSlashIcon } from '@phosphor-icons/react/dist/csr/BellSlash';
import { StopCircleIcon } from '@phosphor-icons/react/dist/csr/StopCircle';
import { ThumbsUpIcon } from '@phosphor-icons/react/dist/csr/ThumbsUp';
import { MoonIcon } from '@phosphor-icons/react/dist/csr/Moon';
import { slaApplies, buildSlaChip, buildSlaHistoryLines, slaActionsFor } from '../lib/sla.js';
import wordIconUrl from '../assets/file_word.svg';
import { useAuth } from '../auth/AuthContext.jsx';
import { useAppConfig } from '../contexts/AppConfigContext.jsx';
import { filterByEventType, itemAvailableForEventType, itemAvailableForTreatmentType } from '../lib/configFilters.js';
import StatusBadge, { SeverityBadge, TreatmentBadge } from '../components/StatusBadge.jsx';
import CaseTags from '../components/CaseTags.jsx';
import StaffPicker from '../components/StaffPicker.jsx';
import SearchableSelect from '../components/SearchableSelect.jsx';
import PlainDropdown from '../components/PlainDropdown.jsx';
import DateInput, { todayIso } from '../components/DateInput.jsx';
import Comments from '../components/Comments.jsx';
import Costs from '../components/Costs.jsx';
import CostChip from '../components/CostChip.jsx';
import FilePreview, { canPreview } from '../components/FilePreview.jsx';
import { saveUrlToDisk } from '../lib/saveToDisk.js';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import { ATTACHMENT_CATEGORY_LABELS, TYPED_TEXT_CATEGORIES, getRequirements, caseDocumentSlots, treatmentHasCodes, FIELD_META, CLINIC_INCIDENT_PERSON_STATUS, CLINIC_INCIDENT_TYPES, NEAR_MISS_CATEGORIES, nearMissCategoryOptions, NEAR_MISS_REPORTING_FUNCTIONS, NEAR_MISS_SEVERITY_TIERS, categoryNeedsMetadata } from '../lib/eventVocabularies.js';
import { isRefusalRegisterCase, refusalDateOf, REFUSAL_TS_KEYS, REFUSAL_REGISTER_CATEGORY } from '@shared/refusalRegister.js';
import { incidentFieldLabel, formatIncidentFieldValue, outstandingIncidentFields, isOptionalIncidentField } from '../lib/incidentFields.js';
import { getWaivers, isWaived, isWaivable } from '../lib/waivers.js';
import { attachmentMetadataLine, historyFileMetaLines, orderedIncidentKeys, reportIncidentFieldRows } from '../lib/attachmentMetadata.js';
import SlotUploadModal from '../components/SlotUploadModal.jsx';
import SlotStateIcon from '../components/SlotStateIcon.jsx';
import { UploadIcon, PencilSquareIcon } from '../components/SlotActionIcons.jsx';
import YesNoSwitch, { coerceYesNo } from '../components/YesNoSwitch.jsx';
import { formatDate, formatDateTime } from '../lib/dates.js';
import { richToText } from '../lib/richText.js';
import { CharCountInput, CharCountTextarea } from '../components/CharCountField.jsx';
import DateTimeExtraField from '../components/DateTimeExtraField.jsx';
import { getIncidentCategories } from '../lib/eventConfig.js';
import {
  subtypesForTreatment,
  SUBTYPE_OTHER,
  resolveTreatmentSubtypeValue,
  treatmentSubtypeToForm,
} from '@shared/incidentCategoryCascade.js';
import { buildUploadFile, categoryToken, parseAttachmentName } from '../lib/attachmentNaming.js';
import TypedTextFileModal from '../components/TypedTextFileModal.jsx';
import {
  REQ_OTHER_KEY,
  REQ_KIND_SIGNOFF,
  reqCategory,
  requirementKind,
  isSignoffDone,
  getRequirements as getCaseRequirements,
  canonicalCategoryForRequirement,
} from '@shared/requirements.js';

function addMonths(date, n) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}

// formatDate / formatDateTime come from lib/dates.js (shared with Comments).

// ── Attachment helpers ─────────────────────────────────────────────────────────

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'svg']);

function fileExt(name = '') {
  return (name.split('.').pop() || '').toLowerCase();
}

function isImage(name) {
  return IMAGE_EXTS.has(fileExt(name));
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Renders a single attachment.
 * Uses contentUrl directly — the browser sends SharePoint session cookies
 * automatically after MSAL login, so no extra auth headers are needed.
 *
 * Images are shown inline; other files open in SharePoint via a button.
 */
// ── Lightbox ───────────────────────────────────────────────────────────────────

function Lightbox({ src, label, onClose }) {
  // Close on Escape key
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '24px',
        cursor: 'zoom-out',
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          position: 'absolute', top: 16, right: 16,
          display: 'flex', gap: 8, alignItems: 'center',
        }}
        onClick={e => e.stopPropagation()}
      >
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#fff', fontSize: '12px', opacity: 0.7,
            textDecoration: 'none', padding: '4px 8px',
            border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4,
          }}
        >
          Open full size ↗
        </a>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#fff', fontSize: 24, lineHeight: 1, padding: '0 4px',
            opacity: 0.8,
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Image */}
      <img
        src={src}
        alt={label}
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '90vw',
          maxHeight: '85vh',
          objectFit: 'contain',
          borderRadius: 6,
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
          cursor: 'default',
        }}
      />

      {/* Caption */}
      {label && (
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: 12 }}>
          {label}
        </div>
      )}
    </div>
  );
}

function friendlyName(name = '', fieldName = '') {
  if (name.startsWith('Reserved_ImageAttachment')) return fieldName || 'Photo';
  return name;
}

// ── File type icons ────────────────────────────────────────────────────────────

// ── Branded file type icon ─────────────────────────────────────────────────────
// Maps file extension → /filetypes/{name}.svg from Branding/icons/SVG.
// CSV uses the XLS icon. Images use their own icon (no sensitive thumbnail).

const EXT_ICON_MAP = {
  jpg: 'jpg', jpeg: 'jpg',
  png: 'png',
  gif: 'gif',
  svg: 'svg',
  bmp: 'png',    // closest available
  webp: 'png',   // closest available
  pdf: 'pdf',
  doc: 'doc', docx: 'doc',
  xls: 'xls', xlsx: 'xls',
  csv: 'xls',    // user preference: CSV uses XLS icon
  txt: 'txt',
  xml: 'xml',
  zip: 'zip',
  rar: 'rar',
  mp3: 'mp3',
  wav: 'wav',
  ppt: 'ppt', pptx: 'ppt',
};

function FileTypeIcon({ name, size = 36 }) {
  const ext  = fileExt(name);
  const slug = EXT_ICON_MAP[ext] || null;
  if (slug) {
    return (
      <img
        src={`/filetypes/${slug}.svg`}
        alt={ext}
        style={{ width: size, height: size, flexShrink: 0, display: 'block' }}
        draggable={false}
      />
    );
  }
  // Fallback: generic grey file shape
  return (
    <svg style={{ width: size, height: size, flexShrink: 0 }} viewBox="0 0 36 36" fill="none">
      <rect width="36" height="36" rx="8" fill="#94a3b8"/>
      <path d="M11 8h10l5 5v16a1 1 0 0 1-1 1H11a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"
        fill="white" fillOpacity=".15" stroke="white" strokeOpacity=".4" strokeWidth="1.5"/>
      <path d="M21 8v5h5" stroke="white" strokeOpacity=".4" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M13 18h10M13 21h8M13 24h5" stroke="white" strokeOpacity=".7" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

// ── Document preview modal ─────────────────────────────────────────────────────

function DocPreview({ url, name, caseId, onClose }) {
  const ext   = fileExt(name);
  const isPdf  = ext === 'pdf';
  const isWord = ['doc', 'docx'].includes(ext);

  // Shared blob state — used differently per type
  const [blobUrl,   setBlobUrl]   = useState(null);  // PDF iframe src
  const [docHtml,   setDocHtml]   = useState(null);  // DOCX rendered HTML
  const [loading,   setLoading]   = useState(isPdf || isWord);
  const [loadError, setLoadError] = useState(false);
  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') close(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const isSasUrl = url && url.includes('.blob.core.windows.net');

  useEffect(() => {
    if (!isPdf && !isWord) return;

    let revokable = null;
    setLoading(true);
    setLoadError(false);

    async function load() {
      let blob;
      if (isSasUrl) {
        // Blob Storage SAS URL — fetch directly without auth headers
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        blob = await res.blob();
      } else {
        // Legacy SP path
        if (!caseId) throw new Error('No case ID for SP attachment fetch');
        blob = await getAttachmentBlob(caseId, name);
        if (!blob) throw new Error('Blob fetch returned null');
      }

      if (isPdf) {
        const u = URL.createObjectURL(blob);
        revokable = u;
        setBlobUrl(u);
      } else {
        const mammoth = await import('mammoth');
        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocHtml(result.value);
      }
    }

    load()
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));

    return () => { if (revokable) URL.revokeObjectURL(revokable); };
  }, [isPdf, isWord, isSasUrl, url, caseId, name]);

  const btnStyle = {
    color: 'rgba(255,255,255,0.8)', fontSize: 12, textDecoration: 'none',
    padding: '5px 12px', border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 6, whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.07)',
  };

  const docxFallback = (
    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', padding: 32 }}>
      <FileTypeIcon name={name} size={56} />
      <p style={{ marginTop: 12, fontSize: 14 }}>Could not load preview</p>
      <a href={url} download={name} style={{ color: '#60A5FA', fontSize: 13 }}>Download instead</a>
    </div>
  );

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.9)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Toolbar */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.4)',
          flexShrink: 0,
        }}
      >
        <FileTypeIcon name={name} size={28} />
        <span style={{ color: '#fff', flex: 1, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </span>
        <a href={url} target="_blank" rel="noopener noreferrer" style={btnStyle}>
          Open in SharePoint ↗
        </a>
        <a href={url} download={name} style={btnStyle}>
          Download ↓
        </a>
        <button
          onClick={close}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', fontSize: 24, lineHeight: 1, padding: '0 4px', opacity: 0.7 }}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div
        onClick={e => e.stopPropagation()}
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
      >
        {loading ? (
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>Loading preview…</p>
        ) : loadError ? (
          docxFallback
        ) : isPdf && blobUrl ? (
          <iframe
            src={blobUrl}
            title={name}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        ) : isWord && docHtml ? (
          /* DOCX rendered as HTML in a white sandboxed iframe */
          <iframe
            srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>
              body { font-family: Calibri, Georgia, serif; font-size: 14px; line-height: 1.7;
                     margin: 0; padding: 32px 48px; color: #1a1a1a; max-width: 820px; }
              h1,h2,h3 { font-family: 'Calibri Light', sans-serif; }
              table { border-collapse: collapse; width: 100%; }
              td, th { border: 1px solid #ccc; padding: 6px 10px; }
            </style></head><body>${docHtml}</body></html>`}
            sandbox="allow-same-origin"
            title={name}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        ) : (
          docxFallback
        )}
      </div>
    </div>
  );
}

function AttachmentItem({ attachment }) {
  const { name, size, contentUrl, _fieldName, uploadedBy, uploadedAt, isPublic, metadata } = attachment;
  const label       = friendlyName(name, _fieldName);
  // "By · date · taken/trainer line" — ONE clamped row so the card never grows;
  // the full text rides on the title. Upload metadata shows wherever the file
  // does (slot rows, comment pills, History, and this grid).
  const uploadMeta  = [
    uploadedBy,
    uploadedAt ? formatDate(uploadedAt) : null,
    attachmentMetadataLine(metadata, { formatDate }),
  ].filter(Boolean).join(' · ');
  const previewable = contentUrl && canPreview(name);
  // isPublic===false means the parent comment is private; null means case-level (no restriction)
  const isPrivate   = isPublic === false;
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Previewable files open the modal (Download lives in its toolbar); files
  // the previewer can't render (e.g. videos) download to disk on click so
  // they stay reachable without a per-card Download button.
  async function onCardClick() {
    if (previewable) { setOpen(true); return; }
    if (!contentUrl || downloading) return;
    setDownloading(true);
    try {
      await saveUrlToDisk(contentUrl, name);
    } catch {
      window.open(contentUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      {open && contentUrl && (
        <FilePreview url={contentUrl} name={name} displayName={label} onClose={() => setOpen(false)} />
      )}

      <div
        className="att-item"
        title={previewable
          ? `Click to preview · ${uploadMeta || label}`
          : (contentUrl ? `Click to download · ${uploadMeta || label}` : (uploadMeta || undefined))}
        style={{
          cursor:       previewable ? 'zoom-in' : (contentUrl ? (downloading ? 'wait' : 'pointer') : 'default'),
          borderLeft:   isPrivate ? '3px solid #F59E0B' : undefined,
          background:   isPrivate ? 'rgba(245,158,11,0.04)' : undefined,
        }}
        onClick={onCardClick}
      >
        {/* Preview area */}
        <div className={`att-thumb${isImage(name) && contentUrl ? " is-photo" : ""}`} style={{
          height: 120, position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--surface-1)',
          borderRadius: '6px 6px 0 0',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          {isImage(name) && contentUrl ? (
            <img
              className="att-thumb-img"
              src={contentUrl}
              alt={label}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              draggable={false}
            />
          ) : (
            <FileTypeIcon name={name} size={54} />
          )}
          {/* Private lock badge */}
          {isPrivate && (
            <div style={{
              position: 'absolute', top: 6, right: 6,
              background: 'rgba(245,158,11,0.9)',
              borderRadius: 4, padding: '2px 5px',
              display: 'flex', alignItems: 'center', gap: 3,
              fontSize: 9, fontWeight: 700, color: '#fff', letterSpacing: '.04em',
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" style={{ width: 9, height: 9 }}>
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              PRIVATE
            </div>
          )}
        </div>

        {/* Info — fills the fixed card height; every line clamps to a single
            ellipsized row so content can never change the card size. No
            per-card Download button: preview's toolbar has one, and
            non-previewable cards download on click. */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
          padding: '10px 10px 12px', gap: 2,
          flex: 1, minHeight: 0, width: '100%',
        }}>
          <div className="att-name" title={label} style={{ width: '100%', fontSize: 12 }}>{label}</div>
          <div className="att-meta" style={{ maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {downloading ? 'Downloading…' : formatBytes(size)}
          </div>
          {uploadMeta && (
            <div className="att-meta" title={uploadMeta}
              style={{ fontSize: 10, maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {uploadMeta}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Info row ───────────────────────────────────────────────────────────────────

/** A single info-list row */
function InfoRow({ label, value, mono = false, emptyText = '—' }) {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="info-row">
      <div className="k" title={typeof label === 'string' ? label : undefined}>{label}</div>
      <div className={`v${mono ? ' mono' : ''}`}>
        {isEmpty ? <span className="muted">{emptyText}</span> : value}
      </div>
    </div>
  );
}

/** Muted inline "(optional)" after a matrix-optional field's label — plain
 *  text that flows with the label (a chip broke the label column's alignment
 *  and wrapped to its own line). Never counts as outstanding. */
function OptionalMark() {
  return (
    <span title="Optional — never counts as outstanding"
      style={{ color: 'var(--text-3)', fontWeight: 400, fontSize: '0.92em', whiteSpace: 'nowrap' }}>
      {' '}(optional)
    </span>
  );
}

/**
 * One editable incident-specific field, typed by FIELD_META[fieldKey].type.
 * Value is stored raw in the type_specific bag (checkbox → boolean, yesno →
 * null|boolean, others → string). `onChange` receives the new raw value.
 * `optional` adds the muted inline "(optional)" mark (matrix optionalFields).
 *
 * The yes/no switch is the shared components/YesNoSwitch (same control the
 * wizard renders); it reads legacy 'true'/'false' strings itself. Its
 * radiogroup is named via aria-labelledby — a <label htmlFor> cannot target a
 * div — while every other control gets a real id + htmlFor pair.
 */
function IncidentExtraField({ fieldKey, value, onChange, optional = false }) {
  const meta = FIELD_META[fieldKey] || {};
  const label = incidentFieldLabel(fieldKey);
  const labelNode = <>{label}{optional && <OptionalMark />}</>;
  const id      = `edit-ts-${fieldKey}`;
  const labelId = `${id}-label`;

  if (meta.type === 'checkbox') {
    return (
      <div className="field">
        <label htmlFor={id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
          <input id={id} type="checkbox" style={{ marginTop: 2 }}
            checked={coerceYesNo(value) === true}
            onChange={e => onChange(e.target.checked)} />
          <span>{labelNode}</span>
        </label>
      </div>
    );
  }

  if (meta.type === 'yesno') {
    return (
      <div className="field">
        <label id={labelId}>{labelNode}</label>
        <YesNoSwitch id={id} ariaLabelledby={labelId} value={value} onChange={onChange} />
      </div>
    );
  }

  return (
    <div className="field">
      <label htmlFor={id}>{labelNode}</label>
      {meta.type === 'textarea' ? (
        <CharCountTextarea id={id} fieldKey={fieldKey} value={value || ''} style={{ minHeight: 72 }}
          onChange={e => onChange(e.target.value)} />
      ) : meta.type === 'select' ? (
        <SearchableSelect
          id={id}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Select…"
          options={(meta.options || []).map(o => ({ value: o, label: o }))}
        />
      ) : meta.type === 'date' ? (
        <DateInput id={id} value={value || ''} onChange={e => onChange(e.target.value)} />
      ) : meta.type === 'datetime-local' ? (
        <DateTimeExtraField fieldKey={fieldKey} value={value || ''}
          onChange={(k, v) => onChange(v)} />
      ) : (
        <CharCountInput id={id} fieldKey={fieldKey} type="text" value={value || ''}
          onChange={e => onChange(e.target.value)} />
      )}
    </div>
  );
}

/**
 * Clinic / Region / Country pickers for the Case-information modal.
 *
 * All three are dropdowns over the TherapieMaps directory — never free text. A
 * typo in a hand-typed clinic or region silently detaches the case from that
 * clinic's stats, its HoRO routing and the region filters, and nothing
 * downstream catches it.
 *
 * Picking a clinic stamps Region + Country from its directory row (same rule as
 * the New Case wizard). Region/Country stay selectable — region re-orgs and
 * off-directory legacy clinics both need it — but only over values that exist
 * in the directory, and a value that disagrees with the chosen clinic is called
 * out with a one-click fix.
 *
 * `clinics` is null until the directory loads; `loadError` disables the whole
 * group rather than falling back to free text.
 */
function CaseLocationFields({ draft, onChange, clinics, loadError }) {
  const rows      = clinics || [];
  const dirLoaded = !!clinics && !loadError;
  const locked    = !dirLoaded;

  const dirClinic = rows.find(
    c => (c.name || '').toLowerCase() === (draft.Clinic || '').trim().toLowerCase(),
  ) || null;

  // Pin the current value as an option when the directory doesn't carry it
  // verbatim, so a legacy / renamed clinic can't be blanked just by opening the
  // modal. Only flag it as off-directory when nothing matches even loosely —
  // a casing-only difference is normalised by re-picking the directory entry.
  const withCurrent = (options, current, group) => {
    const val = (current || '').trim();
    if (!val || options.some(o => o.value === val)) return options;
    const knownLoosely = options.some(o => o.value.toLowerCase() === val.toLowerCase());
    return [
      {
        value: val,
        label: val,
        sub:   dirLoaded && !knownLoosely ? 'Not in the clinic directory' : undefined,
        group,
      },
      ...options,
    ];
  };

  const clinicOptions = withCurrent(
    // Pending clinics (not yet open in Maps) are not offered; withCurrent still
    // shows a case's existing value even when the clinic is not in this list.
    rows.filter(c => c && c.name && (c.clinicStatus || '').toLowerCase() !== 'pending')
      .sort((a, b) =>
        (a.country || '').localeCompare(b.country || '') ||
        (a.name    || '').localeCompare(b.name    || ''))
      .map(c => ({
        value: c.name,
        label: c.name,
        sub:   [c.region, c.address].filter(Boolean).join(' · '),
        group: c.country || 'Other',
        meta:  clinicMeta(c),
      })),
    draft.Clinic,
    'Current value',
  );

  const distinct = (key) => [...new Set(
    rows.map(c => (c[key] || '').trim()).filter(Boolean),
  )].sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));

  const regionOptions  = withCurrent(distinct('region'),  draft.Region);
  const countryOptions = withCurrent(distinct('country'), draft.Country);

  // Stamp Region/Country from the picked clinic; keep the existing value when
  // the directory row is missing one rather than blanking it.
  const pickClinic = (name) => {
    const row = rows.find(c => c.name === name);
    onChange({
      Clinic:  name,
      Region:  row?.region  || draft.Region  || '',
      Country: row?.country || draft.Country || '',
    });
  };

  // Warn when a hand-picked Region/Country disagrees with the chosen clinic's
  // directory row — a deliberate override is fine, a stale one shouldn't be silent.
  const mismatchHint = (field, dirValue) => {
    if (!dirClinic || !dirValue || (draft[field] || '').trim() === dirValue) return null;
    return (
      <div className="field-hint" style={{ color: 'var(--warn, #b45309)' }}>
        Directory says <strong>{dirValue}</strong> for this clinic.{' '}
        <button type="button" className="btn ghost sm"
          style={{ display: 'inline', padding: '0 2px' }}
          onClick={() => onChange({ [field]: dirValue })}>Use it</button>
      </div>
    );
  };

  return (
    <>
      {loadError && (
        <div className="callout danger" style={{ marginBottom: 14 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <strong>Could not load the clinic directory</strong> — {loadError}. Clinic,
            Region and Country can&rsquo;t be changed until it loads.
          </div>
        </div>
      )}

      <div className="field">
        <label htmlFor="edit-clinic">Clinic</label>
        <SearchableSelect
          id="edit-clinic" name="Clinic"
          value={draft.Clinic || ''}
          onChange={e => pickClinic(e.target.value)}
          options={clinicOptions}
          disabled={locked}
          placeholder={loadError ? 'Unavailable' : (dirLoaded ? 'Select clinic…' : 'Loading clinics…')}
          noOptionsLabel="No clinics found"
        />
        <div className="field-hint">
          {dirClinic
            ? [dirClinic.region, dirClinic.country, dirClinic.address].filter(Boolean).join(' · ')
            : 'Region and Country are stamped from the clinic directory.'}
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="edit-region">Region</label>
          <SearchableSelect
            id="edit-region" name="Region"
            value={draft.Region || ''}
            onChange={e => onChange({ Region: e.target.value })}
            options={regionOptions}
            disabled={locked}
            placeholder={loadError ? 'Unavailable' : (dirLoaded ? 'Select region…' : 'Loading…')}
            noOptionsLabel="No regions found"
          />
          {mismatchHint('Region', dirClinic?.region)}
        </div>
        <div className="field">
          <label htmlFor="edit-country">Country</label>
          <SearchableSelect
            id="edit-country" name="Country"
            value={draft.Country || ''}
            onChange={e => onChange({ Country: e.target.value })}
            options={countryOptions}
            disabled={locked}
            placeholder={loadError ? 'Unavailable' : (dirLoaded ? 'Select country…' : 'Loading…')}
            noOptionsLabel="No countries found"
          />
          {mismatchHint('Country', dirClinic?.country)}
        </div>
      </div>
    </>
  );
}

/** Recipient emails of a `notification_sent` event — visible by default,
 *  clamped to two chip rows with a Show-all toggle only when they overflow. */
// Two rows of 11px chips (3px vertical padding) plus the 6px row gap.
const NOTIF_RECIPIENTS_COLLAPSED_MAX = 52;

function NotificationRecipients({ recipients }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const wrapRef = useRef(null);
  const list = (Array.isArray(recipients) ? recipients : []).filter(Boolean);

  // Measure only while clamped: expanded content never overflows, and the
  // collapsed measurement is what decides whether a toggle is needed at all.
  useLayoutEffect(() => {
    if (expanded) return;
    const el = wrapRef.current;
    if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [list.length, expanded]);

  if (!list.length) return null;
  return (
    <div style={{ marginTop: 8, marginLeft: 36 }}>
      <div
        ref={wrapRef}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 6,
          maxHeight: expanded ? 'none' : NOTIF_RECIPIENTS_COLLAPSED_MAX,
          overflow: 'hidden',
        }}
      >
        {list.map((email, i) => (
          <span key={i} style={{
            display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20,
            background: 'var(--surface)', border: '1px solid var(--border)',
            fontSize: 11, color: 'var(--text-2)', overflowWrap: 'anywhere',
          }}>{email}</span>
        ))}
      </div>
      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded(o => !o)}
          aria-expanded={expanded}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginTop: 6,
            fontSize: 11.5, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
            <polyline points="9 18 15 12 9 6" />
          </svg>
          {expanded ? 'Show fewer' : `Show all ${list.length} recipients`}
        </button>
      )}
    </div>
  );
}

// ── Timeline event day-dots ──────────────────────────────────────────────────
// Group events by calendar day, then evenly space each day's dot within its
// month segment so dots never pile up and always have comfortable hover targets.

function buildDayDots(events) {
  // One dot per calendar day, positioned at the actual time consumed on the bar.
  const dayMap = new Map();
  for (const ev of events) {
    const key = ev.d.toISOString().slice(0, 10);
    if (!dayMap.has(key)) dayMap.set(key, { key, d: ev.d, items: [], pcts: [] });
    dayMap.get(key).items.push(ev);
    dayMap.get(key).pcts.push(ev.pct);
  }

  const dots = [...dayMap.values()]
    .map(g => ({
      key:   g.key,
      d:     g.d,
      items: g.items,
      pct:   g.pcts.reduce((s, p) => s + p, 0) / g.pcts.length,
    }))
    .sort((a, b) => a.pct - b.pct);

  // Spread pass: if two adjacent dots are closer than MIN_GAP, nudge the older
  // one (left/earlier) back far enough to restore hover clearance.
  // Newer dot stays at its real position; only shift left when space allows.
  const MIN_GAP = 1.8; // % of bar — ~7–10px at typical widths
  for (let i = dots.length - 2; i >= 0; i--) {
    const gap = dots[i + 1].pct - dots[i].pct;
    if (gap < MIN_GAP) {
      dots[i] = { ...dots[i], pct: Math.max(0.2, dots[i + 1].pct - MIN_GAP) };
    }
  }

  return dots;
}

function DayDot({ group, tooltipBox, fmt }) {
  const [open, setOpen] = useState(false);
  const n     = group.items.length;
  const color = '#64748b';   // neutral slate — distinct from month milestone gradient dots
  const label = `${fmt(group.d)} · ${n} event${n > 1 ? 's' : ''}`;
  // Tooltips near either end of the track hug that end instead of centring on
  // the dot — a centred box would run past the card edge and get clipped.
  const tipAlign = group.pct < 10 ? { left: 0 } : group.pct > 90 ? { right: 0 } : { left: '50%', transform: 'translateX(-50%)' };

  return (
    <div
      style={{ position: 'absolute', left: `clamp(8px, ${group.pct}%, calc(100% - 8px))`, top: '50%', zIndex: open ? 10 : 4 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div style={{ position: 'absolute', transform: 'translate(-50%, calc(-50% - 13px))', cursor: 'default' }}>
        {/* Dot */}
        <div style={{
          width: 7, height: 7, borderRadius: '50%',
          background: color,
          border: '2px solid var(--surface)',
          boxShadow: `0 0 0 2px ${color}28`,
        }} />
        {/* Stem */}
        <div style={{
          position: 'absolute', left: '50%', top: '100%',
          transform: 'translateX(-50%)',
          width: 1, height: 11,
          background: `linear-gradient(to bottom, ${color}60, transparent)`,
          pointerEvents: 'none',
        }} />
        {/* Tooltip */}
        {open && (
          <div style={{ ...tooltipBox, bottom: 'calc(100% + 4px)', whiteSpace: 'nowrap', ...tipAlign }}>
            {label}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Legal timeline ─────────────────────────────────────────────────────────────
// 6-month bar from date of incident to legal threshold.
// Gradient: brand teal → amber → danger red → Code 5 crimson.

const TIMELINE_GRADIENT = 'linear-gradient(to right, #0D9488 0%, #F59E0B 40%, #DC2626 72%, #7F1D1D 100%)';
// Dot accent colours sampled from the gradient at each month position (1–5)
const DOT_COLORS = ['#0D9488', '#D97706', '#F97316', '#DC2626', '#B91C1C'];

function CaseLegalTimeline({ startDate, months = 6, caseId, refreshKey, leading = null, closed = false }) {
  const [tooltip,  setTooltip]  = useState(null);
  const [evtDots,  setEvtDots]  = useState([]);

  useEffect(() => {
    if (!caseId) return;
    getCaseEvents(caseId)
      .then(data => setEvtDots(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [caseId, refreshKey]);

  if (!startDate) return null;
  const base = new Date(startDate);
  if (isNaN(base)) return null;

  const legal   = addMonths(base, months);
  const now     = new Date();
  const totalMs = legal - base;
  const elapsed = Math.min(Math.max(now - base, 0), totalMs);
  const progress = (elapsed / totalMs) * 100;
  const isPast  = now > legal;
  // A closed case past its threshold is history, not an alarm — mute the reds.
  const alarm   = isPast && !closed;

  const fmt     = d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const fmtTime = d => d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // Position events that fall within [base, legal] on the track
  const eventsOnTrack = evtDots
    .filter(ev => ev.createdAt)
    .map(ev => {
      const d   = new Date(ev.createdAt);
      const pct = ((d - base) / totalMs) * 100;
      return { ...ev, d, pct };
    })
    .filter(ev => ev.pct >= 0 && ev.pct <= 100)
    .sort((a, b) => a.pct - b.pct);

  // Generate up to 5 evenly-spaced milestone dots (excluding start and threshold itself)
  const numDots = Math.min(months - 1, 5);
  const dotStep = months / (numDots + 1);
  const dots = Array.from({ length: numDots }, (_, i) => {
    const m = Math.round(dotStep * (i + 1));
    return {
      key: m,
      date: addMonths(base, m),
      pct: (m / months) * 100,
      passed: now >= addMonths(base, m),
      color: DOT_COLORS[i] || DOT_COLORS[DOT_COLORS.length - 1],
    };
  });

  const tooltipBox = {
    position: 'absolute',
    bottom: 'calc(100% + 10px)',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '5px 10px',
    fontSize: 11,
    whiteSpace: 'nowrap',
    color: 'var(--text)',
    boxShadow: 'var(--shadow-md)',
    pointerEvents: 'none',
    zIndex: 10,
  };

  return (
    <div className="case-clocks">
      {/* Clocks row — the response-SLA chip (open coded cases) leads on the
          left, the legal-threshold countdown sits on the right; the six-month
          track runs underneath. Two clocks, one row. */}
      <div className="case-clocks-row">
        <div className="case-clocks-lead">
          {leading || <span className="tl-label">Legal threshold</span>}
        </div>
        {/* Track area — takes whatever width is left on the row; its top padding
            clears the dot tooltips, and its height is balanced so the bar sits
            level with the chip beside it. */}
        <div className="case-clocks-track">
        <div style={{ position: 'relative', padding: '10px 0' }}>
        {/* Track background */}
        <div style={{
          position: 'relative',
          height: 5,
          background: 'var(--surface-3)',
          borderRadius: 100,
          overflow: 'visible',
        }}>
          {/* Gradient fill — always full-width, clipped to progress */}
          <div style={{
            position: 'absolute', inset: 0,
            background: TIMELINE_GRADIENT,
            borderRadius: 100,
            clipPath: `inset(0 ${100 - progress}% 0 0 round 100px)`,
            transition: 'clip-path .7s cubic-bezier(0.32,0.72,0,1)',
          }} />

          {/* Start dot — inside the track's left end so its ring lines up with
              the chip and badges above instead of overhanging the edge. */}
          <div style={{
            position: 'absolute', left: 2, top: '50%',
            transform: 'translate(0,-50%)',
            width: 9, height: 9, borderRadius: '50%',
            background: '#0D9488',
            border: '2px solid var(--surface)',
            boxShadow: '0 0 0 2px rgba(13,148,136,.2)',
            zIndex: 2,
          }} />

          {/* Monthly milestone dots */}
          {dots.map(dot => (
            <div
              key={dot.key}
              style={{
                position: 'absolute',
                left: `${dot.pct}%`, top: '50%',
                transform: 'translate(-50%,-50%)',
                zIndex: 3,
              }}
              onMouseEnter={() => setTooltip(dot.key)}
              onMouseLeave={() => setTooltip(null)}
            >
              <div style={{
                width: 11, height: 11, borderRadius: '50%',
                background: dot.passed ? dot.color : 'var(--surface)',
                border: `2px solid ${dot.passed ? dot.color : 'var(--border)'}`,
                boxShadow: dot.passed ? `0 0 0 3px ${dot.color}28` : 'none',
                transition: 'all .25s var(--ease)',
                cursor: 'default',
              }} />
              {tooltip === dot.key && (
                <div style={{ ...tooltipBox, left: '50%', transform: 'translateX(-50%)' }}>
                  <span style={{ color: 'var(--text-3)' }}>Month {dot.key} · </span>
                  <strong style={{ color: dot.color }}>{fmt(dot.date)}</strong>
                </div>
              )}
            </div>
          ))}

          {/* History event dots — one per day, evenly spaced within each month */}
          {buildDayDots(eventsOnTrack).map(group => (
            <DayDot key={group.key} group={group} tooltipBox={tooltipBox} fmt={fmt} />
          ))}

          {/* Legal / Code 5 endpoint */}
          <div
            style={{
              position: 'absolute', right: 2, top: '50%',
              transform: 'translate(0,-50%)',
              zIndex: 3,
            }}
            onMouseEnter={() => setTooltip('legal')}
            onMouseLeave={() => setTooltip(null)}
          >
            <div style={{
              width: 13, height: 13, borderRadius: '50%',
              background: alarm ? '#7F1D1D' : 'var(--surface)',
              border: `2.5px solid ${alarm ? '#7F1D1D' : 'var(--border-strong)'}`,
              boxShadow: alarm ? '0 0 0 3px rgba(127,29,29,.2)' : 'none',
              cursor: 'default',
              transition: 'all .25s var(--ease)',
            }} />
            {tooltip === 'legal' && (
              <div style={{ ...tooltipBox, right: 0 }}>
                <span style={{ color: alarm ? '#7F1D1D' : 'var(--text-3)', fontWeight: 600 }}>Legal threshold · </span>
                <strong>{fmt(legal)}</strong>
                <span style={{ color: 'var(--text-3)' }}>
                  {' · '}
                  {isPast
                    ? `${Math.floor((now - legal) / 86400000)}d past${closed ? ' (case closed)' : ''}`
                    : `${Math.ceil((legal - now) / 86400000)}d to go`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Month labels below */}
        <div style={{ position: 'relative', marginTop: 10, height: 14 }}>
          {dots.map(dot => (
            <span key={dot.key} className="tl-month" style={{
              position: 'absolute',
              left: `${dot.pct}%`,
              transform: 'translateX(-50%)',
              fontSize: 10,
              color: dot.passed ? dot.color : 'var(--text-3)',
              fontWeight: dot.passed ? 600 : 400,
              letterSpacing: '.02em',
              transition: 'color .25s var(--ease)',
              userSelect: 'none',
            }}>
              {dot.key}m
            </span>
          ))}
          <span style={{
            position: 'absolute', left: 0,
            fontSize: 10, color: 'var(--text-3)', letterSpacing: '.02em', userSelect: 'none',
          }}>
            {fmt(base)}
          </span>
          <span style={{
            position: 'absolute', right: 0,
            fontSize: 10,
            color: alarm ? '#7F1D1D' : 'var(--text-3)',
            fontWeight: alarm ? 600 : 400,
            letterSpacing: '.02em',
            userSelect: 'none',
          }}>
            {fmt(legal)}
          </span>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
}

// ── Report panel ───────────────────────────────────────────────────────────────

// The Notes panel/section is titled "Notes" for Near-Miss & Clinic-Incident
// events, and "Medical notes" otherwise. The underlying field key
// (medicalNotes / medical_notes) is UNCHANGED — this is a display label only.
function notesLabel(eventType, { caps = false } = {}) {
  // The label is derived straight from the event type, so the capitalised form
  // can never drift from the plain form if the wording changes.
  const isNotesOnly = eventType === 'NearMiss' || eventType === 'ClinicIncident';
  const noun = caps ? 'Notes' : 'notes';
  return isNotesOnly ? 'Notes' : `Medical ${noun}`;
}

// The medical-team-member field is labelled "Case Admin" on the non-treatment
// event types (Near Miss / Clinic Incident) to match those scenarios — there is
// no clinical "medical team" on a facilities/process incident. Treatment events
// keep "Medical Team Member". The underlying field key is unchanged.
function medicalTeamLabel(eventType) {
  const isNonTreatment = eventType === 'NearMiss' || eventType === 'ClinicIncident';
  return isNonTreatment ? 'Case Admin' : 'Medical Team Member';
}

// Per-event-type report theme — header colour + report title. Treatment/Adverse
// Event keeps the brand teal; Near miss is blue; Clinic incident is amber.
function reportTheme(eventType) {
  switch (eventType) {
    case 'NearMiss':       return { accent: '#2563EB', title: 'Near Miss Report' };
    case 'ClinicIncident': return { accent: '#B45309', title: 'Clinic Incident Report' };
    default:               return { accent: '#0D9488', title: 'Adverse Event Report' };
  }
}

// 'YYYY-MM-DDTHH:mm' → "17 September 2026, 14:30" using the caller's date
// formatter (long form in the printed report, short on the case file). The register
// is the one place the TIME of an event matters, so it is never dropped.
function formatRefusalDateTime(value, formatDate) {
  if (!value) return null;
  const [datePart, timePart] = String(value).split('T');
  const date = formatDate ? formatDate(datePart) : datePart;
  const time = (timePart || '').slice(0, 5);
  return time ? `${date}, ${time}` : date;
}

// Map a stored clinic-incident personStatus value (e.g. 'MemberOfPublic') to its
// human label. Falls back to the raw value for unknown/legacy values.
function clinicPersonLabel(value) {
  if (!value) return null;
  return CLINIC_INCIDENT_PERSON_STATUS.find(p => p.value === value)?.label || value;
}

// Edit-field specs for the non-treatment case types. `kind: 'top'` fields are
// real case columns (mapped via SP_TO_PATCH); `kind: 'ts'` fields live in the
// type_specific bag. Mirrors the display panels + the wizard's create forms.
const opt = (s) => (typeof s === 'string' ? { value: s, label: s } : s);
const NEAR_MISS_EDIT_FIELDS = [
  { label: 'Date of Incident',   kind: 'top', key: 'DateofIncident',      type: 'date' },
  { label: 'Incident Title',     kind: 'top', key: 'Title',               type: 'text' },
  { label: 'Reporting Function', kind: 'ts',  key: 'reportingFunction',   type: 'select', options: NEAR_MISS_REPORTING_FUNCTIONS },
  { label: 'Category',           kind: 'top', key: 'IncidentCategory',    type: 'select', options: NEAR_MISS_CATEGORIES },   // register entries use REFUSAL_REGISTER_EDIT_FIELDS
  { label: 'Cause',              kind: 'ts',  key: 'cause',               type: 'textarea' },
  { label: 'Corrective Action',  kind: 'ts',  key: 'correctiveAction',    type: 'textarea' },
  { label: 'Assigned To',        kind: 'ts',  key: 'assignee',            type: 'text' },
  { label: 'Target Resolution',  kind: 'ts',  key: 'targetResolutionDate', type: 'date' },
  { label: 'Potential Severity', kind: 'ts',  key: 'potentialSeverity',   type: 'select', options: NEAR_MISS_SEVERITY_TIERS.map(t => t.label) },
];
const CLINIC_INCIDENT_EDIT_FIELDS = [
  { label: 'Date of Incident',      kind: 'top', key: 'DateofIncident',   type: 'date' },
  { label: 'Incident Title',        kind: 'top', key: 'Title',            type: 'text' },
  { label: 'Type of Incident',      kind: 'top', key: 'IncidentCategory', type: 'select', options: CLINIC_INCIDENT_TYPES },
  { label: 'Injured Person',        kind: 'ts',  key: 'personStatus',     type: 'select', options: CLINIC_INCIDENT_PERSON_STATUS },
  { label: 'Injured Person Name',   kind: 'ts',  key: 'injuredName',      type: 'text' },
  { label: 'Injured Person Contact', kind: 'ts', key: 'injuredContact',   type: 'text' },
  { label: 'Nature of Injuries',    kind: 'ts',  key: 'injuryNature',     type: 'textarea' },
  { label: 'Immediate Response',    kind: 'ts',  key: 'immediateResponse', type: 'textarea' },
  { label: 'Witness Name',          kind: 'ts',  key: 'witnessName',      type: 'text' },
  { label: 'Witness Contact',       kind: 'ts',  key: 'witnessContact',   type: 'text' },
  { label: 'Witness Statement',     kind: 'ts',  key: 'witnessStatement', type: 'textarea' },
];
// Refusal Register entries (refusal_register_logic.md) carry the register's own
// items instead of the cause / corrective-action / assignee set. "Details of the
// incident" is the immutable incident_description, so it is not editable here —
// same rule as every other event type's submitted narrative.
const REFUSAL_REGISTER_EDIT_FIELDS = [
  { label: 'Date and Time of Request',        kind: 'ts',  key: REFUSAL_TS_KEYS.requestedAt,      type: 'datetime-local' },
  { label: 'Incident Title',                  kind: 'top', key: 'Title',                          type: 'text' },
  { label: 'Category',                        kind: 'top', key: 'IncidentCategory',               type: 'select', options: nearMissCategoryOptions(REFUSAL_REGISTER_CATEGORY) },
  { label: 'Description of the Potential Buyer', kind: 'ts', key: REFUSAL_TS_KEYS.buyerDescription, type: 'textarea' },
  { label: 'Action Taken',                    kind: 'ts',  key: REFUSAL_TS_KEYS.actionTaken,      type: 'textarea' },
];

/** Edit-field spec for a non-treatment case — the register has its own set. */
function nonTreatmentEditFields(eventType, incidentCategory) {
  if (isRefusalRegisterCase({ eventType, incidentCategory })) return REFUSAL_REGISTER_EDIT_FIELDS;
  return eventType === 'NearMiss' ? NEAR_MISS_EDIT_FIELDS
       : eventType === 'ClinicIncident' ? CLINIC_INCIDENT_EDIT_FIELDS
       : [];
}

// Report sections, adapted to the case type. Section IDs are stable across types
// (so the checked-state map carries over) — only labels/descriptions change, and
// the "incident" section renders type-specific fields in buildReportHtml. The
// list mirrors the case file panel for panel: whatever the file shows, the
// report can print.
function reportSections(eventType) {
  const nonTreatment = eventType === 'NearMiss' || eventType === 'ClinicIncident';
  return [
    { id: 'overview',    label: 'Case overview',                  desc: 'Reference, event type, status, severity, tags, key dates and the response clock' },
    { id: 'location',    label: 'Clinic & location',              desc: 'Clinic name, region and country' },
    nonTreatment
      ? { id: 'incident', label: eventType === 'NearMiss' ? 'Near-miss details' : 'Incident details',
          desc: 'Reporting function, category, potential severity, cause, corrective action, assignee' }
      : { id: 'incident', label: 'Incident details',             desc: 'Date, treatment and subtype, category, service, client, and every incident-specific field' },
    { id: 'staff',       label: 'Staff information',              desc: nonTreatment ? 'Staff member, clinic manager and case admin' : 'Staff member, clinic manager and medical team' },
    { id: 'notes',       label: nonTreatment ? 'Notes' : 'Description & medical notes', desc: 'The submitted account and the notes added since' },
    { id: 'documents',   label: 'Required documents & sign-offs', desc: 'Each required document: attached, recorded as not existing, or outstanding; per-case requests and sign-offs' },
    { id: 'attachments', label: 'Files',                          desc: 'Every file on the case with who uploaded it and when' },
    { id: 'comments',    label: 'Comments (public)',              desc: 'Public messages with author, time and acknowledgements. Private notes are never printed' },
    { id: 'resolution',  label: 'Resolution',                     desc: 'Closed date and resolution narrative (closed cases only)' },
    { id: 'legal',       label: 'Timeline',                       desc: 'Legal threshold progress and day count' },
    { id: 'history',     label: 'History',                        desc: 'The full audit trail, oldest first' },
  ];
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function fmtLong(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

const LOGO_SVG_FOOTER = `<svg id="tc-logo" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360.95 44.57" style="height:20px;width:auto;display:block;"><defs><style>.cls-1{fill-rule:evenodd}.cls-1,.cls-2{fill:#7173b6}.cls-1,.cls-2,.cls-3{stroke-width:0px}.cls-3{fill:#231f20}</style></defs><g><path class="cls-1" d="m9.77,6.43v.19c.68,3.93,2.12,7.27,4.08,9.97,1.39,1.91,2.68,3.56,4.34,5.11,2.45,2.29,5.25,4.38,8.74,5.5.23.05.06-.17,0-.19-5.56-4.6-3.98-12.34-4.78-13.17-2.13-2.5-6.79-6.04-12.37-7.41"/><path class="cls-1" d="m18.44,24c-.53-.42-1.16-1.02-1.74-1.6-.52-.51-1.23-1.49-1.75-1.66-1.53-.23-8.62-.96-14.95,1.09,7.01,5.51,17.27,7.15,25.36,6.46-2.64-1.13-4.76-2.57-6.92-4.28h0Z"/><path class="cls-1" d="m8.28,27.64c-2.09.79-4.28,1.88-6.02,3.13,2.39.88,5.38,1.51,8.61,1.66,2.27.11,5.41.12,7.86-.6,1.65-.48,3.59-1.85,4.15-2.37-11.26.58-14.1-1.99-14.61-1.83Z"/><path class="cls-1" d="m52.8,27.65c2.09.79,4.28,1.88,6.02,3.13-2.39.88-5.38,1.51-8.61,1.66-2.27.11-5.41.12-7.86-.6-1.65-.48-3.59-1.85-4.15-2.37,11.26.58,14.1-1.99,14.61-1.83Z"/><path class="cls-1" d="m19.41,34.93c2.16.48,4.5-.03,6.15-.64-.14-1.9.5-3.63,1.49-4.98.28-.38.31-.52.26-.51-2.3.41-6.55,2.52-7.89,6.14Z"/><path class="cls-2" d="m30.54,27.67h0c1.56.12,2.86-1.06,3.69-2.2,5.07-6.52,3.53-20.34-3.69-25.47h0,0s0,0,0,0c-7.22,5.13-8.75,18.96-3.69,25.47.83,1.14,2.13,2.32,3.69,2.2h0,0Z"/><path class="cls-1" d="m51.31,6.43v.19c-.68,3.93-2.12,7.27-4.08,9.97-1.39,1.91-2.68,3.56-4.34,5.11-2.45,2.29-5.25,4.38-8.74,5.5-.23.05-.06-.17,0-.19,5.56-4.6,3.98-12.34,4.78-13.17,2.13-2.5,6.79-6.04,12.37-7.41"/><path class="cls-1" d="m42.64,24c.53-.42,1.16-1.02,1.74-1.6.52-.51,1.23-1.49,1.75-1.66,1.53-.23,8.62-.96,14.95,1.09-7.01,5.51-17.27,7.15-25.36,6.46,2.64-1.13,4.76-2.57,6.92-4.28h0Z"/><path class="cls-1" d="m41.67,34.93c-2.16.48-4.5-.03-6.15-.64.14-1.9-.5-3.63-1.49-4.98-.28-.38-.31-.52-.26-.51,2.3.41,6.55,2.52,7.89,6.14Z"/><path class="cls-1" d="m30.54,28.86c-4.56,0-3.97,8.5,0,10.86,4.05-2.63,4.56-10.86,0-10.86Z"/></g><path class="cls-3" d="m81.45,35.4c1.25.02,2.48-.24,3.61-.77v-1.95c-1.01.49-2.13.75-3.26.76-2.56,0-4.42-1.1-4.42-4.21v-13.49h7.74v-1.92h-7.76V4.71h-2.13v9.12h-3.22v1.92h3.22v13.71c0,4.12,2.78,5.95,6.21,5.95m33.84-12.25h16.57c-.51-3.87-3.77-7.35-8.19-7.35-4.29.02-7.89,3.18-8.37,7.35m17.91,7.09c-1.82,3.52-5.55,5.71-9.58,5.62-6.37,0-10.42-5.06-10.42-11.02s4.14-10.94,10.42-10.94,10.38,5.11,10.33,11.16h-18.66c-.14,5.32,3.63,8.88,8.61,8.88,2.9,0,5.3-1.47,6.9-3.7h2.4Zm-3.27-23.73l-2.61-1.41-5.96,5.62,1.57,1.11,7-5.32Zm90.85,16.64h16.56c-.51-3.87-3.77-7.35-8.18-7.35-4.3.01-7.9,3.17-8.39,7.35h.02Zm17.91,7.09c-1.82,3.52-5.55,5.71-9.58,5.62-6.38,0-10.43-5.06-10.43-11.02s4.14-10.94,10.43-10.94,10.38,5.11,10.33,11.16h-18.67c-.14,5.32,3.63,8.88,8.6,8.88,2.9,0,5.3-1.47,6.9-3.7h2.42Zm-27.07-15.88h-2.1v21.03h2.1V14.37Zm-1.02-8.28c-1.05,0-1.91.84-1.91,1.87,0,1.03.85,1.87,1.91,1.87s1.91-.84,1.91-1.87c0-.5-.21-.98-.57-1.33-.36-.35-.85-.55-1.36-.54h.02Zm-18.66,9.73c5.26,0,8.47,4.46,8.47,9.29s-3.35,8.83-8.39,8.83-8.56-3.69-8.56-8.61,2.98-9.52,8.47-9.52h.02Zm-8.33-1.45h-2.1v30.2h2.1v-12.81h.09c1.77,2.69,4.98,4.1,8.28,4.1,6.47,0,10.51-4.65,10.51-10.81s-4.05-11.16-10.6-11.16c-3.16,0-6.6,1.55-8.18,4.24h-.1v-3.76Zm-21,1.45c5.21,0,8.6,3.83,8.6,8.7s-2.97,9.42-8.46,9.42-8.47-4.37-8.47-9.2c0-4.55,3.4-8.93,8.33-8.93m10.56-1.45h-2.09v3.64h-.09c-1.89-2.63-5-4.16-8.28-4.1-6.47,0-10.52,4.69-10.52,10.84s4.09,11.11,10.6,11.11c3.16,0,6.62-1.55,8.19-4.24h.09v3.78h2.09V14.37Zm-30.89,0h-2.1v21.03h2.1v-10.75c0-3.09-.28-8.84,4.42-8.84.53,0,1.04.16,1.49.43l1.12-1.82c-.71-.33-1.49-.5-2.28-.51-2.27,0-3.81,1.3-4.65,3.19h-.09v-2.75Zm-50.95-9.66v30.69h2.1v-10.37c0-4.28.88-9.2,6.28-9.2,4.69,0,5.16,3.1,5.16,6.88v12.7h2.09v-12.71c0-5.42-1.54-8.8-7.07-8.8-2.68-.05-5.16,1.4-6.39,3.74h-.09V4.71h-2.09Z"/><g><path class="cls-3" d="m255.28,24.7c0-6.4,4.77-10.77,10.75-10.77,3.82,0,6.41,1.78,8.09,4.12l-2.21,1.22c-1.2-1.75-3.38-3.03-5.88-3.03-4.52,0-8.03,3.5-8.03,8.46s3.51,8.46,8.03,8.46c2.5,0,4.68-1.25,5.88-3.03l2.24,1.22c-1.77,2.37-4.3,4.12-8.12,4.12-5.97,0-10.75-4.37-10.75-10.76Z"/><path class="cls-3" d="m279.86,14.28h2.62v18.5h9.8v2.31h-12.42V14.28Z"/><path class="cls-3" d="m298.35,14.28h2.62v20.81h-2.62V14.28Z"/><path class="cls-3" d="m310.9,18.3v16.79h-2.62V14.28h2.69l12.13,16.45V14.28h2.62v20.81h-2.53l-12.29-16.79Z"/><path class="cls-3" d="m333.02,14.28h2.62v20.81h-2.62V14.28Z"/><path class="cls-3" d="m342.09,24.7c0-6.4,4.77-10.77,10.75-10.77,3.82,0,6.41,1.78,8.09,4.12l-2.21,1.22c-1.2-1.75-3.38-3.03-5.88-3.03-4.52,0-8.03,3.5-8.03,8.46s3.51,8.46,8.03,8.46c2.5,0,4.68-1.25,5.88-3.03l2.24,1.22c-1.77,2.37-4.3,4.12-8.12,4.12-5.97,0-10.75-4.37-10.75-10.76Z"/></g></svg>`;

// A4 dimensions at 96 dpi — the preview iframe, the export window and the
// pagination script all measure in these units (@page margin is 0, the 30px
// top/bottom breathing room is part of the page model below).
const A4_W = 794;
const A4_H = 1123;
const PAGE_PAD = 30;

const EVENT_TYPE_LABELS = { Treatment: 'Treatment incident', NearMiss: 'Near miss', ClinicIncident: 'Clinic incident' };

function fmtBytes(n) {
  const b = Number(n);
  if (!Number.isFinite(b) || b <= 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// Runs inside the report document (preview iframe AND export window). Pushes
// any block that would straddle a page boundary onto the next page — keeping a
// section title / column header with the row that follows it — then sizes the
// body to whole pages, stamps a footer on each page and tells the parent how
// many pages there are. The printer slices the flow at the same 1123px
// boundaries (@page margin 0), so what the preview shows is what prints.
const REPORT_PAGINATE_JS = `
(function () {
  var PAGE = ${A4_H}, PAD = ${PAGE_PAD};
  function top(el) { return el.getBoundingClientRect().top + window.scrollY; }
  function place() {
    var body = document.body;
    body.style.height = 'auto';
    var blocks = Array.prototype.slice.call(document.querySelectorAll('.blk'));
    blocks.forEach(function (el) { el.style.marginTop = ''; });
    blocks.forEach(function (el) {
      var t = top(el), h = el.getBoundingClientRect().height;
      var pageIdx = Math.floor(t / PAGE);
      var pageEnd = pageIdx * PAGE + PAGE - PAD;
      if (t + h > pageEnd && h <= PAGE - 2 * PAD) {
        var target = el, prev = el.previousElementSibling;
        while (prev && prev.classList.contains('keep') && Math.floor(top(prev) / PAGE) === pageIdx) { target = prev; prev = prev.previousElementSibling; }
        var cur = parseFloat(getComputedStyle(target).marginTop) || 0;
        target.style.marginTop = (cur + (pageIdx * PAGE + PAGE + PAD - top(target))) + 'px';
      }
    });
    var total = Math.max(1, Math.ceil(body.scrollHeight / PAGE));
    body.style.height = (total * PAGE) + 'px';
    Array.prototype.forEach.call(document.querySelectorAll('.pfoot'), function (n) { n.remove(); });
    var logo = document.getElementById('foot-logo');
    for (var p = 0; p < total; p++) {
      var f = document.createElement('div');
      f.className = 'pfoot';
      f.style.top = (p * PAGE + PAGE - 26) + 'px';
      f.innerHTML = '<span class="pfoot-l">' + (logo ? logo.innerHTML : '') + '<span>' + body.getAttribute('data-foot') + '</span></span><span>Page ' + (p + 1) + ' of ' + total + '</span>';
      body.appendChild(f);
    }
    return total;
  }
  var total = place();
  if (window.parent !== window) { try { window.parent.postMessage({ type: 'ae-report-pages', pages: total }, '*'); } catch (e) {} }
  if (document.body.getAttribute('data-print') === '1') { setTimeout(function () { window.print(); }, 60); }
})();`;

function buildReportHtml(f, sections, opts = {}, legalThresholdMonths = 6) {
  const { preview = false, tags = [], sla = null, requirements = [], comments = [], events = [] } = opts;
  const now       = new Date();
  const generated = fmtLong(now.toISOString());
  const theme     = reportTheme(f.eventType);
  const accent    = theme.accent;
  const nonTreatment = f.eventType === 'NearMiss' || f.eventType === 'ClinicIncident';
  const ts = f.typeSpecific || {};

  const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const para = (s) => esc(s).replace(/\n/g, '<br>');
  // `row` takes trusted HTML; `rowT` escapes a plain value.
  const row  = (label, html) => `<div class="field"><div class="field-label">${label}</div><div class="field-value">${html || '—'}</div></div>`;
  const rowT = (label, val)  => row(label, val == null || val === '' ? null : esc(val));

  let body = '';

  if (sections.overview) {
    const chip = sla?.open ? buildSlaChip(sla, { formatDateTime }) : null;
    body += `
    <div class="section blk">
      <div class="section-title">Case Overview</div>
      <div class="grid">
        ${row('Case Reference', `<span class="mono">${esc(f.caseRef || '—')}</span>`)}
        ${rowT('Event type', EVENT_TYPE_LABELS[f.eventType] || f.eventType)}
        ${nonTreatment ? '' : rowT('Treatment', [f.treatment, f.treatmentSubtype].filter(Boolean).join(' — '))}
        ${rowT('Status',         f.status)}
        ${treatmentHasCodes(f.treatment) ? rowT('Severity', f.severity) : ''}
        ${rowT('Tags',           (tags || []).map(t => t.label).filter(Boolean).join(', '))}
        ${rowT('Date Reported',  fmtLong(f.reportedDate))}
        ${rowT('Date of Incident', f.dateOfIncident ? fmtLong(f.dateOfIncident) : null)}
        ${rowT('Review date',    f.reviewDate ? fmtLong(f.reviewDate) : null)}
        ${rowT('Last Updated',   fmtLong(f.modifiedDate))}
        ${chip ? rowT('Response clock', chip.text) : ''}
      </div>
    </div>`;
  }

  if (sections.location) body += `
    <div class="section blk">
      <div class="section-title">Clinic &amp; Location</div>
      <div class="grid">
        ${rowT('Clinic',  f.clinic)}
        ${rowT('Region',  f.region)}
        ${rowT('Country', f.country)}
      </div>
    </div>`;

  if (sections.incident) {
    const catLabel = f.incidentCategory === 'Other' && f.incidentCategoryOther
      ? `Other — ${f.incidentCategoryOther}`
      : f.incidentCategory;
    const incidentTitle = f.eventType === 'NearMiss' ? 'Near-Miss Details' : 'Incident Details';
    const clinicIncidentFields = `
        ${rowT('Incident Title',        f.clientName)}
        ${rowT('Date of Incident',      fmtLong(f.dateOfIncident))}
        ${rowT('Type of Incident',      catLabel)}
        ${rowT('Injured Person',        clinicPersonLabel(ts.personStatus))}
        ${rowT('Injured Person Name',   ts.injuredName)}
        ${rowT('Injured Person Contact', ts.injuredContact)}
        ${rowT('Nature of Injuries',    ts.injuryNature)}
        ${rowT('Immediate Response',    ts.immediateResponse)}
        ${rowT('Witness Name',          ts.witnessName)}
        ${rowT('Witness Contact',       ts.witnessContact)}
        ${rowT('Witness Statement',     ts.witnessStatement)}
      `;
    // A refusal register entry prints the register items — what an auditor asks for.
    const refusalRegisterFields = `
        ${rowT('Incident Title',     f.clientName)}
        ${rowT('Reporting Function', ts.reportingFunction)}
        ${rowT('Category',           catLabel)}
        ${rowT('Date and Time of Request', formatRefusalDateTime(ts[REFUSAL_TS_KEYS.requestedAt], fmtLong))}
        ${rowT('Details of the Incident',  f.incidentDescription)}
        ${rowT('Description of the Potential Buyer', ts[REFUSAL_TS_KEYS.buyerDescription])}
        ${rowT('Action Taken',       ts[REFUSAL_TS_KEYS.actionTaken])}
        ${rowT('Staff Member',       f.staffMember)}
      `;
    const nearMissFields = `
        ${rowT('Incident Title',     f.clientName)}
        ${rowT('Date of Incident',   fmtLong(f.dateOfIncident))}
        ${rowT('Reporting Function', ts.reportingFunction)}
        ${rowT('Category',           catLabel)}
        ${rowT('Potential Severity', ts.potentialSeverity)}
        ${rowT('Cause',              ts.cause)}
        ${rowT('Corrective Action',  ts.correctiveAction)}
        ${rowT('Assigned To',        ts.assignee)}
        ${rowT('Target Resolution',  ts.targetResolutionDate ? fmtLong(ts.targetResolutionDate) : null)}
      `;
    // Treatment: the same rows as the case file's Incident details panel, then
    // every incident-specific field (matrix-required and optional alike, empty
    // ones as a dash) — the same ordered key list the panel uses.
    const extraRows = nonTreatment ? [] : reportIncidentFieldRows(f.treatment, f.severity, ts, { formatDate: fmtLong, includeEmptyOptional: true });
    const treatmentFields = `
        ${rowT('Date of Incident',       fmtLong(f.dateOfIncident))}
        ${rowT('Treatment / Department', f.treatment)}
        ${rowT('Subtype',                f.treatmentSubtype)}
        ${rowT('Incident Category',      catLabel)}
        ${rowT('Service Carried Out',    f.serviceCarriedOut)}
        ${rowT('Client Name',            f.clientName)}
        ${rowT('Client phone',           f.clientPhone)}
      `;
    body += `
    <div class="section blk">
      <div class="section-title">${incidentTitle}</div>
      <div class="grid">${nonTreatment ? (f.eventType === 'ClinicIncident' ? clinicIncidentFields : isRefusalRegisterCase({ eventType: f.eventType, incidentCategory: f.incidentCategory }) ? refusalRegisterFields : nearMissFields) : treatmentFields}</div>
      ${extraRows.length ? `
      <div class="subhead">Incident-specific details</div>
      <div class="grid">${extraRows.map(r => rowT(r.label, r.value)).join('')}</div>` : ''}
    </div>`;
  }

  if (sections.staff) body += `
    <div class="section blk">
      <div class="section-title">Staff Information</div>
      <div class="grid">
        ${rowT('Staff Member Concerned',   f.staffMember)}
        ${rowT('Clinic Manager',           f.clinicManager)}
        ${rowT(medicalTeamLabel(f.eventType), f.medicalTeam)}
      </div>
    </div>`;

  // Immutable submitter account — the permanent record of the original report.
  if (sections.notes && f.incidentDescription) body += `
    <div class="section blk">
      <div class="section-title">Incident Description</div>
      <div class="notes-box">${para(f.incidentDescription)}</div>
    </div>`;

  if (sections.notes && f.medicalNotes) body += `
    <div class="section blk">
      <div class="section-title">${notesLabel(f.eventType, { caps: true })}</div>
      <div class="notes-box">${para(f.medicalNotes)}</div>
    </div>`;

  // Required documents & sign-offs — mirrors the case-file panel: one line per
  // matrix slot (attached / recorded as not existing / outstanding) and one per
  // per-case request (document or sign-off).
  if (sections.documents) {
    const attachments = f.attachments || [];
    const byCategory = {};
    for (const att of attachments) {
      const parsed = parseAttachmentName(att.name);
      if (parsed?.categoryKey) (byCategory[parsed.categoryKey] = byCategory[parsed.categoryKey] || []).push(att);
    }
    const uploads = nonTreatment ? [] : caseDocumentSlots(f.eventType, f.treatment, f.severity);
    const waivers = getWaivers(ts);
    const lines = [];
    for (const cat of uploads) {
      const label = ATTACHMENT_CATEGORY_LABELS[cat] || cat;
      const files = byCategory[cat] || [];
      if (files.length) {
        lines.push(`<div class="lrow cols3 blk"><span>${esc(label)}</span><span class="st ok">Attached · ${files.length}</span><span class="muted">${files.map(a => esc(a.name)).join('<br>')}</span></div>`);
      } else if (isWaived(ts, cat)) {
        const w = waivers[cat] || {};
        const detail = [w.reason, w.byName ? `by ${w.byName}` : null, w.at ? fmtLong(w.at) : null].filter(Boolean).join(' · ');
        lines.push(`<div class="lrow cols3 blk"><span>${esc(label)}</span><span class="st warn">No ${esc(label.toLowerCase())}</span><span class="muted">${esc(detail)}</span></div>`);
      } else {
        lines.push(`<div class="lrow cols3 blk"><span>${esc(label)}</span><span class="st out">Outstanding</span><span class="muted">—</span></div>`);
      }
    }
    for (const r of (requirements || [])) {
      const isSignoff = requirementKind(r) === REQ_KIND_SIGNOFF;
      const files = attachments.filter(a => a.category === reqCategory(r.id));
      const status = isSignoff
        ? (isSignoffDone(r) ? '<span class="st ok">Signed off</span>' : '<span class="st out">Awaiting sign-off</span>')
        : (files.length ? `<span class="st ok">Uploaded · ${files.length}</span>` : '<span class="st out">Awaiting upload</span>');
      const detail = [
        r.note || null,
        r.requestedByName ? `Requested by ${r.requestedByName}${r.requestedAt ? ` · ${fmtLong(r.requestedAt)}` : ''}` : null,
        files.length ? files.map(a => a.name).join(', ') : null,
      ].filter(Boolean).map(esc).join('<br>');
      lines.push(`<div class="lrow cols3 blk"><span>${isSignoff ? 'Sign-off: ' : ''}${esc(r.label)}</span>${status}<span class="muted">${detail || '—'}</span></div>`);
    }
    body += `
    <div class="section flow">
      <div class="section-title keep blk">Required Documents &amp; Sign-offs</div>
      ${lines.length
        ? `<div class="lhead cols3 keep blk"><span>Document</span><span>Status</span><span>Detail</span></div>${lines.join('')}`
        : '<div class="blk muted">No required documents for this case.</div>'}
    </div>`;
  }

  if (sections.attachments) {
    const attachments = f.attachments || [];
    const catLabelFor = (att) => {
      if (att.category && String(att.category).startsWith('req:')) {
        const r = (requirements || []).find(x => reqCategory(x.id) === att.category);
        return r ? r.label : 'Requested document';
      }
      const parsed = parseAttachmentName(att.name);
      return parsed?.categoryKey ? (ATTACHMENT_CATEGORY_LABELS[parsed.categoryKey] || parsed.categoryKey) : '—';
    };
    const lines = attachments.map(att => {
      const meta = attachmentMetadataLine(att.metadata, { formatDate: fmtLong });
      const size = fmtBytes(att.size ?? att.sizeBytes ?? att.length);
      return `<div class="lrow cols4 blk"><span class="fname">${esc(att.name)}${size ? `<span class="muted"> · ${size}</span>` : ''}</span><span>${esc(catLabelFor(att))}</span><span>${esc(att.uploadedBy || '—')}</span><span class="muted">${att.uploadedAt ? esc(fmtLong(att.uploadedAt)) : '—'}${meta ? `<br>${esc(meta)}` : ''}</span></div>`;
    });
    body += `
    <div class="section flow">
      <div class="section-title keep blk">Files</div>
      ${lines.length
        ? `<div class="lhead cols4 keep blk"><span>File</span><span>Category</span><span>Uploaded by</span><span>Date · details</span></div>${lines.join('')}`
        : '<div class="blk muted">No files on this case.</div>'}
    </div>`;
  }

  // Public comments only — a printed report leaves the building; private notes
  // stay in the app.
  if (sections.comments) {
    const pub = (comments || []).filter(c => c && c.isPublic !== false && (c._source == null || c._source === 'comment'));
    const lines = pub.map(c => {
      const acks = Array.isArray(c.acks) && c.acks.length ? ` · Acknowledged · ${c.acks.length}` : '';
      const files = Array.isArray(c.attachments) && c.attachments.length ? `<div class="cmt-f">${c.attachments.map(a => esc(a.name)).join(' · ')}</div>` : '';
      return `<div class="cmt blk"><div class="cmt-h">${esc(c.created ? formatDateTime(c.created) : '—')} · ${esc(c.authorName || 'Unknown')}${esc(acks)}</div><div class="cmt-b">${esc(richToText(c.text))}</div>${files}</div>`;
    });
    body += `
    <div class="section flow">
      <div class="section-title keep blk">Comments</div>
      ${lines.length ? lines.join('') : '<div class="blk muted">No public comments.</div>'}
    </div>`;
  }

  const resolution = opts.resolutionText ?? f.caseResolution;
  if (sections.resolution && (resolution || f.closedAt)) body += `
    <div class="section blk">
      <div class="section-title">Resolution</div>
      <div class="grid">
        ${rowT('Closed On', fmtLong(f.closedAt))}
      </div>
      ${resolution ? `<div class="notes-box" style="margin-top:12px">${para(resolution)}</div>` : ''}
    </div>`;

  if (sections.legal) {
    const base = f.dateOfIncident || f.reportedDate;
    if (base) {
      const d     = new Date(base);
      const legal = new Date(d); legal.setMonth(legal.getMonth() + legalThresholdMonths);
      const pct   = Math.min(Math.round(((now - d) / (legal - d)) * 100), 100);
      const isPast = now > legal;
      const days  = Math.abs(Math.ceil((legal - now) / 86400000));
      body += `
        <div class="section blk">
          <div class="section-title">Timeline</div>
          <div class="tl-bg"><div class="tl-fill" style="width:${pct}%"></div></div>
          <div class="tl-meta">
            <span>Incident: ${fmtLong(base)}</span>
            <span class="${isPast ? 'danger' : ''}">${isPast ? `${days}d past threshold` : `${days}d remaining`}</span>
            <span>Threshold: ${fmtLong(legal.toISOString())}</span>
          </div>
        </div>`;
    }
  }

  if (sections.history) {
    const ordered = [...(events || [])].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    const lines = ordered.map(ev => {
      const change = ev.fieldName && ev.newValue !== null && ev.newValue !== undefined
        ? `<div class="ev-d">${esc(ev.oldValue || '—')} → ${esc(ev.newValue)}</div>` : '';
      return `<div class="ev blk"><span class="ev-t">${esc(ev.createdAt ? formatDateTime(ev.createdAt) : '—')}</span><span>${esc(ev.label || ev.eventType || '')}${change}</span></div>`;
    });
    body += `
    <div class="section flow">
      <div class="section-title keep blk">History</div>
      ${lines.length ? lines.join('') : '<div class="blk muted">No history yet.</div>'}
    </div>`;
  }

  const foot = esc(`Therapie Clinic · ${theme.title} · Confidential · Generated ${generated}`);

  return `<!DOCTYPE html>
<html lang="en"><head>
<meta charset="utf-8">
<title>AE Report — ${esc(f.caseRef)}</title>
<style>
@page { size: A4; margin: 0; }
* { box-sizing: border-box; margin: 0; padding: 0; }
html, body { background: #fff; }
body { font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;
       font-size: 11px; line-height: 1.55; color: #1e293b;
       width: ${A4_W}px; margin: 0 auto; padding: ${PAGE_PAD}px 38px 0; position: relative; }
.hdr { background: ${accent}; color: #fff; padding: 22px 28px; border-radius: 10px;
       margin-bottom: 28px; display: flex; justify-content: space-between; align-items: flex-start; }
.brand { font-size: 9px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase;
         opacity: .6; margin-bottom: 6px; }
.ref   { font-size: 26px; font-weight: 800; letter-spacing: -.02em; }
.sub   { font-size: 12px; opacity: .75; margin-top: 3px; }
.pills { margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap; }
.pill  { display: inline-block; padding: 3px 10px; border-radius: 20px;
         font-size: 10px; font-weight: 600; background: rgba(255,255,255,.18); }
.pill2 { background: rgba(0,0,0,.18); }
.hdr-r { text-align: right; font-size: 10px; opacity: .65; line-height: 1.9; }
.section { margin-bottom: 22px; }
.section-title { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .1em;
                 color: ${accent}; border-bottom: 1.5px solid ${accent}; padding-bottom: 5px; margin-bottom: 12px; }
.subhead { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #64748b; margin: 14px 0 8px; }
.grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; }
.field-label { font-size: 9px; color: #94a3b8; font-weight: 600; text-transform: uppercase;
               letter-spacing: .06em; margin-bottom: 3px; }
.field-value { font-size: 12px; color: #1e293b; font-weight: 500; overflow-wrap: anywhere; }
.mono, .fname { font-family: 'SF Mono','Fira Code','Courier New',monospace; }
.mono { font-weight: 700; }
.fname { font-size: 10px; overflow-wrap: anywhere; }
.notes-box { background: #f8fafc; border-left: 3px solid ${accent};
             padding: 12px 16px; border-radius: 0 6px 6px 0;
             font-size: 12px; line-height: 1.75; color: #334155; overflow-wrap: anywhere; }
.lhead, .lrow { display: grid; gap: 10px; padding: 6px 0; border-bottom: 1px solid #eef2f7; font-size: 10.5px; align-items: start; overflow-wrap: anywhere; }
.lhead { font-size: 9px; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; font-weight: 600; border-bottom: 1px solid #cbd5e1; }
.cols3 { grid-template-columns: 1.3fr 1fr 2fr; }
.cols4 { grid-template-columns: 1.7fr 1fr 1fr 1.6fr; }
.st { font-weight: 600; } .st.ok { color: #059669; } .st.warn { color: #B45309; } .st.out { color: #94a3b8; }
.muted { color: #94a3b8; }
.cmt { padding: 8px 0; border-bottom: 1px solid #eef2f7; }
.cmt-h { font-size: 10px; color: #64748b; margin-bottom: 3px; }
.cmt-b { font-size: 11.5px; line-height: 1.6; white-space: pre-wrap; overflow-wrap: anywhere; }
.cmt-f { font-size: 10px; color: #94a3b8; margin-top: 3px; }
.ev { display: grid; grid-template-columns: 118px 1fr; gap: 10px; padding: 4px 0; border-bottom: 1px solid #f1f5f9; font-size: 10.5px; overflow-wrap: anywhere; }
.ev-t { color: #94a3b8; } .ev-d { color: #94a3b8; font-size: 10px; }
.tl-bg   { height: 6px; background: #e2e8f0; border-radius: 100px; overflow: hidden; margin-bottom: 8px; }
.tl-fill { height: 100%; border-radius: 100px;
           background: linear-gradient(to right,#0D9488,#F59E0B,#DC2626,#7F1D1D); }
.tl-meta { display: flex; justify-content: space-between; font-size: 10px; color: #64748b; }
.danger  { color: #DC2626; font-weight: 600; }
.pfoot { position: absolute; left: 38px; right: 38px; display: flex; justify-content: space-between; align-items: center;
         padding-top: 5px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8; }
.pfoot-l { display: flex; align-items: center; gap: 10px; }
.pfoot-l svg { height: 14px !important; width: auto !important; }
#foot-logo { display: none; }
@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body data-foot="${foot}" data-print="${preview ? '0' : '1'}">
<template id="foot-logo">${LOGO_SVG_FOOTER}</template>
<div class="hdr blk">
  <div>
    <div class="brand">Therapie Clinic · ${esc(theme.title)}</div>
    <div class="ref">${esc(f.caseRef)}</div>
    <div class="sub">${esc([f.clinic, f.region].filter(Boolean).join(' · ') || 'Case details')}</div>
    <div class="pills">
      <span class="pill">${esc(EVENT_TYPE_LABELS[f.eventType] || f.eventType)}</span>
      <span class="pill">${esc(f.status || 'Open')}</span>
      ${treatmentHasCodes(f.treatment) && f.severity ? `<span class="pill pill2">${esc(f.severity)}</span>` : ''}
      ${(tags || []).map(t => `<span class="pill pill2">${esc(t.label)}</span>`).join('')}
    </div>
  </div>
  <div class="hdr-r">
    <div>Generated ${generated}</div>
    ${f.reportedDate   ? `<div>Reported ${fmtLong(f.reportedDate)}</div>`   : ''}
    ${f.dateOfIncident ? `<div>Incident ${fmtLong(f.dateOfIncident)}</div>` : ''}
  </div>
</div>
${body}
<script>${REPORT_PAGINATE_JS}</script>
</body></html>`;
}

function ReportPanel({ f, resolutionText, caseId, tags = [], sla = null, requirements = [], role, canSeeCosts = false }) {
  const { config } = useAppConfig();
  const sectionDefs = reportSections(f.eventType);
  const [selected, setSelected] = useState(
    Object.fromEntries(sectionDefs.map(s => [s.id, true]))
  );
  const [generating, setGenerating] = useState(false);
  // Comments and history are printed too — loaded once for the tab (the
  // Overview tab keeps its own copies).
  const [comments, setComments] = useState([]);
  const [events, setEvents]     = useState([]);
  useEffect(() => {
    if (!caseId) return undefined;
    let cancelled = false;
    Promise.all([
      getComments(caseId).catch(() => []),
      getCaseEvents(caseId).catch(() => []),
    ]).then(([cs, evs]) => {
      if (cancelled) return;
      const list = Array.isArray(cs) ? cs : (Array.isArray(cs?.comments) ? cs.comments : []);
      setComments(list);
      setEvents(groupCommentAttachments(filterEventsForRole(Array.isArray(evs) ? evs : [], role, canSeeCosts)));
    });
    return () => { cancelled = true; };
  }, [caseId, role, canSeeCosts]);

  // Preview pagination: the report paginates itself and reports its page count;
  // the viewport shows one A4 page at a time and slides between them.
  const [pages, setPages] = useState(1);
  const [page,  setPage]  = useState(1);
  useEffect(() => {
    function onMsg(e) {
      if (e.data?.type !== 'ae-report-pages') return;
      const n = Math.max(1, Number(e.data.pages) || 1);
      setPages(n);
      setPage(p => Math.min(p, n));
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Measure the white page div width so we can scale the A4 iframe to fill it exactly
  const previewColRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.5);
  useEffect(() => {
    const el = previewColRef.current;
    if (!el) return;
    const measure = () => setPreviewScale(el.getBoundingClientRect().width / A4_W);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const anySelected = Object.values(selected).some(Boolean);
  const reportOpts  = { resolutionText, tags, sla, requirements, comments, events };
  const previewHtml = buildReportHtml(f, selected, { ...reportOpts, preview: true }, config.legalThresholdMonths);

  async function generate() {
    setGenerating(true);
    try {
      const html = buildReportHtml(f, selected, reportOpts, config.legalThresholdMonths);
      const win  = window.open('', '_blank', 'width=920,height=720');
      if (!win) { alert('Allow pop-ups for this site to generate the report.'); return; }
      win.document.write(html);
      win.document.close();
    } finally {
      setGenerating(false);
    }
  }

  const canPrev = page > 1;
  const canNext = page < pages;

  return (
    <div className="row report-layout">

      {/* ── Left: live A4 preview — one page at a time, with a pager; sticky so
          it stays in view while the section controls scroll. ── */}
      <div style={{ height: '100%' }}>
        <div className="panel report-preview" style={{ overflow: 'hidden' }}>
          <div className="panel-head">
            <div className="panel-title">Preview</div>
            <div className="report-pager" aria-live="polite">
              <button type="button" className="btn ghost sm" disabled={!canPrev} onClick={() => setPage(p => Math.max(1, p - 1))} aria-label="Previous page">‹</button>
              <span>Page {page} of {pages}</span>
              <button type="button" className="btn ghost sm" disabled={!canNext} onClick={() => setPage(p => Math.min(pages, p + 1))} aria-label="Next page">›</button>
            </div>
          </div>
          {/* White page fills the panel body edge-to-edge */}
          <div
            ref={previewColRef}
            style={{
              position: 'relative',
              overflow: 'hidden',
              aspectRatio: `${A4_W} / ${A4_H}`,
            }}
          >
            <div style={{
              width: A4_W,
              height: A4_H,
              position: 'absolute',
              top: 0,
              left: 0,
              overflow: 'hidden',
              transformOrigin: 'top left',
              transform: `scale(${previewScale})`,
            }}>
              <iframe
                srcDoc={previewHtml}
                sandbox="allow-same-origin allow-scripts"
                title="Report preview"
                style={{
                  width: A4_W, height: pages * A4_H, border: 'none', pointerEvents: 'none', display: 'block',
                  transform: `translateY(-${(page - 1) * A4_H}px)`,
                  transition: 'transform .25s var(--ease)',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: section controls + attachment archive ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">Report sections</div>
          </div>
          <div className="panel-body">
            <p style={{ fontSize: 13, color: 'var(--text-3)', margin: '0 0 20px' }}>
              Choose the sections to include, then click <strong style={{ color: 'var(--text)' }}>Export as PDF</strong>.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {sectionDefs.map(s => (
                <label key={s.id} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={!!selected[s.id]}
                    onChange={() => setSelected(p => ({ ...p, [s.id]: !p[s.id] }))}
                    style={{ marginTop: 3, accentColor: 'var(--brand)', width: 14, height: 14, flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', lineHeight: 1.3 }}>{s.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{s.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            <button
              onClick={generate}
              disabled={!anySelected || generating}
              style={{
                marginTop: 24, width: '100%', padding: '11px 16px',
                background: anySelected && !generating ? 'var(--brand)' : 'var(--surface-3)',
                color: anySelected && !generating ? '#fff' : 'var(--text-3)',
                border: 'none', borderRadius: 8,
                cursor: anySelected && !generating ? 'pointer' : 'not-allowed',
                fontSize: 14, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {generating ? (
                'Generating…'
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="12" y1="18" x2="12" y2="12"/>
                    <line x1="9"  y1="15" x2="15" y2="15"/>
                  </svg>
                  Export as PDF
                </>
              )}
            </button>
          </div>
        </div>

        <AttachmentArchive attachments={f.attachments || []} caseRef={f.caseRef} />
      </div>

    </div>
  );
}

// ── Required-attachment slots (mirrors the NewCaseWizard UX) ─────────────────
// For a case whose (treatment, severity) triggers required uploads, surface
// one row per required category. Existing files are bucketed into matching
// slots by parsing their filename; clicking a slot opens the OS file picker
// with the category locked, then uploads with the canonical AE{id}-{Cat}-N
// rename. No category prompt — the slot already declares it.

function RequiredAttachmentSlots({
  caseId, eventType = null, treatment, severity, attachments, canEdit, onChange, onBulkDelete, attDeleting,
  // canRespond = may UPLOAD into a slot (respond to a requirement). canEdit = may
  // DELETE / manage attachments. They differ: responder roles (CM/HoRO/service)
  // can upload but not delete. See requirement_permissions_logic.md.
  canRespond = false,
  // Per-case requirements (admin-created) folded into this ONE surface.
  requirements: caseRequirements = [],
  canManageRequirements = false,
  canFulfilRequirements = false,
  // Waivers ("No consent form — <reason>") live in type_specific.waivers; the
  // slot reads them and canWaive (capability waiveReq) gates record/withdraw.
  typeSpecific = null,
  canWaive = false,
  // Case date of incident (YYYY-MM-DD) — lower bound for a post photo's taken-on.
  incidentDate = null,
}) {
  // Matrix slots + the consent form on every Treatment case (the wizard
  // captures it on every submission, so the upload or waiver must have a slot).
  const requiredUploads = caseDocumentSlots(eventType, treatment, severity);
  const waivers        = getWaivers(typeSpecific);
  const fileInputRef   = useRef(null);
  const pendingCatRef  = useRef(null);
  const reqFileInputRef = useRef(null);
  const pendingReqIdRef = useRef(null);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError]     = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState(new Set());  // raw numeric attachment ids
  const [typedCat, setTypedCat] = useState(null);       // prose slot with the type-it-in pop-up open
  const [metaCat, setMetaCat]   = useState(null);       // slot whose file+metadata modal is open
  const [metaReq, setMetaReq]   = useState(null);       // per-case requirement whose file+metadata modal is open
  const [waiveCat, setWaiveCat] = useState(null);       // slot whose "record no document" modal is open
  const [waiveBusy, setWaiveBusy] = useState(false);
  const [waiveError, setWaiveError] = useState(null);
  const [withdrawCat, setWithdrawCat] = useState(null); // slot whose waiver-withdraw confirm is open
  // Per-case requirement state
  const [busyReqId, setBusyReqId]       = useState(null);  // upload/complete in flight
  const [removingId, setRemovingId]     = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null); // { id, label, kind }
  const [signoffMsg, setSignoffMsg]     = useState({});      // { [reqId]: message }
  const [notice, setNotice]             = useState(null);    // soft, non-error notice

  const reqs = Array.isArray(caseRequirements) ? caseRequirements : [];

  function toggleSelected(att) {
    const raw = typeof att.id === 'string' ? parseInt(att.id.replace('att-', ''), 10) : att.id;
    if (!Number.isFinite(raw)) return;
    setSelected(prev => {
      const next = new Set(prev);
      next.has(raw) ? next.delete(raw) : next.add(raw);
      return next;
    });
  }

  function exitEditMode() {
    setEditMode(false);
    setSelected(new Set());
  }

  // When the attachments list refreshes (e.g. after a delete), drop any
  // selected ids that no longer exist and exit edit mode if nothing remains
  // to act on.
  useEffect(() => {
    const validIds = new Set(
      (attachments || [])
        .map(a => typeof a.id === 'string' ? parseInt(a.id.replace('att-', ''), 10) : a.id)
        .filter(Number.isFinite)
    );
    setSelected(prev => {
      const next = new Set([...prev].filter(id => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [attachments]);

  // The card is shown only when it has something to display — a matrix-driven
  // required upload (a compliance to-do, kept visible even while unfilled) or a
  // per-case requirement. When there's nothing, hide the whole card; managers
  // add requirements from the Comments composer ("+ Add → Requirement"), and the
  // card appears once one exists. A matrix row that is optional-only (AWI Code
  // 1–2: optionalFields but no uploads) is "nothing" here — the guard keys on
  // the UPLOAD list, never on the row being non-null. NOTE: this guard MUST sit
  // after every hook above — an early return before a hook changes the hook
  // count between renders and crashes React ("Rendered fewer hooks than expected").
  if (requiredUploads.length === 0 && reqs.length === 0) return null;

  // Bucket existing attachments by category via filename parsing.
  const byCategory = {};
  for (const att of (attachments || [])) {
    const parsed = parseAttachmentName(att.name);
    if (parsed?.categoryKey && requiredUploads.includes(parsed.categoryKey)) {
      (byCategory[parsed.categoryKey] = byCategory[parsed.categoryKey] || []).push(att);
    }
  }

  // `metadata` is the one-per-batch object SlotUploadModal collects for
  // categories that need it (post photo taken-on, training certificate trainer
  // + date); it travels with EVERY file in the batch. null for plain slots.
  async function uploadForCategory(catKey, files, metadata = null) {
    if (!files.length) return;
    setBusyKey(catKey);
    setError(null);
    let uploaded = 0;
    try {
      const existing = byCategory[catKey]?.length || 0;
      for (const file of files) {
        const renamed = buildUploadFile({ file, category: catKey, otherLabel: null }, caseId, existing + uploaded);
        // Send the category explicitly (not just encoded in the filename) so the
        // server can authorise a respond-only role uploading into this matrix slot.
        await uploadCaseAttachment(caseId, renamed, { category: catKey, metadata });
        uploaded++;
      }
    } catch (e) {
      setError(files.length > 1
        ? `Upload failed after ${uploaded} of ${files.length} files: ${e.message || 'unknown error'}`
        : (e.message || 'Upload failed.'));
    } finally {
      setBusyKey(null);
      if (uploaded > 0) onChange?.();
    }
  }

  function onSlotClick(catKey) {
    if (!canRespond || busyKey) return;
    // Prose categories (therapist statement) open a type-it-in pop-up that
    // saves as a .txt upload; the pop-up offers the OS picker as well.
    if (TYPED_TEXT_CATEGORIES.has(catKey)) {
      setTypedCat(catKey);
      return;
    }
    // Two-step categories: pick the file(s), then the facts the file can't
    // carry (when the photo was taken / who trained, when) — one modal.
    if (categoryNeedsMetadata(catKey)) {
      setMetaCat(catKey);
      return;
    }
    pendingCatRef.current = catKey;
    fileInputRef.current?.click();
  }

  // ── Waivers ("No consent form — <reason>") ─────────────────────────────────
  async function recordWaiver(catKey, reason) {
    setWaiveBusy(true);
    setWaiveError(null);
    try {
      await waiveRequirement(caseId, catKey, reason);
      setWaiveCat(null);
      onChange?.();
    } catch (e) {
      setWaiveError(e.message || 'Could not record this.');
    } finally {
      setWaiveBusy(false);
    }
  }

  async function withdrawWaiver(catKey) {
    setWaiveBusy(true);
    setError(null);
    try {
      await unwaiveRequirement(caseId, catKey);
      setWithdrawCat(null);
      onChange?.();
    } catch (e) {
      setWithdrawCat(null);
      setError(e.message || 'Could not withdraw the record.');
    } finally {
      setWaiveBusy(false);
    }
  }

  function onFileChosen(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const catKey = pendingCatRef.current;
    pendingCatRef.current = null;
    if (files.length && catKey) uploadForCategory(catKey, files);
  }

  // ── Per-case requirement helpers ──────────────────────────────────────────
  // Bucket bound attachments by requirement id via the 'req:<id>' category.
  const byReq = {};
  for (const att of (attachments || [])) {
    if (!att.category) continue;
    for (const r of reqs) {
      if (att.category === reqCategory(r.id)) {
        (byReq[r.id] = byReq[r.id] || []).push(att);
      }
    }
  }

  // Edit mode bulk-deletes uploaded files; only offer it when there is at least
  // one file to manage (empty slots/sign-offs have nothing to delete).
  const hasUploadedFiles = Object.keys(byCategory).length > 0 || Object.keys(byReq).length > 0;

  // `metadata` is the one-per-batch object SlotUploadModal collects when the
  // requirement maps onto a two-step slot (a per-case "Training certificate"
  // carries trainer + date exactly like the matrix slot); null for plain
  // requirements. The category stays 'req:<id>' — the server resolves the
  // canonical slot from the live requirement and validates against it.
  async function uploadForReq(reqId, files, metadata = null) {
    if (!files.length) return;
    setBusyReqId(reqId);
    setError(null);
    let uploaded = 0;
    try {
      for (const file of files) {
        await uploadCaseAttachment(caseId, file, { category: reqCategory(reqId), metadata });
        uploaded++;
      }
    } catch (e) {
      setError(files.length > 1
        ? `Upload failed after ${uploaded} of ${files.length} files: ${e.message || 'unknown error'}`
        : (e.message || 'Upload failed.'));
    } finally {
      setBusyReqId(null);
      if (uploaded > 0) onChange?.();
    }
  }

  function onReqUploadClick(reqId) {
    if (!canFulfilRequirements || busyReqId) return;
    // A requirement that maps onto a two-step slot (training certificate, post
    // photo) collects the file(s) + the same details the matrix slot asks for;
    // everything else goes straight to the OS picker.
    const r = reqs.find(x => x && x.id === reqId);
    if (r && categoryNeedsMetadata(canonicalCategoryForRequirement(r))) {
      setMetaReq(r);
      return;
    }
    pendingReqIdRef.current = reqId;
    reqFileInputRef.current?.click();
  }

  function onReqFileChosen(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const reqId = pendingReqIdRef.current;
    pendingReqIdRef.current = null;
    if (files.length && reqId) uploadForReq(reqId, files);
  }

  async function completeSignoff(reqId) {
    if (busyReqId) return;
    setBusyReqId(reqId);
    setError(null);
    setNotice(null);
    try {
      const res = await completeCaseRequirement(caseId, reqId, { message: (signoffMsg[reqId] || '').trim() || undefined });
      setSignoffMsg(prev => { const n = { ...prev }; delete n[reqId]; return n; });
      // Completion is recorded server-side regardless; surface a soft notice when
      // the issuer couldn't be emailed so the completer isn't left assuming it sent.
      if (res && res.issuerNotified === false) {
        setNotice('Sign-off recorded, but we couldn’t notify the issuer by email.');
      }
      onChange?.();
    } catch (e) {
      setError(e.message || 'Could not complete sign-off.');
    } finally {
      setBusyReqId(null);
    }
  }

  async function removeReq(reqId) {
    setRemovingId(reqId);
    setError(null);
    setNotice(null);
    try {
      // Server response: { removedFiles, filesKept } — files go with the
      // requirement when the caller may delete files; otherwise they stay as
      // loose attachments and we say so.
      const res = await deleteCaseRequirement(caseId, reqId);
      setPendingRemove(null);
      if (res && res.filesKept > 0) {
        setNotice(`Requirement removed. Its ${res.filesKept} uploaded file${res.filesKept === 1 ? '' : 's'} stay${res.filesKept === 1 ? 's' : ''} in the Attachments list — your role cannot delete files.`);
      }
      onChange?.();
    } catch (e) {
      setError(e.message || 'Could not remove requirement.');
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <>
    <div className="panel">
      <div className="panel-head">
        <div>
          <div className="panel-title">
            Required documents &amp; sign-offs
            {editMode && selected.size > 0 && (
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: 'var(--text-3)' }}>
                {selected.size} selected
              </span>
            )}
          </div>
          <div className="panel-sub">
            {requiredUploads.length > 0 && <>Required for {treatment || '—'} · {severity || '—'}. </>}
            {editMode
              ? 'Tick files to delete, then confirm.'
              : canRespond ? 'Upload into each slot.' : 'Read-only for your role.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {editMode ? (
            <>
              {selected.size > 0 && (
                <button
                  className="btn sm danger filled"
                  disabled={attDeleting}
                  onClick={() => onBulkDelete?.([...selected])}
                >
                  {attDeleting ? 'Deleting…' : `Delete ${selected.size}`}
                </button>
              )}
              <button
                type="button"
                className="btn ghost sm"
                onClick={exitEditMode}
              >
                Cancel
              </button>
            </>
          ) : (
            canEdit && hasUploadedFiles && <EditBtn onClick={() => { setEditMode(true); setSelected(new Set()); }} />
          )}
        </div>
      </div>
      <div className="panel-body">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={onFileChosen}
        />

        {typedCat && (
          <TypedTextFileModal
            title={ATTACHMENT_CATEGORY_LABELS[typedCat] || typedCat}
            placeholder="Type the statement…"
            saving={busyKey === typedCat}
            onClose={() => setTypedCat(null)}
            onChooseFile={() => {
              pendingCatRef.current = typedCat;
              setTypedCat(null);
              fileInputRef.current?.click();
            }}
            onSave={async (text) => {
              const cat  = typedCat;
              const file = new File([text], `${categoryToken({ category: cat, otherLabel: null })}.txt`, { type: 'text/plain' });
              setTypedCat(null);
              await uploadForCategory(cat, [file]);
            }}
          />
        )}

        {/* Two-step slot upload: file(s) + per-category metadata (post photo
            taken-on; training certificate trainer + date). */}
        {metaCat && (
          <SlotUploadModal
            open
            category={metaCat}
            label={ATTACHMENT_CATEGORY_LABELS[metaCat] || metaCat}
            // Base name of the NEXT file in this slot (the dialog appends the
            // chosen file's extension) — numbered past what is already there.
            fileNamePreview={(() => {
              const n = byCategory[metaCat]?.length || 0;
              return `AE${caseId}-${categoryToken({ category: metaCat, otherLabel: null })}${n > 0 ? `-${n + 1}` : ''}`;
            })()}
            multiple
            incidentDate={incidentDate || null}
            onCancel={() => setMetaCat(null)}
            onConfirm={async ({ files, metadata } = {}) => {
              const cat = metaCat;
              setMetaCat(null);
              await uploadForCategory(cat, Array.from(files || []), metadata || null);
            }}
          />
        )}

        {/* Two-step upload for a per-case requirement that maps onto a metadata
            slot (shared canonicalCategoryForRequirement): the dialog shows that
            slot's fields under the requirement's own label. Files keep their
            names (no AE{id}-{Token} rename — that would double-bucket them into
            the matrix slot by filename). */}
        {metaReq && (
          <SlotUploadModal
            open
            category={canonicalCategoryForRequirement(metaReq)}
            label={metaReq.label}
            multiple
            incidentDate={incidentDate || null}
            onCancel={() => setMetaReq(null)}
            onConfirm={async ({ files, metadata } = {}) => {
              const r = metaReq;
              setMetaReq(null);
              await uploadForReq(r.id, Array.from(files || []), metadata || null);
            }}
          />
        )}

        {/* "Record no consent form" — reason required, ≤500 chars. */}
        {waiveCat && (
          <WaiverModal
            label={ATTACHMENT_CATEGORY_LABELS[waiveCat] || waiveCat}
            saving={waiveBusy}
            error={waiveError}
            onClose={() => { if (!waiveBusy) { setWaiveCat(null); setWaiveError(null); } }}
            onConfirm={(reason) => recordWaiver(waiveCat, reason)}
          />
        )}

        {error && (
          <div className="callout danger" style={{ marginBottom: 10, fontSize: 12 }}>
            <div style={{ flex: 1 }}>{error}</div>
            <button className="btn ghost sm" onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {notice && (
          <div className="callout warn" style={{ marginBottom: 10, fontSize: 12 }}>
            <div style={{ flex: 1 }}>{notice}</div>
            <button className="btn ghost sm" onClick={() => setNotice(null)}>Dismiss</button>
          </div>
        )}

        <div className="attach-slot-grid">
          {requiredUploads.map(catKey => {
            const matches   = byCategory[catKey] || [];
            const uploaded  = matches.length > 0;
            // Third state: the document is recorded as not existing (waiver).
            // An actual upload always wins over a stale waiver (uploads
            // auto-withdraw server-side; legacy flags may linger).
            const waiver    = !uploaded && isWaived(typeSpecific, catKey) ? waivers[catKey] : null;
            const satisfied = uploaded || !!waiver;
            const label     = ATTACHMENT_CATEGORY_LABELS[catKey] || catKey;
            const noLabel   = `No ${label.toLowerCase()}`;
            const busy      = busyKey === catKey;
            const typed     = TYPED_TEXT_CATEGORIES.has(catKey);
            const waivable  = isWaivable(catKey);
            return (
              <div key={catKey} className="attach-slot attach-slot--card" data-satisfied={uploaded ? 'true' : waiver ? 'waived' : 'false'}>
                {/* The head is static and always one line tall — icon + title sit
                    at the same height on every card. Every action is a button in
                    the actions column at the right (Upload / Add more, Record
                    no … / Withdraw), which spans the card so a second button never
                    pushes the title down. A waiver shows as a file-style row below. */}
                <div className="attach-slot-head static" title={typed ? `${label} — type it in or attach a file` : label}>
                  <SlotStateIcon state={busy ? 'uploading' : uploaded ? 'done' : waiver ? 'waived' : 'required'} />
                  <span className="attach-slot-label">{label}</span>
                  <span className="attach-slot-status">
                    {busy
                      ? ''
                      : uploaded
                        ? `${matches.length} attached`
                        : waiver
                          ? ''
                          : (canRespond ? '' : 'Required — not uploaded')}
                  </span>
                </div>
                {canRespond && (
                    <span className="slot-actions">
                      <button
                        type="button"
                        className="btn ghost sm slot-action"
                        disabled={busy}
                        onClick={() => onSlotClick(catKey)}
                        aria-label={uploaded ? `Add another ${label} file` : `Upload ${label}`}
                        title={uploaded
                          ? `Add another ${label.toLowerCase()}${typed ? '' : ' file'}`
                          : waiver
                            ? `Upload a ${label.toLowerCase()} anyway — this withdraws the "${noLabel}" record`
                            : (typed ? `Type the ${label.toLowerCase()} in or attach a file` : `Upload the ${label.toLowerCase()}`)}
                      >
                        {busy ? 'Uploading…' : uploaded ? 'Add more' : 'Upload'}
                        {busy ? <span className="spinner" /> : <UploadIcon />}
                      </button>
                      {!satisfied && waivable && canWaive && (
                        <button
                          type="button"
                          className="btn ghost sm slot-action"
                          disabled={busy || waiveBusy}
                          onClick={() => { setWaiveError(null); setWaiveCat(catKey); }}
                          title={`Record that there is no ${label.toLowerCase()} for this case (a reason is required)`}
                        >
                          Record {noLabel.toLowerCase()}<PencilSquareIcon />
                        </button>
                      )}
                      {waiver && canWaive && (
                        <button
                          type="button"
                          className="btn ghost sm slot-action slot-action--body"
                          disabled={waiveBusy || busy}
                          onClick={() => { setError(null); setWithdrawCat(catKey); }}
                          title={`Withdraw the "${noLabel}" record — the ${label.toLowerCase()} becomes required again`}
                        >
                          Withdraw<ArrowCounterClockwiseIcon size={13} />
                        </button>
                      )}
                    </span>
                )}

                {/* Waiver row — laid out like a file row: "No consent form" with
                    "<reason> · by X · date" beneath (Withdraw sits in the actions column) */}
                {waiver && (
                  <div className="attach-slot-waiver">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="9.5" y1="12.5" x2="14.5" y2="17.5"/><line x1="14.5" y1="12.5" x2="9.5" y2="17.5"/>
                    </svg>
                    <div className="attach-slot-waiver-body">
                      <div className="attach-slot-waiver-title">{noLabel}</div>
                      <div className="attach-meta-line">
                        {[waiver.reason, waiver.byName ? `by ${waiver.byName}` : null, waiver.at ? formatDate(waiver.at) : null]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  </div>
                )}

                {matches.length > 0 && (
                  <div className="attach-slot-files">
                    {matches.map(att => {
                      const raw = typeof att.id === 'string' ? parseInt(att.id.replace('att-', ''), 10) : att.id;
                      const checked = Number.isFinite(raw) && selected.has(raw);
                      return (
                        <SlotFileRow
                          key={att.id || att.name}
                          att={att}
                          editMode={editMode}
                          checked={checked}
                          onToggle={() => toggleSelected(att)}
                          disabled={attDeleting}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Per-case requirements (admin-created) — document slots + sign-off
            tasks, folded into this ONE surface. */}
        <input ref={reqFileInputRef} type="file" multiple style={{ display: 'none' }} onChange={onReqFileChosen} />

        {(reqs.length > 0 || canManageRequirements) && (
          <div style={{ marginTop: requiredUploads.length > 0 ? 14 : 0 }}>
            {requiredUploads.length > 0 && (
              <div className="incident-extras-title" style={{ display: 'block', margin: '0 0 8px' }}>
                Requested for this case
              </div>
            )}
            {reqs.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                {canManageRequirements
                  ? 'Nothing extra has been requested yet. Use “Add requirement” in a comment to ask for a document or sign-off.'
                  : 'Nothing extra has been requested for this case.'}
              </p>
            ) : (
              <div className="attach-slot-grid">
                {reqs.map(r => {
                  const kind      = requirementKind(r);
                  const isSignoff = kind === REQ_KIND_SIGNOFF;
                  const matches   = byReq[r.id] || [];
                  const satisfied = isSignoff ? isSignoffDone(r) : matches.length > 0;
                  const busy      = busyReqId === r.id;
                  return (
                    <div key={r.id} className="attach-slot" data-satisfied={satisfied ? 'true' : 'false'}>
                      <div className="attach-slot-head" style={{ cursor: 'default' }} title={isSignoff ? `Sign-off: ${r.label}` : r.label}>
                        <SlotStateIcon state={busy ? 'uploading' : satisfied ? 'done' : 'required'} />
                        <span className="attach-slot-label">
                          {isSignoff ? `Sign-off: ${r.label}` : r.label}
                        </span>
                        <span className="attach-slot-status">
                          {/* A fulfiller sees the actionable "Awaiting …" wording;
                              a read-only viewer sees neutral "Pending" — "Awaiting
                              upload" would imply an action they can't take. */}
                          {satisfied
                            ? (isSignoff ? 'Signed off' : `${matches.length} uploaded`)
                            : (canFulfilRequirements
                                ? (isSignoff ? 'Awaiting sign-off' : 'Awaiting upload')
                                : 'Pending')}
                        </span>
                        <span style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                          {!isSignoff && canFulfilRequirements && (
                            <button
                              type="button"
                              className="btn ghost sm slot-action"
                              disabled={busy}
                              onClick={() => onReqUploadClick(r.id)}
                              title={satisfied ? 'Add another file' : `Upload the ${r.label}`}
                            >
                              {busy ? 'Uploading…' : (satisfied ? 'Add more' : 'Upload')}
                              {busy ? <span className="spinner" /> : <UploadIcon />}
                            </button>
                          )}
                          {canManageRequirements && (
                            <button
                              type="button"
                              className="btn ghost sm"
                              disabled={removingId === r.id}
                              onClick={() => { setError(null); setPendingRemove({ id: r.id, label: r.label, kind, fileCount: isSignoff ? 0 : matches.length }); }}
                              style={{ fontSize: 11, color: 'var(--danger)' }}
                              title={isSignoff
                                ? 'Remove this sign-off task'
                                : (matches.length
                                    ? `Remove this requirement and its ${matches.length} uploaded file${matches.length === 1 ? '' : 's'}`
                                    : 'Remove this requirement')}
                            >
                              {removingId === r.id ? 'Removing…' : 'Remove'}
                            </button>
                          )}
                        </span>
                      </div>

                      {(r.note || r.requestedByName) && (
                        <div style={{ padding: '0 12px 6px 42px', fontSize: 11.5, color: 'var(--text-3)' }}>
                          {r.note && <div style={{ marginBottom: 2 }}>{r.note}</div>}
                          {r.requestedByName && (
                            <div>Requested by {r.requestedByName}{r.requestedAt ? ` · ${formatDate(r.requestedAt)}` : ''}</div>
                          )}
                        </div>
                      )}

                      {/* Document: uploaded file pills */}
                      {!isSignoff && matches.length > 0 && (
                        <div style={{ padding: '0 12px 10px 42px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {matches.map(att => {
                            const raw = typeof att.id === 'string' ? parseInt(att.id.replace('att-', ''), 10) : att.id;
                            const checked = Number.isFinite(raw) && selected.has(raw);
                            return (
                              <div key={att.id || att.name} style={{ display: 'flex', flexDirection: 'column' }}>
                                {/* SlotFileRow shows the upload metadata line (trainer · trained /
                                    taken) under the file name, exactly as the matrix slots do. */}
                                <SlotFileRow
                                  att={att}
                                  editMode={editMode}
                                  checked={checked}
                                  onToggle={() => toggleSelected(att)}
                                  disabled={attDeleting}
                                />
                                {(att.uploadedBy || att.uploadedAt) && (
                                  <span style={{ fontSize: 11, color: 'var(--text-3)', paddingLeft: editMode ? 47 : 23 }}>
                                    {att.uploadedBy ? `Uploaded by ${att.uploadedBy}` : 'Uploaded'}
                                    {att.uploadedAt ? ` · ${formatDate(att.uploadedAt)}` : ''}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Sign-off: done -> attribution + message on the case;
                          not done + fulfiller -> message input + Mark completed. */}
                      {isSignoff && satisfied && (
                        <div style={{ padding: '0 12px 10px 42px', fontSize: 12, color: 'var(--text-2)' }}>
                          <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                            Signed off by {r.completion?.completedByName || 'a team member'}
                            {r.completion?.completedAt ? ` on ${formatDate(r.completion.completedAt)}` : ''}
                          </div>
                          {r.completion?.message && (
                            <div style={{ marginTop: 4, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 8, whiteSpace: 'pre-wrap' }}>
                              {r.completion.message}
                            </div>
                          )}
                        </div>
                      )}
                      {isSignoff && !satisfied && canFulfilRequirements && (
                        <div style={{ padding: '0 12px 10px 42px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <input
                            type="text"
                            className="input"
                            placeholder="Add a note for this sign-off (optional)…"
                            maxLength={500}
                            value={signoffMsg[r.id] || ''}
                            onChange={e => setSignoffMsg(prev => ({ ...prev, [r.id]: e.target.value }))}
                            style={{ fontSize: 12, padding: '5px 10px' }}
                          />
                          <button
                            type="button"
                            className="btn primary sm"
                            disabled={busy}
                            onClick={() => completeSignoff(r.id)}
                            style={{ fontSize: 12, alignSelf: 'flex-start' }}
                          >
                            {busy ? 'Saving…' : 'Mark completed'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {withdrawCat && (
      <ConfirmDialog
        message={`Withdraw the "No ${(ATTACHMENT_CATEGORY_LABELS[withdrawCat] || withdrawCat).toLowerCase()}" record?`}
        detail={`The ${(ATTACHMENT_CATEGORY_LABELS[withdrawCat] || withdrawCat).toLowerCase()} becomes required again and the slot shows as outstanding. This action is logged in the case history.`}
        confirmLabel="Withdraw"
        saving={waiveBusy}
        onConfirm={() => withdrawWaiver(withdrawCat)}
        onCancel={() => !waiveBusy && setWithdrawCat(null)}
      />
    )}

    {pendingRemove && (() => {
      // The files uploaded against a requirement go WITH it (server soft-deletes
      // them in the same transaction) — say how many, up front. A manager whose
      // role cannot delete files keeps them as loose attachments instead.
      const isSignoff = pendingRemove.kind === REQ_KIND_SIGNOFF;
      const n = pendingRemove.fileCount || 0;
      const files = `${n} uploaded file${n === 1 ? '' : 's'}`;
      const message = isSignoff
        ? `Remove the "${pendingRemove.label}" sign-off?`
        : n > 0
          ? `Remove the "${pendingRemove.label}" requirement and its ${files}?`
          : `Remove the "${pendingRemove.label}" requirement?`;
      const detail = isSignoff
        ? 'This removes the sign-off task from the case. This action is logged in the case history.'
        : n > 0
          ? (canEdit
              ? `The ${files} ${n === 1 ? 'is' : 'are'} deleted from the case together with the requirement. This action is logged in the case history.`
              : `Your role cannot delete files, so the ${files} stay${n === 1 ? 's' : ''} in the Attachments list. This action is logged in the case history.`)
          : 'This removes the requirement from the case. This action is logged in the case history.';
      return (
        <ConfirmDialog
          message={message}
          detail={detail}
          confirmLabel="Remove"
          danger
          saving={removingId === pendingRemove.id}
          onConfirm={() => removeReq(pendingRemove.id)}
          onCancel={() => setPendingRemove(null)}
        />
      );
    })()}
    </>
  );
}

/**
 * "Record no <document>" pop-up — a required reason (≤500 chars) that is
 * stored on the case as a waiver, so the slot counts as satisfied with the
 * explanation visible. Same overlay styling as TypedTextFileModal.
 */
const WAIVER_REASON_MAX = 500;
function WaiverModal({ label, saving = false, error = null, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const ready = trimmed.length > 0 && trimmed.length <= WAIVER_REASON_MAX;
  const lower = (label || 'document').toLowerCase();

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Record no ${lower}`}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        padding: 20, width: 'min(520px, calc(100vw - 32px))',
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Record no {lower}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12, lineHeight: 1.5 }}>
          Explain why there is no {lower} for this case. The slot then counts as complete, with your
          reason, name and the date shown on the case file and logged in its history. Uploading a {lower}
          later withdraws this record automatically.
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="waiver-reason">Reason <span className="req">*</span></label>
          <CharCountTextarea
            id="waiver-reason"
            limit={WAIVER_REASON_MAX}
            value={reason}
            autoFocus
            disabled={saving}
            placeholder={`e.g. Client declined to sign; treatment went ahead on verbal consent recorded in notes.`}
            style={{ minHeight: 96, fontSize: 13 }}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        {error && (
          <div className="callout danger" style={{ marginTop: 12, fontSize: 12 }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="btn primary"
            disabled={!ready || saving}
            onClick={() => onConfirm(trimmed)}
          >
            {saving ? 'Saving…' : 'Record'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Compact file row inside a slot. In view mode it's a link that opens the
// file in a new tab; in edit mode it becomes a tickable selection row whose
// state is owned by the parent panel (matches the Attachments grid pattern).
// A file carrying upload metadata (post photo taken-on, training certificate
// trainer + date) shows it on a second line.
function SlotFileRow({ att, editMode = false, checked = false, onToggle, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const previewable = att.contentUrl && canPreview(att.name);
  const metaLine = attachmentMetadataLine(att.metadata, { formatDate });

  // Non-previewable fallback: save to disk (a plain <a download> on the
  // cross-origin SAS URL would open a new tab instead).
  async function downloadFile() {
    if (!att.contentUrl || downloading) return;
    setDownloading(true);
    try {
      await saveUrlToDisk(att.contentUrl, att.name);
    } catch {
      window.open(att.contentUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  }

  const inner = (
    <>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 13, height: 13, color: 'var(--brand)', flexShrink: 0 }}>
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
          {att.name}
        </div>
        {metaLine && <div className="attach-meta-line">{metaLine}</div>}
      </div>
    </>
  );

  if (editMode) {
    return (
      <label
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '2px 0', cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={() => onToggle?.()}
          style={{ accentColor: 'var(--brand)', width: 14, height: 14, flexShrink: 0 }}
        />
        {inner}
      </label>
    );
  }

  // Previewable files open the same FilePreview modal the Attachments grid
  // uses (download lives in its toolbar); everything else keeps the direct
  // new-tab download link.
  if (previewable) {
    return (
      <>
        {open && (
          <FilePreview url={att.contentUrl} name={att.name} onClose={() => setOpen(false)} />
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Click to preview"
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'none', border: 'none', textAlign: 'left',
            color: 'var(--text)', padding: '2px 0', cursor: 'zoom-in', width: '100%',
          }}
        >
          {inner}
        </button>
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={downloadFile}
      disabled={!att.contentUrl || downloading}
      title={downloading ? 'Downloading…' : 'Download to disk'}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'none', border: 'none', textAlign: 'left',
        color: 'var(--text)', padding: '2px 0', width: '100%',
        cursor: downloading ? 'wait' : (att.contentUrl ? 'pointer' : 'default'),
      }}
    >
      {inner}
    </button>
  );
}

// ── Attachment archive downloader ──────────────────────────────────────────────

function AttachmentArchive({ attachments, caseRef }) {
  const keyOf = (att) => att.id || att.name;
  const [sel, setSel]       = useState(() => new Set(attachments.map(keyOf)));
  const [zipping, setZip]   = useState(false);
  const [zipErr, setZipErr] = useState(null);
  const [progress, setProgress] = useState(0);
  const [dlKey, setDlKey]   = useState(null);  // key of the single file being downloaded

  // Reconcile selection when the attachment list changes (add/delete).
  useEffect(() => {
    const valid = new Set(attachments.map(keyOf));
    setSel(prev => {
      const next = new Set([...prev].filter(k => valid.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [attachments]);

  if (attachments.length === 0) return null;

  // Single-file download — fetch → object URL so the file saves to disk.
  // A plain <a download> on the cross-origin SAS URL just opens a new tab.
  async function downloadOne(att) {
    const k = keyOf(att);
    if (!att.contentUrl || dlKey === k) return;
    setDlKey(k); setZipErr(null);
    try {
      await saveUrlToDisk(att.contentUrl, att.name);
    } catch (e) {
      setZipErr(`Could not download ${att.name}: ${e.message}`);
    } finally {
      setDlKey(null);
    }
  }

  function toggle(att) {
    const k = keyOf(att);
    setSel(prev => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  }

  const allSelected = sel.size === attachments.length;
  function toggleAll() {
    setSel(allSelected ? new Set() : new Set(attachments.map(keyOf)));
  }

  async function downloadZip() {
    const chosen = attachments.filter(a => sel.has(keyOf(a)) && a.contentUrl);
    if (chosen.length === 0) return;
    setZip(true); setZipErr(null); setProgress(0);
    try {
      const { default: JSZip } = await import('jszip');
      const zip  = new JSZip();
      const used = new Map();   // de-dupe filenames within the archive
      let done = 0;
      for (const att of chosen) {
        const res = await fetch(att.contentUrl);
        if (!res.ok) throw new Error(`Could not fetch ${att.name} (${res.status})`);
        const blob = await res.blob();
        let name = friendlyName(att.name, att._fieldName) || att.name || 'file';
        // friendlyName can drop the extension (e.g. Reserved_ImageAttachment → "Photo");
        // re-append the original extension so the zipped file opens correctly.
        const ext = (att.name.match(/\.[^.]+$/) || [''])[0];
        if (ext && !name.toLowerCase().endsWith(ext.toLowerCase())) name += ext;
        if (used.has(name)) {
          const n = used.get(name) + 1; used.set(name, n);
          name = name.replace(/(\.[^.]+)?$/, `-${n}$1`);
        } else { used.set(name, 1); }
        zip.file(name, blob);
        setProgress(Math.round((++done / chosen.length) * 100));
      }
      const out = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(out);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${caseRef || 'case'}-attachments.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setZipErr(e.message || 'Could not build the ZIP.');
    } finally {
      setZip(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <div className="panel-title">Attachments</div>
        <button
          className="btn primary sm"
          onClick={downloadZip}
          disabled={zipping || sel.size === 0}
          style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          {zipping ? `Zipping… ${progress}%` : `Download ZIP${sel.size ? ` (${sel.size})` : ''}`}
        </button>
      </div>
      <div className="panel-body">
        {zipErr && (
          <div className="callout danger" style={{ marginBottom: 10, fontSize: 12 }}>
            <div style={{ flex: 1 }}>{zipErr}</div>
          </div>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px 8px', cursor: 'pointer', fontSize: 12, color: 'var(--text-3)' }}>
          <input type="checkbox" checked={allSelected} onChange={toggleAll}
            style={{ accentColor: 'var(--brand)', width: 14, height: 14 }} />
          Select all ({attachments.length})
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {attachments.map(att => {
            const key   = keyOf(att);
            const label = friendlyName(att.name, att._fieldName);
            const busy  = dlKey === key;
            return (
              <div
                key={key}
                style={{
                  display: 'flex', gap: 10, alignItems: 'center',
                  padding: '7px 8px', borderRadius: 6, transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <input
                  type="checkbox"
                  checked={sel.has(key)}
                  onChange={() => toggle(att)}
                  style={{ accentColor: 'var(--brand)', width: 14, height: 14, flexShrink: 0 }}
                />
                <FileTypeIcon name={att.name} size={18} />
                <button
                  type="button"
                  onClick={() => downloadOne(att)}
                  disabled={!att.contentUrl || busy}
                  title="Download to disk"
                  style={{
                    fontSize: 13, flex: 1, color: 'var(--text)', textAlign: 'left',
                    background: 'none', border: 'none', padding: 0,
                    cursor: busy ? 'wait' : (att.contentUrl ? 'pointer' : 'default'),
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {busy ? `Downloading ${label}…` : label}
                </button>
                <button
                  type="button"
                  onClick={() => downloadOne(att)}
                  disabled={!att.contentUrl || busy}
                  title="Download to disk"
                  style={{
                    color: 'var(--text)', display: 'flex', background: 'none', border: 'none',
                    padding: 0, cursor: busy ? 'wait' : (att.contentUrl ? 'pointer' : 'default'),
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round"
                       style={{ width: 14, height: 14, flexShrink: 0, opacity: 0.4 }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── History card ───────────────────────────────────────────────────────────────

// Colours mirror styles/global.css tokens — kept as literal hex because the
// renderers append alpha suffixes (`${meta.color}22`), which var() can't do.
const EVENT_META = {
  case_created:        { color: '#059669', icon: 'M12 5v14M5 12h14' },                                    // --ok
  case_closed:         { color: '#8A94A6', icon: 'M20 6L9 17l-5-5' },                                     // --text-3
  case_resolved:       { color: '#059669', icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8' },
  case_reopened:       { color: '#D97706', icon: 'M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15' }, // --warn
  status_changed:      { color: '#2563EB', icon: 'M4 4h16v16H4zM9 9h6M9 12h6M9 15h4' },                   // --info
  severity_changed:    { color: '#D97706', icon: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01' }, // --warn
  case_updated:        { color: '#7C3AED', icon: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' }, // --violet
  comment_added:       { color: '#0D9488', icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' },  // --brand
  comment_edited:      { color: '#7C3AED', icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' },  // --violet
  comment_deleted:     { color: '#DC2626', icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' },  // --danger
  attachment_uploaded: { color: '#0D9488', icon: 'M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48' }, // --brand
  attachment_deleted:  { color: '#DC2626', icon: 'M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48' }, // --danger
  assigned:            { color: '#2563EB', icon: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' },  // --info
  // These render a Phosphor glyph (see PHOSPHOR_GLYPHS) instead of `icon`; only
  // `color` is used, for the tinted square + icon fill.
  notification_sent:   { color: '#6366F1', icon: '' },  // indigo — informational
  tag_added:           { color: '#0891B2', icon: '' },  // cyan — organisational
  tag_removed:         { color: '#DC2626', icon: '' },  // danger — matches *_deleted
  // Requirement waivers ("No consent form — <reason>") and their withdrawal
  // (manual, or automatic when the document is uploaded after all).
  requirement_waived:           { color: '#D97706', icon: '' },  // --warn — a required item consciously set aside
  requirement_waiver_withdrawn: { color: '#0D9488', icon: '' },  // --brand — the requirement is live again
  // Code 4/5 response SLA (sla_escalation_logic.md) + message acknowledgements —
  // Phosphor glyphs below; colours follow the chip tones (info/warn/danger/muted).
  sla_started:          { color: '#2563EB', icon: '' },  // --info — a turn has started
  sla_responded:        { color: '#059669', icon: '' },  // --ok — the owner side answered in time
  sla_paused:           { color: '#D97706', icon: '' },  // --warn — on hold (acknowledged / Waiting on Client)
  sla_resumed:          { color: '#2563EB', icon: '' },  // --info — clock running again
  sla_breached:         { color: '#DC2626', icon: '' },  // --danger — response overdue
  sla_suppressed:       { color: '#8A94A6', icon: '' },  // --text-3 — Supervisor switched the SLA off on this case
  sla_stopped:          { color: '#8A94A6', icon: '' },  // --text-3 — no further timers (closed / severity lowered)
  sla_snoozed:          { color: '#5B21B6', icon: '' },  // violet — the clinic acknowledged a snooze message; clock pushed out
  comment_acknowledged: { color: '#0D9488', icon: '' },  // --brand — a "like" on a message
  case_deleted:        { color: '#DC2626', icon: 'M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }, // --danger
  case_restored:       { color: '#059669', icon: 'M1 4v6h6M3.51 15a9 9 0 1 0 2.13-9.36L1 10' },  // --ok
  // Cost events — pound-sterling sign icon, brand-ink for ledger-ish feel
  cost_added:          { color: '#0F766E', icon: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10z' },
  cost_updated:        { color: '#7C3AED', icon: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10z' },
  cost_deleted:        { color: '#DC2626', icon: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10z' },
};
// Literal hex (not var()) for the same alpha-suffix reason as above.
const DEFAULT_META = { color: '#8A94A6', icon: 'M12 12h.01M12 6h.01M12 18h.01' };

// Event types whose timeline glyph is a Phosphor component (colour from
// EVENT_META) rather than the inline stroke `icon` path. Add an entry here plus
// a colour in EVENT_META to give an event its own icon.
const PHOSPHOR_GLYPHS = {
  notification_sent:            NotificationIcon,
  tag_added:                    TagIcon,
  tag_removed:                  TagIcon,
  status_changed:               FoldersIcon,
  requirement_waived:           ProhibitIcon,
  requirement_waiver_withdrawn: ArrowCounterClockwiseIcon,
  sla_started:                  TimerIcon,
  sla_responded:                CheckCircleIcon,
  sla_paused:                   PauseCircleIcon,
  sla_resumed:                  PlayCircleIcon,
  sla_breached:                 WarningIcon,
  sla_suppressed:               BellSlashIcon,
  sla_stopped:                  StopCircleIcon,
  sla_snoozed:                  MoonIcon,
  comment_acknowledged:         ThumbsUpIcon,
};

// Human line for a waiver event's metadata: the reason (manual waive) or the
// automatic-withdrawal note. null when the event carries neither.
function waiverEventDetail(ev) {
  const m = ev?.metadata || {};
  if (ev?.eventType === 'requirement_waived' && m.reason) return `“${m.reason}”`;
  if (ev?.eventType === 'requirement_waiver_withdrawn' && m.auto) return 'Withdrawn automatically — the document was uploaded.';
  return null;
}

// ── Shared event-row renderer (used in both the card and the full panel) ──────

function EventDot({ color, size = 26 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `${color}18`, border: `1.5px solid ${color}40`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ width: size * 0.38, height: size * 0.38, borderRadius: '50%', background: color }} />
    </div>
  );
}

// ── HistoryCard (right-column preview, last 10) ───────────────────────────────

// Merge attachment_uploaded events into their parent comment_added event.
// Both events share metadata.commentId as the link.
// The merged comment event gets a _files array with { fileName, sizeBytes }.
// Remove private comment/attachment events for ClinicManager role.
// Also drop cost_* events for any role that can't see the cost feature
// (defence-in-depth — the API already filters them out server-side).
function filterEventsForRole(events, role, canSeeCosts = false) {
  let next = events;
  if (!canSeeCosts) {
    next = next.filter(ev => !ev.eventType?.startsWith('cost_'));
  }
  if (role !== 'ClinicManager') return next;
  const privateCommentIds = new Set(
    next
      .filter(ev => ev.eventType === 'comment_added' && ev.metadata?.isPublic === false)
      .map(ev => ev.metadata?.commentId)
      .filter(Boolean)
  );
  return next.filter(ev => {
    if (ev.eventType === 'comment_added' && ev.metadata?.isPublic === false) return false;
    if (ev.eventType === 'comment_acknowledged' && ev.metadata?.isPublic === false) return false;
    if (ev.eventType === 'attachment_uploaded' && privateCommentIds.has(ev.metadata?.commentId)) return false;
    return true;
  });
}

function groupCommentAttachments(events) {
  // Collect all comment_added events keyed by commentId
  const byComment = {};
  for (const ev of events) {
    if (ev.eventType === 'comment_added' && ev.metadata?.commentId != null) {
      byComment[ev.metadata.commentId] = ev;
    }
  }
  // Absorb matching attachment_uploaded events into their parent, and fold any
  // notify tagged to the comment (metadata.commentId) into a _notifications array
  // so the timeline can show "Notification sent to: …" under the comment action.
  // Rule-based / digest sends carry no commentId and stay as their own rows.
  const absorbed = new Set();
  for (const ev of events) {
    if (ev.eventType === 'attachment_uploaded' && ev.metadata?.commentId != null) {
      const parent = byComment[ev.metadata.commentId];
      if (parent) {
        parent._files = parent._files || [];
        parent._files.push({ fileName: ev.metadata.fileName, sizeBytes: ev.metadata.sizeBytes, metadata: ev.metadata.metadata || null });
        absorbed.add(ev.id);
      }
    }
    if (ev.eventType === 'notification_sent' && ev.metadata?.commentId != null) {
      const parent = byComment[ev.metadata.commentId];
      if (parent) {
        parent._notifications = parent._notifications || [];
        parent._notifications.push({
          recipients: Array.isArray(ev.metadata.recipients) ? ev.metadata.recipients : [],
          status:     ev.metadata.status,
        });
        absorbed.add(ev.id);
      }
    }
  }
  return events.filter(ev => !absorbed.has(ev.id));
}

/**
 * Resolve a recipient email → display name for the history timeline, from the
 * same two directories the notify picker uses: TherapieMaps (HoRO/CM/AM, keyed by
 * mail) and the department staff list. Falls back to the raw address when a name
 * isn't known (ad-hoc typed emails, or recipients outside the viewer's scope).
 */
function useRecipientNames() {
  const [byEmail, setByEmail] = useState({});
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getTherapieMapsData().catch(() => null),
      getReferenceStaff().catch(() => null),
    ]).then(([tm, staff]) => {
      if (cancelled) return;
      const m = {};
      const add = (email, name) => {
        const e = String(email || '').trim().toLowerCase();
        if (e && name && !m[e]) m[e] = name;
      };
      if (tm) {
        for (const src of [tm.cmByMail, tm.amByMail, tm.horoByMail]) {
          for (const v of Object.values(src || {})) add(v.mail, v.displayName);
        }
      }
      for (const s of (staff?.staff || [])) add(s.email, s.name);
      setByEmail(m);
    });
    return () => { cancelled = true; };
  }, []);
  return useCallback(
    (email) => byEmail[String(email || '').trim().toLowerCase()] || email,
    [byEmail],
  );
}

/** "Notification sent to: …" — folded under a comment_added event. */
function CommentNotifyLine({ notifications, resolveName, indent = 36 }) {
  const [open, setOpen] = useState(false);
  const emails = [...new Set((notifications || []).flatMap(n => n.recipients || []))].filter(Boolean);
  if (!emails.length) return null;
  const names = emails.map(resolveName);
  const LIMIT = 6;
  const shown = open ? names : names.slice(0, LIMIT);
  const extra = names.length - shown.length;
  return (
    <div style={{ marginTop: 6, marginLeft: indent, fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.5, display: 'flex', gap: 5 }}>
      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
        <path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4 20-7z" />
      </svg>
      <span style={{ minWidth: 0 }}>
        <strong style={{ fontWeight: 600 }}>Notification sent to:</strong> {shown.join(', ')}
        {!open && extra > 0 && (
          <button type="button" onClick={() => setOpen(true)}
            style={{ marginLeft: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand)', fontSize: 11 }}>
            +{extra} more
          </button>
        )}
      </span>
    </div>
  );
}

function HistoryCard({ caseId, refreshKey, onViewAll, role, canSeeCosts }) {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const resolveName = useRecipientNames();

  useEffect(() => {
    if (!caseId) return;
    setLoading(true);
    getCaseEvents(caseId)
      .then(data => setEvents(groupCommentAttachments(filterEventsForRole(Array.isArray(data) ? data : [], role, canSeeCosts))))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [caseId, refreshKey, role, canSeeCosts]);

  const preview = events.slice(0, 6);
  const hasMore = events.length > 6;

  return (
    <div className="panel">
      <div className="panel-head">
        <div className="panel-title">History</div>
        {!loading && events.length > 0 && (
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {events.length} event{events.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="panel-body" style={{ padding: 0 }}>
        {loading && <div style={{ padding: '16px', fontSize: 13, color: 'var(--text-3)' }}>Loading…</div>}
        {!loading && events.length === 0 && <div style={{ padding: '16px', fontSize: 13, color: 'var(--text-3)' }}>No history yet.</div>}
        {!loading && events.length > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {preview.map((ev, i) => {
                const meta   = EVENT_META[ev.eventType] || DEFAULT_META;
                const isLast = i === preview.length - 1 && !hasMore;
                return (
                  <div key={ev.id} style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start',
                    padding: '10px 16px',
                    borderBottom: isLast ? 'none' : '1px solid var(--border)',
                  }}>
                    <EventDot color={meta.color} />
                    <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                      <div style={{ fontSize: 12.5, color: 'var(--text)', lineHeight: 1.4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{ev.label}</span>
                        {ev.eventType?.startsWith('cost_') && ev.metadata?.amount != null && ev.metadata?.currency && (
                          <CostChip amount={ev.metadata.amount} currency={ev.metadata.currency} size="sm" />
                        )}
                      </div>
                      {ev.fieldName && ev.newValue !== null && (
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                          <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{ev.oldValue || '—'}</span>
                          <span style={{ margin: '0 4px' }}>→</span>
                          <span style={{ color: meta.color, fontWeight: 500 }}>{ev.newValue}</span>
                        </div>
                      )}
                      {waiverEventDetail(ev) && (
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, fontStyle: 'italic', overflowWrap: 'anywhere' }}>
                          {waiverEventDetail(ev)}
                        </div>
                      )}
                      {/* Upload metadata (taken / trainer · trained) for the file(s) on this event */}
                      {historyFileMetaLines(ev, { formatDate }).map((line, li) => (
                        <div key={li} style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, overflowWrap: 'anywhere' }}>{line}</div>
                      ))}
                      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>
                        {ev.createdAt ? new Date(ev.createdAt).toLocaleString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                        }) : '—'}
                      </div>
                      {ev.eventType === 'comment_added' && ev._notifications?.length > 0 && (
                        <CommentNotifyLine notifications={ev._notifications} resolveName={resolveName} indent={0} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {hasMore && (
              <button
                onClick={onViewAll}
                style={{
                  display: 'block', width: '100%', padding: '10px 16px',
                  background: 'none', border: 'none', borderTop: '1px solid var(--border)',
                  cursor: 'pointer', fontSize: 12, color: 'var(--brand)', textAlign: 'center',
                  fontFamily: 'inherit', fontWeight: 500,
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                View all {events.length} events →
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── HistoryPanel (full History tab — vertical timeline) ───────────────────────

function groupByDay(events) {
  const groups = [];
  let curDay = null, curGroup = null;
  for (const ev of events) {
    const day = ev.createdAt
      ? new Date(ev.createdAt).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      : 'Unknown date';
    if (day !== curDay) {
      curDay = day;
      curGroup = { day, items: [] };
      groups.push(curGroup);
    }
    curGroup.items.push(ev);
  }
  return groups;
}

// Share a case with a registered clinic staff member — temporary access for
// updates when the CM/AM is away. A header button (share icon + live count in a
// dot) opens a popover to share with, or remove access from, clinic staff.
// A share always ends on case-Closed; a day limit is optional (blank = none).
const SHARE_DAYS_OPTS = [
  { value: '', label: 'Ends when the case is closed' },
  ...['5', '10', '15', '20', '25', '30'].map(d => ({ value: d, label: `Ends after ${d} days` })),
];

// "8 days left" / "1 day left" / "expires today" / null (no cap).
function shareDaysLeft(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const d = Math.ceil(ms / (24 * 60 * 60 * 1000));
  return d <= 1 ? 'expires today' : `${d} days left`;
}

// Case editors only (gated by the caller).
function CaseShareButton({ caseId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);   // { shares, shareableStaff, clinicHasMailbox }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState('');
  const [days, setDays] = useState('');   // optional day cap ('' = ends on Closed only)
  const [error, setError] = useState(null);
  const ref = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getCaseShares(caseId)); }
    catch (e) { setError(e.message || 'Could not load sharing.'); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  // Close the popover on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  async function share() {
    if (!pick || busy) return;
    setBusy(true); setError(null);
    try { await shareCaseWith(caseId, pick, days); setPick(''); await load(); }
    catch (e) { setError(e.message || 'Could not share.'); }
    finally { setBusy(false); }
  }
  async function revoke(shareId) {
    setBusy(true); setError(null);
    try { await revokeCaseShare(caseId, shareId); await load(); }
    catch (e) { setError(e.message || 'Could not revoke.'); }
    finally { setBusy(false); }
  }

  // Hidden entirely when the clinic has no roster and nothing is shared.
  if (!loading && data && !data.clinicHasMailbox && !(data.shares || []).length) return null;
  const count = (data?.shares || []).length;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn" onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open} title="Share with clinic staff">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16 }}>
          <path fillRule="evenodd" clipRule="evenodd" d="M15.75 4.5a3 3 0 1 1 .825 2.066l-8.421 4.679a3.002 3.002 0 0 1 0 1.51l8.421 4.679a3 3 0 1 1-.729 1.31l-8.421-4.678a3 3 0 1 1 0-4.132l8.421-4.679a3 3 0 0 1-.096-.755Z" />
        </svg>
        Share
        {count > 0 && (
          <span className="count">{count}</span>
        )}
      </button>

      {open && (
        <div role="dialog" aria-label="Share case" style={{
          position: 'absolute', top: 'calc(100% + 12px)', right: 0, zIndex: 50, width: 320,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
          boxShadow: '0 12px 32px rgba(0,0,0,.14)', padding: 16, textAlign: 'left',
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Share with clinic staff</div>
          <p style={{ fontSize: 12, color: 'var(--text-3)', margin: '0 0 12px' }}>
            Temporary access to this case, for updates when the manager is away.
          </p>
          {error && <div className="callout danger" style={{ marginBottom: 10, fontSize: 12 }}><div style={{ flex: 1 }}>{error}</div></div>}
          {loading ? <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Loading…</div> : (
            <>
              {count > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  {data.shares.map(s => {
                    const left = data?.caseClosed ? 'access ended' : shareDaysLeft(s.expiresAt);
                    return (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                        <strong>{s.displayName || s.subId}</strong>
                        {left && <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> · {left}</span>}
                      </span>
                      <button className="btn ghost sm" disabled={busy} onClick={() => revoke(s.id)} style={{ fontSize: 12 }}>Remove</button>
                    </div>
                  );})}
                </div>
              )}
              {data?.caseClosed ? (
                <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>This case is closed. Shared access has ended.</div>
              ) : (data?.shareableStaff || []).length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <PlainDropdown value={pick} onChange={setPick} placeholder="Choose a staff member…"
                    options={data.shareableStaff.map(s => ({ value: s.subId, label: `${s.displayName}${s.jobTitle ? ` · ${s.jobTitle}` : ''}` }))} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <PlainDropdown value={days} onChange={setDays} options={SHARE_DAYS_OPTS} style={{ flex: 1 }} />
                    <button className="btn primary sm" disabled={!pick || busy} onClick={share}>Share</button>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                  <div>{data?.clinicHasMailbox
                    ? 'No more registered staff to share with.'
                    : 'No staff is registered at this clinic yet.'}</div>
                  <Link to="/knowledge/case-sharing" style={{ display: 'inline-block', marginTop: 4, color: 'var(--brand-ink)', fontWeight: 600 }}>Read the guide</Link>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryPanel({ caseId, refreshKey, role, canSeeCosts }) {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const resolveName = useRecipientNames();

  useEffect(() => {
    if (!caseId) return;
    setLoading(true);
    getCaseEvents(caseId)
      .then(data => setEvents(groupCommentAttachments(filterEventsForRole(Array.isArray(data) ? data : [], role, canSeeCosts))))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [caseId, refreshKey, role, canSeeCosts]);

  if (loading) return (
    <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
      Loading history…
    </div>
  );

  if (!events.length) return (
    <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
      No events recorded yet.
    </div>
  );

  const groups = groupByDay(events);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '8px 0 40px' }}>
      {groups.map(({ day, items }, gi) => (
        <div key={day}>
          {/* Day header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            margin: gi === 0 ? '0 0 28px' : '36px 0 28px',
          }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '.08em',
              textTransform: 'uppercase', color: 'var(--text-3)',
              whiteSpace: 'nowrap',
            }}>{day}</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          {/* Events in this day */}
          {items.map((ev, i) => {
            const meta   = EVENT_META[ev.eventType] || DEFAULT_META;
            const isLast = gi === groups.length - 1 && i === items.length - 1;
            const meta_js = ev.metadata;

            return (
              <div key={ev.id} style={{ display: 'flex', gap: 0, marginBottom: isLast ? 0 : 4 }}>
                {/* Left rail: dot + line */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 44, flexShrink: 0 }}>
                  {/* Dot */}
                  <div style={{
                    width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                    background: meta.color,
                    boxShadow: `0 0 0 4px ${meta.color}22`,
                    zIndex: 1, marginTop: 16,
                  }} />
                  {/* Connector line */}
                  {!isLast && (
                    <div style={{
                      flex: 1, width: 2,
                      background: 'linear-gradient(to bottom, var(--border), var(--border))',
                      marginTop: 6, minHeight: 24,
                    }} />
                  )}
                </div>

                {/* Right: event card — minWidth 0 so the card shrinks with the viewport
                    instead of letting wide content push it out of bounds */}
                <div style={{ flex: 1, minWidth: 0, paddingLeft: 16, paddingBottom: isLast ? 0 : 20 }}>
                  <div style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    padding: '12px 16px',
                  }}>
                    {/* Top row: icon + label + time */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                        {/* Coloured icon */}
                        <div style={{
                          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                          background: `${meta.color}16`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {(() => {
                            const Glyph = PHOSPHOR_GLYPHS[ev.eventType];
                            return Glyph ? (
                              <Glyph size={15} weight="regular" color={meta.color} />
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke={meta.color} strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                                <path d={meta.icon} />
                              </svg>
                            );
                          })()}
                        </div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span>{ev.label}</span>
                          {ev.eventType?.startsWith('cost_') && meta_js?.amount != null && meta_js?.currency && (
                            <CostChip amount={meta_js.amount} currency={meta_js.currency} size="md" />
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0, paddingTop: 2 }}>
                        {ev.createdAt ? new Date(ev.createdAt).toLocaleString('en-GB', {
                          hour: '2-digit', minute: '2-digit',
                        }) : ''}
                      </div>
                    </div>

                    {/* Actor */}
                    {ev.actorName && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 5, marginLeft: 36, overflowWrap: 'anywhere' }}>
                        by {ev.actorName}{ev.actorEmail ? ` · ${ev.actorEmail}` : ''}
                      </div>
                    )}

                    {/* Who a notification reached — expand on click (standalone
                        rule/digest sends; manual comment tags fold in below) */}
                    {ev.eventType === 'notification_sent' && meta_js?.recipients?.length > 0 && (
                      <NotificationRecipients recipients={meta_js.recipients} />
                    )}

                    {/* Notification(s) tagged to this comment, folded in */}
                    {ev.eventType === 'comment_added' && ev._notifications?.length > 0 && (
                      <CommentNotifyLine notifications={ev._notifications} resolveName={resolveName} indent={36} />
                    )}

                    {/* Field diff */}
                    {ev.fieldName && (ev.oldValue !== null || ev.newValue !== null) && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                        marginTop: 10, marginLeft: 36,
                        padding: '7px 12px', borderRadius: 6,
                        background: 'var(--surface)', border: '1px solid var(--border)',
                        fontSize: 12,
                      }}>
                        <span title={ev.oldValue || undefined} style={{ color: 'var(--text-3)', textDecoration: 'line-through', maxWidth: 'min(160px, 100%)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ev.oldValue || '—'}
                        </span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round"
                          style={{ width: 12, height: 12, flexShrink: 0, color: 'var(--text-3)' }}>
                          <line x1="5" y1="12" x2="19" y2="12"/>
                          <polyline points="12 5 19 12 12 19"/>
                        </svg>
                        <span title={ev.newValue || undefined} style={{ color: meta.color, fontWeight: 600, maxWidth: 'min(160px, 100%)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ev.newValue || '—'}
                        </span>
                        <span style={{ fontSize: 10, color: 'var(--text-3)', marginLeft: 4, opacity: 0.6 }}>
                          {ev.fieldName}
                        </span>
                      </div>
                    )}

                    {/* Waiver reason / automatic-withdrawal note */}
                    {waiverEventDetail(ev) && (
                      <div style={{
                        marginTop: 10, marginLeft: 36,
                        padding: '7px 12px', borderRadius: 6,
                        background: 'var(--surface)', border: '1px solid var(--border)',
                        borderLeft: `3px solid ${meta.color}60`,
                        fontSize: 12, color: 'var(--text-2)', fontStyle: 'italic',
                        lineHeight: 1.5, overflowWrap: 'anywhere',
                      }}>
                        {waiverEventDetail(ev)}
                      </div>
                    )}

                    {/* Comment snippet */}
                    {meta_js?.snippet && (
                      <div style={{
                        marginTop: 10, marginLeft: 36,
                        padding: '7px 12px', borderRadius: 6,
                        background: 'var(--surface)', border: '1px solid var(--border)',
                        borderLeft: `3px solid ${meta.color}60`,
                        fontSize: 12, color: 'var(--text-2)', fontStyle: 'italic',
                        lineHeight: 1.5,
                        overflowWrap: 'anywhere',
                      }}>
                        "{meta_js.snippet}"
                      </div>
                    )}

                    {/* Grouped comment + attachment files */}
                    {ev._files && ev._files.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, marginLeft: 36 }}>
                        {ev._files.map((f, fi) => (
                          <div key={fi} title={f.fileName} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '3px 10px', borderRadius: 20,
                            background: 'var(--surface)', border: '1px solid var(--border)',
                            fontSize: 11, color: 'var(--text-2)',
                            maxWidth: '100%', overflow: 'hidden',
                          }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                              strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11, flexShrink: 0 }}>
                              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                            </svg>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{f.fileName}</span>
                            {f.sizeBytes ? (
                              <span style={{ opacity: 0.5 }}>
                                · {f.sizeBytes < 1024 * 1024
                                    ? `${(f.sizeBytes / 1024).toFixed(0)} KB`
                                    : `${(f.sizeBytes / (1024 * 1024)).toFixed(1)} MB`}
                              </span>
                            ) : null}
                            {attachmentMetadataLine(f.metadata, { formatDate }) && (
                              <span style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>· {attachmentMetadataLine(f.metadata, { formatDate })}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Standalone attachment (not from a comment) */}
                    {meta_js?.fileName && !meta_js?.snippet && (
                      <div title={meta_js.fileName} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        marginTop: 10, marginLeft: 36,
                        padding: '4px 10px', borderRadius: 20,
                        background: 'var(--surface)', border: '1px solid var(--border)',
                        fontSize: 11, color: 'var(--text-2)',
                        maxWidth: 'calc(100% - 36px)', overflow: 'hidden',
                      }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11, flexShrink: 0 }}>
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                        </svg>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{meta_js.fileName}</span>
                        {meta_js.sizeBytes ? (
                          <span style={{ opacity: 0.5 }}>
                            · {meta_js.sizeBytes < 1024 * 1024
                                ? `${(meta_js.sizeBytes / 1024).toFixed(0)} KB`
                                : `${(meta_js.sizeBytes / (1024 * 1024)).toFixed(1)} MB`}
                          </span>
                        ) : null}
                        {attachmentMetadataLine(meta_js.metadata, { formatDate }) && (
                          <span style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>· {attachmentMetadataLine(meta_js.metadata, { formatDate })}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Count footer */}
      <div style={{ textAlign: 'center', marginTop: 32, fontSize: 12, color: 'var(--text-3)' }}>
        {events.length} event{events.length !== 1 ? 's' : ''} total
      </div>
    </div>
  );
}

// ── Mark resolved modal ───────────────────────────────────────────────────────

function ResolveCaseModal({ onClose, onSubmit, saving, error, resolution, setResolution }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, onClose]);

  const canSubmit = resolution.trim().length > 0;

  return (
    <div
      onClick={saving ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '60px 24px',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          width: '100%', maxWidth: 560,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px 16px',
          borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Mark case resolved</div>
          <button onClick={onClose} disabled={saving} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-3)', fontSize: 20, lineHeight: 1, padding: '0 4px',
          }}>×</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-2)', lineHeight: 1.55 }}>
            Describe how this case was resolved. The case will be closed and this text is stored on the record.
          </p>
          <div className="field">
            <label htmlFor="case-resolution">Case resolution <span style={{ color: 'var(--danger)' }}>*</span></label>
            <CharCountTextarea
              fieldKey="caseResolution"
              id="case-resolution"
              style={{ minHeight: 160 }}
              placeholder="Outcome, actions taken, follow-up agreed with the client…"
              value={resolution}
              onChange={e => setResolution(e.target.value)}
              disabled={saving}
              autoFocus
            />
            <div className="field-hint">Required · visible on the closed case · logged in history</div>
          </div>
          {error && (
            <div className="callout danger" style={{ marginTop: 16 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div>{error}</div>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '14px 24px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface-2)',
          borderRadius: '0 0 12px 12px',
        }}>
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn primary"
            onClick={() => canSubmit && !saving && onSubmit()}
            disabled={!canSubmit || saving}
            style={{ minWidth: 120, justifyContent: 'center' }}
          >
            {saving ? 'Closing…' : 'Close case'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reusable edit-panel modal shell ──────────────────────────────────────────

function EditModal({ title, onClose, onSave, saving, error, children }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, onClose]);

  return (
    <div
      onClick={saving ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '60px 24px',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          width: '100%', maxWidth: 560,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px 16px',
          borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
          <button onClick={onClose} disabled={saving} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-3)', fontSize: 20, lineHeight: 1, padding: '0 4px',
          }}>×</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {children}
          {error && (
            <div className="callout danger" style={{ marginTop: 16 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div>{error}</div>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '14px 24px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface-2)',
          borderRadius: '0 0 12px 12px',
        }}>
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn primary" onClick={onSave} disabled={saving} style={{ minWidth: 120 }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Small pencil-button used in panel headers
function EditBtn({ onClick, label = 'Edit' }) {
  return (
    <button
      className="btn ghost sm"
      onClick={onClick}
      title={label}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
      {label}
    </button>
  );
}

// Small chevron that marks a header badge as a dropdown trigger.
function BadgeChevron({ className = 'badge-ctl-chev' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

// ── Response-SLA chip (header clocks row) ─────────────────────────────────────
// Renders ONLY for Treatment Code 4/5 cases while config.slaEnabled (the caller
// gates). Text comes from the pure lib/sla.js builder; the tooltip is the leg
// history; Supervisors (canManageSla) get Lift hold / Suppress / Resume.

// A hold says "On hold" in words already — the icon carries the OTHER half of
// the message: reminder emails are not going out (crossed bell). Suppressed is
// the SLA switched off altogether, so it gets its own glyph.
const SLA_STATE_ICON = {
  awaiting:    TimerIcon,
  hold_ack:    BellSlashIcon,
  hold_client: BellSlashIcon,
  suppressed:  ProhibitIcon,
  snoozed:     MoonIcon,
  breached:    WarningIcon,
};

function SlaChip({ caseId, sla, canManage, onChanged }) {
  const [, setTick]           = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason]   = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const ref = useRef(null);

  // Re-render every 30s so the "left" countdown moves between refreshes.
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) { setMenuOpen(false); setReasonOpen(false); } }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const chip = buildSlaChip(sla, { formatDateTime });
  if (!chip) return null;
  const Icon = SLA_STATE_ICON[chip.state] || TimerIcon;
  const history = buildSlaHistoryLines(sla, { formatDateTime });
  const actions = slaActionsFor(sla.open);

  async function run(action, r = null) {
    setBusy(true); setError(null);
    try {
      await slaAction(caseId, action, r);
      setMenuOpen(false); setReasonOpen(false); setReason('');
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sla-cell" ref={ref} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {/* One pill: the clock text, then — for those who can manage the
            clock — a hard divider and a bell + chevron that opens the menu. */}
        <span className={`sla-chip ${chip.tone}`} title={history.join('\n') || undefined}>
          <span className="sla-chip-main">
            <Icon size={13} weight="bold" />
            <span>{chip.text}</span>
          </span>
        {canManage && actions.length > 0 && (
          <span className="sla-manage">
            <button
              type="button"
              className="sla-manage-btn"
              onClick={() => { setMenuOpen(o => !o); setReasonOpen(false); }}
              disabled={busy}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Manage the response clock"
              title="Manage the response clock"
            >
              <BellIcon size={13} weight="bold" />
              <BadgeChevron className="chev" />
            </button>
            {menuOpen && (
              <div className="sla-menu dd-shell" role="menu">
                {actions.includes('lift') && (
                  <button type="button" className="item dd-item" onClick={() => run('lift')} disabled={busy}>
                    <PlayCircleIcon size={14} /> Lift hold — restart the timer
                  </button>
                )}
                {actions.includes('resume') && (
                  <button type="button" className="item dd-item" onClick={() => run('resume')} disabled={busy}>
                    <PlayCircleIcon size={14} /> Resume the response SLA
                  </button>
                )}
                {actions.includes('suppress') && !reasonOpen && (
                  <button type="button" className="item dd-item" onClick={() => setReasonOpen(true)} disabled={busy}>
                    <BellSlashIcon size={14} /> Suppress the SLA on this case…
                  </button>
                )}
                {reasonOpen && (
                  <div style={{ padding: '4px 6px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 11, color: 'var(--text-3)' }}>Reason (shown in History)</label>
                    <textarea className="input" rows={2} value={reason} onChange={e => setReason(e.target.value)} maxLength={500}
                      placeholder="Why the timers are being switched off for this case" style={{ fontSize: 12, resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button type="button" className="btn ghost sm" onClick={() => setReasonOpen(false)} disabled={busy}>Cancel</button>
                      <button type="button" className="btn primary sm" onClick={() => run('suppress', reason.trim() || null)} disabled={busy}>
                        {busy ? 'Saving…' : 'Suppress'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </span>
        )}
        </span>
        {error && <span className="field-error" style={{ marginTop: 0 }}>{error}</span>}
      </div>
    </div>
  );
}

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { config } = useAppConfig();

  // Navigation context passed from CaseList (sorted IDs in list order, newest first)
  const navIds   = location.state?.ids   ?? null;
  const navIndex = location.state?.index ?? null;

  const hasList      = navIds !== null && navIndex !== null;
  const prevDisabled = hasList ? navIndex <= 0 : false;
  const nextDisabled = hasList ? navIndex >= navIds.length - 1 : false;

  function navToCase(targetId, newIndex) {
    navigate(`/cases/${targetId}`, hasList ? { state: { ids: navIds, index: newIndex } } : undefined);
  }
  function goPrev() {
    if (prevDisabled) return;
    navToCase(hasList ? navIds[navIndex - 1] : String(Number(id) + 1), hasList ? navIndex - 1 : null);
  }
  function goNext() {
    if (nextDisabled) return;
    navToCase(hasList ? navIds[navIndex + 1] : String(Number(id) - 1), hasList ? navIndex + 1 : null);
  }

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const [historyKey, setHistoryKey] = useState(0);

  // ── Inline editing ─────────────────────────────────────────────────────────
  const [editPanel,     setEditPanel]     = useState(null);  // null | 'status' | 'incident' | 'staff' | 'info' | 'notes'
  const [attEditMode,      setAttEditMode]      = useState(false);
  const [attSelected,      setAttSelected]      = useState(new Set());
  const [attDeleting,      setAttDeleting]      = useState(false);
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  const [editDraft,     setEditDraft]     = useState({});
  const [statusOpen,    setStatusOpen]    = useState(false);
  const [severityOpen,  setSeverityOpen]  = useState(false);
  const statusRef   = useRef(null);
  const severityRef = useRef(null);

  // Close the status / severity dropdowns on an outside click.
  useEffect(() => {
    if (!statusOpen && !severityOpen) return;
    function onDown(e) {
      if (statusOpen   && statusRef.current   && !statusRef.current.contains(e.target))   setStatusOpen(false);
      if (severityOpen && severityRef.current && !severityRef.current.contains(e.target)) setSeverityOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [statusOpen, severityOpen]);

  // Clinic directory (TherapieMaps) — backs the Clinic / Region / Country
  // dropdowns in the Case-information modal. Fetched the first time that modal
  // opens; blobClient caches it process-wide, so it's normally already warm
  // from the auth bootstrap.
  const [clinicDir,      setClinicDir]      = useState(null);
  const [clinicDirError, setClinicDirError] = useState(null);

  useEffect(() => {
    if (editPanel !== 'info' || clinicDir || clinicDirError) return;
    let cancelled = false;
    getTherapieMapsData()
      .then(data => { if (!cancelled) setClinicDir(data.clinics || []); })
      .catch(e   => { if (!cancelled) setClinicDirError(e.message); });
    return () => { cancelled = true; };
  }, [editPanel, clinicDir, clinicDirError]);

  const [pendingAction, setPendingAction] = useState(null);  // { updates, label, danger }
  const [resolveOpen,   setResolveOpen]   = useState(false);
  const [resolveText,   setResolveText]   = useState('');
  const [saving,        setSaving]        = useState(false);
  const [saveError,     setSaveError]     = useState(null);

  function openEdit(panel, draft) {
    setEditPanel(panel);
    setEditDraft(draft);
    setSaveError(null);
  }

  function requestSave(updates, label, danger = false) {
    setPendingAction({ updates, label, danger });
  }

  async function executeSave() {
    const { updates } = pendingAction;
    setSaving(true);
    setSaveError(null);
    try {
      if (updates._attDelete) {
        // Delete selected attachments sequentially
        setAttDeleting(true);
        for (const attId of updates._attDelete) {
          await deleteAttachment(attId);
        }
        setAttDeleting(false);
        setAttEditMode(false);
        setAttSelected(new Set());
        setCommentsRefreshKey(k => k + 1);  // re-fetch comments so deleted attachment pills vanish
      } else if (updates._caseDelete) {
        await deleteCase(id);
        setPendingAction(null);
        navigate('/cases');                  // case is gone for this user — leave the page
        return;
      } else if (updates._caseRestore) {
        await restoreCase(id);
      } else {
        await patchCase(id, updates);
      }
      const fresh = await getCase(id);
      setCaseData(fresh);
      setHistoryKey(k => k + 1);
      setEditPanel(null);
      setStatusOpen(false);
      setSeverityOpen(false);
      setPendingAction(null);
    } catch (e) {
      setSaveError(e.message);
      setAttDeleting(false);
      setPendingAction(null);
    } finally {
      setSaving(false);
    }
  }

  function cancelAction() { setPendingAction(null); }

  // Re-fetch the case after a tag add/remove so pills + history stay in sync,
  // without a full page reload.
  async function refreshCase() {
    const fresh = await getCase(id);
    setCaseData(fresh);
    setHistoryKey(k => k + 1);
  }

  function openResolveModal() {
    setResolveText('');
    setSaveError(null);
    setResolveOpen(true);
  }

  async function submitResolve() {
    const text = resolveText.trim();
    if (!text) {
      setSaveError('Case resolution is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await patchCase(id, { status: 'Closed', caseResolution: text });
      const fresh = await getCase(id);
      setCaseData(fresh);
      setHistoryKey(k => k + 1);
      setResolveOpen(false);
      setResolveText('');
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // Refresh after a comment / attachment / requirement action. Merge the WHOLE
  // fresh case (not just attachments) so requirement adds, sign-off completions,
  // and removals — which live in type_specific — reflect immediately. Every
  // action button routes here, so the consequence is always shown at once.
  const refreshAfterActivity = useCallback(async () => {
    setHistoryKey(k => k + 1);
    try {
      const fresh = await getCase(id);
      setCaseData(prev => prev ? { ...prev, ...fresh } : fresh);
    } catch { /* non-fatal */ }
  }, [id]);

  // ── Code 4/5 response SLA (sla_escalation_logic.md §7) ───────────────────
  // Only Treatment Code 4/5 cases carry a chip, and only while the feature is
  // on. Re-fetched whenever the history refreshes (comments, uploads, status,
  // acknowledgements all move the state machine).
  const slaEligible = slaApplies(caseData, config);
  // Acknowledging a message is a clinic-side action (ClinicManager / clinic
  // mailbox people); HQ roles only see who acknowledged. Same role lists the
  // response clock uses (governed slaSides), so the two never disagree.
  const clinicSideRoles = (config?.slaSides?.clinic || ['ClinicManager', 'ClinicMailbox']).map(r => String(r).toLowerCase());
  const canAcknowledge = !!user && !user.isSuperAdmin && clinicSideRoles.includes(String(user.role || '').toLowerCase());
  const [sla, setSla] = useState(null);
  useEffect(() => {
    if (!slaEligible || !id) { setSla(null); return; }
    let cancelled = false;
    getCaseSla(id)
      .then(d => { if (!cancelled) setSla(d); })
      .catch(() => { if (!cancelled) setSla(null); });
    return () => { cancelled = true; };
  }, [id, slaEligible, historyKey, commentsRefreshKey]);

  // SharePoint item URL for attachments
  const sharepointItemUrl =
    `https://therapieclinic.sharepoint.com/sites/your-site/_layouts/15/listform.aspx` +
    `?PageType=4&ListId=a94677af-fb22-424c-b20d-330f83056089&ID=${id}`;

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const data = await getCase(id);
        setCaseData(data);
      } catch (err) {
        setError(err.message || 'Failed to load case');
      } finally {
        setLoading(false);
      }
    }
    if (id) load();
  }, [id]);

  // HSRC directory staff for the Medical Team Member picker (with emails, so the
  // chosen person can be notified). Fails soft to [] → field stays free-text.
  const [hsrcStaff, setHsrcStaff] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getReferenceStaff().then(r => { if (!cancelled) setHsrcStaff(r?.staff || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Resolution fallback: cases closed before the case_resolution column was
  // populated still have the text in their history (a 'case_resolved' event).
  // When the column is empty, surface that so the resolution always shows.
  const [historyResolution, setHistoryResolution] = useState(null);
  useEffect(() => {
    setHistoryResolution(null);
    if (!caseData || caseData.status !== 'Closed' || caseData.caseResolution) return;
    let cancelled = false;
    getCaseEvents(id)
      .then(events => {
        if (cancelled) return;
        const ev = (events || []).find(e => e.eventType === 'case_resolved' && e.newValue);
        if (ev) setHistoryResolution(ev.newValue);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id, caseData]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
        Loading case…
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <>
        <header className="page-head">
          <div>
            <div className="breadcrumb">
              <a href="#" onClick={e => { e.preventDefault(); navigate('/dashboard'); }}>Overview</a>
              <span>›</span>
              <a href="#" onClick={e => { e.preventDefault(); navigate('/cases'); }}>All Cases</a>
              <span>›</span>
              <span>Error</span>
            </div>
          </div>
          <div className="head-actions">
            <button className="btn" onClick={() => navigate('/dashboard')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Back to All Cases
            </button>
          </div>
        </header>
        <div className="callout danger">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div><strong>Could not load case #{id}</strong> — {error}</div>
        </div>
      </>
    );
  }

  if (!caseData) return null;

  const c = caseData;
  const raw = c._raw || {};
  const canResolve           = !!user?.canResolveCase;
  const canReopen            = !!user?.canReopenCase;
  const canDelete            = !!user?.canDeleteCase;
  const canEdit              = !!user?.canEditCase;
  const canChangeStatusCode  = !!user?.canChangeStatusAndCode;
  // Private content gates on the capability (custom roles included), not the
  // CM role string — the server strips private items anyway; this just keeps
  // the UI consistent with what actually comes back.
  const canSeePrivate   = !!user?.canSeePrivateComments;

  // Log raw SP fields to the browser console so we can identify the correct
  // internal field names for treatment, date of incident, etc.

  // Helper: try multiple possible SP internal field names and return first truthy value
  function pick(...keys) {
    for (const k of keys) {
      if (raw[k] !== undefined && raw[k] !== null && raw[k] !== '') return raw[k];
    }
    return '';
  }

  const f = {
    caseRef:     c.caseRef || `#${c.id}`,
    clinic:      c.clinic  || pick('Clinic', 'ClinicName', 'Clinic0', 'field_3'),
    region:      c.region  || pick('Region', 'RegionName', 'RegionName0'),
    // Confirmed SP internal name: "Department" (not "Treatment")
    treatment:   c.treatment || pick('Department', 'Treatment', 'TreatmentType', 'Dept'),
    severity:    c.severity  || pick('Severity', 'AWICode', 'SeverityCode'),
    status:      c.status,
    caseResolution: c.caseResolution || null,
    closedAt:      c.closedAt || null,
    reportedDate:  c.reportedDate  || pick('ReportedDate', 'DateReported') || c.createdDate,
    modifiedDate:  c.modifiedDate,
    // Confirmed SP internal name: "DateofIncident" (lowercase 'o')
    dateOfIncident: pick(
      'DateofIncident', 'DateOfIncident', 'Date_x0020_of_x0020_Incident', 'IncidentDate', 'EventDate'
    ) || null,
    // Client review date (Code 3–5 Treatment; editable for every case). A real
    // column (cases.review_date) — 'YYYY-MM-DD' or null.
    reviewDate:    c.reviewDate ?? raw.ReviewDate ?? null,
    staffMember:   pick('StaffMemberConcerned', 'StaffMember', 'Staff'),
    clinicManager: pick('ClinicManagerResponsible', 'ClinicManager', 'Manager'),
    medicalTeam:   pick('MedicalTeamMemberResponsible', 'MedicalTeam', 'MedicalDirector'),
    // Immutable submitter account — separate from the editable notes below.
    incidentDescription: c.incidentDescription || pick('IncidentDescription') || null,
    medicalNotes:  pick('MedicalNotes', 'Notes', 'ClinicalNotes', 'Details', 'Description'),
    clientName:       pick('Title', 'ClientName', 'Client'),
    clientPhone:      pick('ClientPhoneNumber', 'ClientPhone'),
    country:          pick('Country', 'CountryName'),
    serviceCarriedOut:pick('ServiceCarriedOut'),
    eventType:        c.eventType        || pick('EventType') || 'Treatment',
    treatmentSubtype: c.treatmentSubtype || pick('TreatmentSubtype') || null,
    incidentCategory: c.incidentCategory || pick('IncidentCategory') || null,
    incidentCategoryOther: c.incidentCategoryOther || pick('IncidentCategoryOther') || null,
    typeSpecific:     c.typeSpecific     || raw.TypeSpecific || null,
    deletedAt:        c.deletedAt        || null,
    attachments:   c.attachments || [],
    attachmentError:    c.attachmentError    || null,
    hasAttachmentsFlag: c.hasAttachmentsFlag || false,
  };

  // Per-case requirements live in type_specific.requirements (shared accessor,
  // tolerant of null / non-object). Empty array when none.
  const caseRequirements = getCaseRequirements(f.typeSpecific);

  // The Attachments grid is the case's COMPLETE file list — every upload,
  // whatever path added it (wizard, required slot, per-case requirement,
  // comment). Slot/requirement files also render in the Required-docs card;
  // only the viewer's privacy scope filters here.
  const visibleAttachments = (f.attachments || [])
    .filter(att => canSeePrivate || att.isPublic !== false);

  // Event-type-aware derived values for the details panel.
  const incidentCategoryLabel = f.incidentCategory === 'Other' && f.incidentCategoryOther
    ? `Other — ${f.incidentCategoryOther}`
    : f.incidentCategory;
  // Treatment incident-specific ("requirement extra") fields — the matrix set
  // for this (treatment, code) plus any extra stored key, so every field
  // captured at submission is reflected here and gaps stay visible.
  // Derived from the SAME getRequirements + FIELD_META the wizard uses, so
  // parity holds for every treatment/code combination. (Near-Miss / Clinic-
  // Incident fields have their own dedicated rows below and are excluded here.)
  // Matrix-required keys (+ any stored extra) first, then the matrix's optional
  // keys — the optional ones render with an "Optional" tag and never count in
  // the outstanding chip (outstandingIncidentFields ignores them).
  const incidentKeys = f.eventType === 'Treatment'
    ? orderedIncidentKeys(f.treatment, f.severity, f.typeSpecific).all
    : [];
  const incidentOutstanding = f.eventType === 'Treatment'
    ? outstandingIncidentFields(f.treatment, f.severity, f.typeSpecific)
    : 0;
  // For the Edit Incident Details modal, recompute from the treatment selected
  // IN the modal (editDraft.Department) — not the loaded one — so switching
  // treatment shows/saves the right incident fields instead of the old set.
  // Optional keys are included so they can be filled in from the case file.
  const modalTreatment = editDraft.Department || f.treatment;
  const modalIncidentKeys = editPanel === 'incident'
    ? orderedIncidentKeys(modalTreatment, f.severity, editDraft.ts).all
    : incidentKeys;

  const isClosed        = f.status === 'Closed';
  const canChangeStatus = canChangeStatusCode && (!isClosed || canReopen);
  // Resolution: prefer the stored column, fall back to the history event.
  const resolutionText  = f.caseResolution || historyResolution;


  return (
    <>
      {/* ───────── Page head ───────── */}
      <header className="page-head">
        <div>
          <div className="breadcrumb">
            <a href="#" onClick={e => { e.preventDefault(); navigate('/dashboard'); }}>Overview</a>
            <span>›</span>
            <a href="#" onClick={e => { e.preventDefault(); navigate('/cases'); }}>All Cases</a>
            <span>›</span>
            <span>Case detail</span>
          </div>
        </div>
        <div className="head-actions">
          <span className="btn-group" role="group" aria-label="Previous / next case">
            <button className="btn icon" onClick={goPrev} disabled={prevDisabled} title="Previous case" aria-label="Previous case">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            <button className="btn icon" onClick={goNext} disabled={nextDisabled} title="Next case" aria-label="Next case">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </span>
        </div>
      </header>

      {/* ───────── Deleted-case banner (SuperAdmin bin view) ───────── */}
      {c.deletedAt && (
        <div className="callout danger" style={{ marginBottom: 16, alignItems: 'center' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
          <div style={{ flex: 1 }}>
            <strong>This case was deleted</strong>
            {' '}— {formatDate(c.deletedAt)}{c.deletedBy ? ` by ${c.deletedBy}` : ''}.
            Restorable for 90 days after deletion.
          </div>
          {user?.isSuperAdmin && (
            <button
              className="btn"
              style={{ flexShrink: 0 }}
              onClick={() => requestSave({ _caseRestore: true }, `Restore case ${f.caseRef}?`)}
            >
              Restore case
            </button>
          )}
        </div>
      )}

      {/* ───────── Case header card ─────────
          One card, three rows: identity (ref + clinic · region); the badge row
          (event/treatment · severity · status · tags — severity and status ARE
          the change controls when the role allows); and the clocks row (the
          response-SLA chip for open coded cases + the legal-threshold track).
          Dates and people live in the right-column panels — one fact, one home. */}
      <div className="case-header">
        <div className="case-header-top">
          <div style={{ minWidth: 0 }}>
            <div className="case-id-big">{f.caseRef}</div>
            <div className="case-title">
              {f.clinic || f.caseRef}
              {/* A test case is only ever visible to a SuperAdmin, but it looks
                  exactly like a real one once it is open — say so. */}
              {c.isTest && (
                <span className="clinic-test-chip" style={{ marginLeft: 8, verticalAlign: 2 }}
                  title="Filed against a test clinic — excluded from every total, and escalation emails are withheld">
                  Test clinic
                </span>
              )}
              {f.region && <span className="case-title-region">{f.region}</span>}
            </div>
          </div>
          <div className="case-actions">
            <div className="case-actions-row">
            {user?.canShareCaseAccess && !c.deletedAt && <CaseShareButton caseId={id} />}
            {canResolve && !isClosed && (
              <button className="btn" onClick={openResolveModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Mark resolved
              </button>
            )}
            {canDelete && !c.deletedAt && (
              <button
                className="btn danger"
                onClick={() => requestSave(
                  { _caseDelete: true },
                  `Delete case ${f.caseRef}?`,
                  true
                )}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete
              </button>
            )}
            </div>
          </div>
        </div>

        {/* Badge row — ONE line under the three text lines: the two controls
            (severity ▾, status ▾) on the left, the descriptor tags (treatment /
            event type, case tags, "+ Add tag") on the right. The action buttons
            above keep the header-top to themselves. */}
        <div className="case-badges">
          <div className="case-badges-left">
            {/* Severity — only for coded treatments (LHR/AWI); hidden for the flat
                workbook treatments and non-treatment event types. Read-only for CM. */}
            {treatmentHasCodes(f.treatment) && (
              <span className="badge-ctl" ref={severityRef}>
                {!canChangeStatusCode
                  ? (f.severity ? <SeverityBadge value={f.severity} /> : <span className="badge">No code</span>)
                  : <>
                    <button
                      type="button"
                      className="badge-ctl-btn"
                      onClick={() => setSeverityOpen(o => !o)}
                      title="Change severity"
                      aria-haspopup="menu"
                      aria-expanded={severityOpen}
                    >
                      {f.severity ? <SeverityBadge value={f.severity} /> : <span className="badge">No code</span>}
                      <BadgeChevron />
                    </button>
                    {severityOpen && (
                      <div className="badge-menu dd-shell" role="menu">
                        {config.severityCodes
                          .filter(s => s.value !== f.severity && itemAvailableForEventType(s, f.eventType))
                          .map(s => (
                            <button
                              key={s.value}
                              type="button"
                              role="menuitem"
                              className="item dd-item"
                              onClick={() => {
                                setSeverityOpen(false);
                                requestSave({ Severity: s.value }, `Change severity to ${s.value}?`);
                              }}
                            >
                              <SeverityBadge value={s.value} />
                            </button>
                          ))}
                      </div>
                    )}
                  </>}
              </span>
            )}

            {/* Status — read-only for CM; Closed is only reached via Mark resolved. */}
            <span className="badge-ctl" ref={statusRef}>
              {!canChangeStatus ? <StatusBadge status={f.status} /> : <>
                <button
                  type="button"
                  className="badge-ctl-btn"
                  onClick={() => setStatusOpen(o => !o)}
                  title="Change status"
                  aria-haspopup="menu"
                  aria-expanded={statusOpen}
                >
                  <StatusBadge status={f.status} />
                  <BadgeChevron />
                </button>
                {statusOpen && (
                  <div className="badge-menu dd-shell" role="menu">
                    {config.statusValues
                      // 'New' is an initial-only state — a case leaves it on first
                      // activity (auto-Pending) and never returns; never offer it as a
                      // manual target. 'Closed' has its own Mark-resolved / reopen path.
                      .filter(s => s.value !== f.status && s.value !== 'Closed' && s.value !== 'New'
                        && itemAvailableForEventType(s, f.eventType)
                        && itemAvailableForTreatmentType(s, f.treatment, config.treatmentTypes))
                      .map(s => (
                        <button
                          key={s.value}
                          type="button"
                          role="menuitem"
                          className="item dd-item"
                          onClick={() => {
                            setStatusOpen(false);
                            requestSave(
                              { status: s.value },
                              isClosed
                                ? `Reopen case — change status from Closed to ${s.value}?`
                                : `Change status from ${f.status} to ${s.value}?`,
                              isClosed
                            );
                          }}
                        >
                          <StatusBadge status={s.value} />
                        </button>
                      ))}
                  </div>
                )}
              </>}
            </span>
          </div>
          {/* Descriptor zone — right end of the badge row: the treatment /
              event-type badge, then the tag pills and "+ Add tag".
              System tags render locked; assignment gated by canAssignTags,
              create-if-new by canCreateTags. */}
          <div className="case-tags-zone">
            {f.eventType === 'NearMiss'
              ? <span className="badge event-type">Near miss</span>
              : f.eventType === 'ClinicIncident'
              ? <span className="badge event-type">Clinic incident</span>
              : <TreatmentBadge value={f.treatment} />}
            <CaseTags
              caseId={id}
              tags={c.tags || []}
              canWrite={canEdit && !c.deletedAt && !!user?.canAssignTags}
              canCreate={canEdit && !c.deletedAt && !!user?.canCreateTags}
              onChange={refreshCase}
            />
          </div>
        </div>

        {isClosed && !canReopen && (
          <p className="muted case-closed-note">
            This case is closed. Only Admin roles can reopen it — add a comment if you need it reopened.
          </p>
        )}

        {/* Clocks: response SLA (Treatment with a severity code, while slaEnabled and open)
            leads the row; the legal-threshold track runs under it. */}
        <CaseLegalTimeline
          startDate={f.dateOfIncident || f.reportedDate}
          months={config.legalThresholdMonths}
          caseId={id}
          refreshKey={historyKey}
          closed={isClosed}
          leading={slaEligible && sla?.open ? (
            <SlaChip
              caseId={id}
              sla={sla}
              canManage={!!user?.canManageSla && !f.deletedAt}
              onChanged={() => setHistoryKey(k => k + 1)}
            />
          ) : null}
        />
      </div>

      {/* ───────── Tabs ───────── */}
      <div className="tabs-lg" role="tablist">
        {[
          { key: 'overview', label: 'Overview' },
          // Report tab = printable case report + attachment/ZIP downloads —
          // its own role capability since the reportView split (2026-07).
          ...(user?.canUseCaseReports ? [{ key: 'report', label: 'Report' }] : []),
          ...(user?.canSeeCosts ? [{ key: 'costs', label: 'Costs' }] : []),
          { key: 'history',  label: 'History' },
        ].map(t => (
          <div
            key={t.key}
            role="tab"
            tabIndex={0}
            aria-selected={activeTab === t.key}
            className={`tab-lg${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab(t.key); }
            }}
          >
            {t.label}
          </div>
        ))}
      </div>

      {/* ───────── Overview tab ───────── */}
      {activeTab === 'overview' && (
        <div className="row detail-layout">
          {/* ── LEFT column — the record: what happened → evidence → discussion ── */}
          <div className="detail-col">

            {/* Incident details panel — arranged by event type */}
            <div className="panel">
              <div className="panel-head">
                <div className="panel-title">
                  {f.eventType === 'NearMiss' ? 'Near miss details'
                    : f.eventType === 'ClinicIncident' ? 'Clinic incident details'
                    : 'Incident details'}
                </div>
                {canEdit && (f.eventType === 'Treatment'
                  ? (() => {
                      const subtypeOpts = subtypesForTreatment(
                        config.treatmentTypes, f._raw?.Department || f.treatment,
                        config.incidentCategoriesByTreatmentAndCode, config.severityCodes,
                      );
                      const subtypeForm = treatmentSubtypeToForm(f.treatmentSubtype, subtypeOpts);
                      return (
                        <EditBtn onClick={() => openEdit('incident', {
                          DateofIncident:        f.dateOfIncident || '',
                          Department:            f._raw?.Department || f.treatment || '',
                          TreatmentSubtype:      subtypeForm.select,
                          TreatmentSubtypeOther: subtypeForm.other,
                          IncidentCategory:      f.incidentCategory || '',
                          IncidentCategoryOther: f.incidentCategoryOther || '',
                          ServiceCarriedOut:     f.serviceCarriedOut || '',
                          ClientPhoneNumber:     f.clientPhone || '',
                          Title:                 f.clientName || '',
                          ts:                    { ...(f.typeSpecific || {}) },
                        })} />
                      );
                    })()
                  : <EditBtn onClick={() => openEdit('ntDetails', {
                      DateofIncident:   f.dateOfIncident || '',
                      Title:            f.clientName || '',
                      IncidentCategory: f.incidentCategory || '',
                      ts:               { ...(f.typeSpecific || {}) },
                    })} />
                )}
              </div>
              {/* Two columns when a treatment carries incident-specific fields:
                  general facts left, the specific ones right — the panel is wide
                  and a single list left its right half empty. */}
              <div className={`panel-body${f.eventType === 'Treatment' && incidentKeys.length > 0 ? ' incident-details-cols' : ''}`}>
                <div className="info-list">
                  <InfoRow label="Date of Incident" value={f.dateOfIncident ? formatDate(f.dateOfIncident) : null} />
                  {isRefusalRegisterCase({ eventType: f.eventType, incidentCategory: f.incidentCategory }) ? (
                    <>
                      <InfoRow label="Incident Title"     value={f.clientName} />
                      <InfoRow label="Reporting Function" value={f.typeSpecific?.reportingFunction} />
                      <InfoRow label="Category"           value={incidentCategoryLabel} />
                      <InfoRow label="Date and Time of Request" value={formatRefusalDateTime(f.typeSpecific?.[REFUSAL_TS_KEYS.requestedAt], formatDate)} />
                      <InfoRow label="Details of the Incident" value={f.incidentDescription} />
                      <InfoRow label="Description of the Potential Buyer" value={f.typeSpecific?.[REFUSAL_TS_KEYS.buyerDescription]} />
                      <InfoRow label="Action Taken"       value={f.typeSpecific?.[REFUSAL_TS_KEYS.actionTaken]} />
                      <InfoRow label="Staff Member"       value={f.staffMember} />
                    </>
                  ) : f.eventType === 'NearMiss' ? (
                    <>
                      <InfoRow label="Incident Title"     value={f.clientName} />
                      <InfoRow label="Reporting Function" value={f.typeSpecific?.reportingFunction} />
                      <InfoRow label="Category"           value={incidentCategoryLabel} />
                      <InfoRow label="Cause"              value={f.typeSpecific?.cause} />
                      <InfoRow label="Corrective Action"  value={f.typeSpecific?.correctiveAction} />
                      <InfoRow label="Assigned To"        value={f.typeSpecific?.assignee} />
                      <InfoRow label="Target Resolution"  value={f.typeSpecific?.targetResolutionDate ? formatDate(f.typeSpecific.targetResolutionDate) : null} />
                      <InfoRow label="Potential Severity" value={f.typeSpecific?.potentialSeverity} />
                    </>
                  ) : f.eventType === 'ClinicIncident' ? (
                    <>
                      <InfoRow label="Incident Title"        value={f.clientName} />
                      <InfoRow label="Type of Incident"      value={incidentCategoryLabel} />
                      <InfoRow label="Injured Person"        value={clinicPersonLabel(f.typeSpecific?.personStatus)} />
                      <InfoRow label="Injured Person Name"   value={f.typeSpecific?.injuredName} />
                      <InfoRow label="Injured Person Contact" value={f.typeSpecific?.injuredContact} />
                      <InfoRow label="Nature of Injuries"    value={f.typeSpecific?.injuryNature} />
                      <InfoRow label="Immediate Response"    value={f.typeSpecific?.immediateResponse} />
                      <InfoRow label="Witness Name"          value={f.typeSpecific?.witnessName} />
                      <InfoRow label="Witness Contact"       value={f.typeSpecific?.witnessContact} />
                      <InfoRow label="Witness Statement"     value={f.typeSpecific?.witnessStatement} />
                    </>
                  ) : (
                    <>
                      <InfoRow label="Treatment / Dept"    value={f.treatment} />
                      {f.treatmentSubtype && <InfoRow label="Subtype" value={f.treatmentSubtype} />}
                      {f.incidentCategory && <InfoRow label="Incident Category" value={incidentCategoryLabel} />}
                      <InfoRow label="Service Carried Out" value={f.serviceCarriedOut} />
                      <InfoRow label="Client Name"         value={f.clientName} />
                      {f.clientPhone && <InfoRow label="Client phone" value={f.clientPhone} />}
                    </>
                  )}
                </div>

                {/* Incident-specific details — the treatment/code requirement
                    fields (laser serial no., skin type, …). Empty ones show
                    "Not provided" so outstanding items stay visible. */}
                {f.eventType === 'Treatment' && incidentKeys.length > 0 && (
                  <div className="incident-extras">
                    <div className="incident-extras-head">
                      <span className="incident-extras-title">Incident-specific details</span>
                      {incidentOutstanding > 0 && (
                        <span className="incident-extras-count">{incidentOutstanding} outstanding</span>
                      )}
                    </div>
                    <div className="info-list">
                      {incidentKeys.map(k => {
                        const optional = isOptionalIncidentField(f.treatment, f.severity, k);
                        return (
                          <InfoRow key={k}
                            label={incidentFieldLabel(k)}
                            value={formatIncidentFieldValue(k, f.typeSpecific?.[k], { formatDate })}
                            emptyText={optional ? '—' : 'Not provided'} />
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* Case resolution card — shown between Incident details and Staff once closed */}
            {isClosed && (resolutionText || f.closedAt) && (
              <div className="panel resolution-card">
                <div className="panel-head">
                  <div className="panel-title">Case resolution</div>
                  {f.closedAt && (
                    <span className="resolution-closed-on">Closed on {formatDate(f.closedAt)}</span>
                  )}
                </div>
                <div className={`panel-body${resolutionText ? '' : ' compact'}`}>
                  {resolutionText
                    ? <div className="resolution-text">{resolutionText}</div>
                    : <span className="muted">No resolution narrative recorded.</span>}
                </div>
              </div>
            )}

            {/* Incident description — the submitter's original account of what
                happened. IMMUTABLE: no edit controls here and patchCase rejects
                any attempt to change it, so the report can't be tampered with.
                Only rendered when present (cases created after the split; older
                cases still carry their description in the Notes panel below). */}
            {f.incidentDescription && (
              <div className="panel">
                <div className="panel-head">
                  <div className="panel-title">Incident description</div>
                  <span className="badge" title="Submitted report — permanent record, cannot be edited"
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" style={{ width: 10, height: 10 }}>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    Read-only
                  </span>
                </div>
                <div className="panel-body">
                  <p style={{ margin: 0, fontSize: '13.5px', lineHeight: '1.75', whiteSpace: 'pre-line', color: 'var(--text)' }}>
                    {f.incidentDescription}
                  </p>
                </div>
              </div>
            )}

            {/* Notes — always visible, + to add/edit. Label is "Notes" for
                Near-Miss / Clinic-Incident, "Medical notes" otherwise (field
                key unchanged). Editable only by admin/supervisors (canEdit =
                canEditCase). Holds admin-added clinical notes — the submitted
                account lives in the immutable card above. */}
            <div className="panel">
              <div className="panel-head">
                <div className="panel-title">{notesLabel(f.eventType)}</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {canEdit && f.medicalNotes && (
                    <button
                      className="btn ghost sm"
                      onClick={() => openEdit('notes-append', { newNote: '' })}
                      title="Add a new dated note"
                                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      Add
                    </button>
                  )}
                  {canEdit && <button
                    className="btn ghost sm"
                    onClick={() => openEdit('notes', { medicalNotes: f.medicalNotes || '' })}
                    title={f.medicalNotes ? `Edit ${notesLabel(f.eventType).toLowerCase()}` : `Add ${notesLabel(f.eventType).toLowerCase()}`}
                                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
                      {f.medicalNotes
                        ? <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>
                        : <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>
                      }
                    </svg>
                    {f.medicalNotes ? 'Edit' : 'Add'}
                  </button>}
                </div>
              </div>
              <div className={`panel-body${f.medicalNotes ? '' : ' compact'}`}>
                {f.medicalNotes ? (
                  <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '10px' }}>
                      {f.modifiedDate && (
                        <span className="badge">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '10px', height: '10px' }}>
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                          </svg>
                          {formatDate(f.modifiedDate)}
                        </span>
                      )}
                      {f.medicalTeam && (
                        <span className="badge" style={{ fontWeight: 600, color: 'var(--text)' }}>
                          {f.medicalTeam}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: 0, fontSize: '13.5px', lineHeight: '1.75', whiteSpace: 'pre-line', color: 'var(--text)' }}>
                      {f.medicalNotes}
                    </p>
                  </>
                ) : (
                  <p className="muted panel-empty">
                    No {notesLabel(f.eventType).toLowerCase()} yet.
                  </p>
                )}
              </div>
            </div>

            {/* Required documents & sign-offs — ONE surface. Matrix-driven
                required uploads (from treatment, severity) PLUS admin-created
                per-case requirements (document slots + sign-off tasks). Document
                satisfaction is DERIVED from a 'req:<id>' attachment; sign-off
                completion is stored on the case. */}
            <RequiredAttachmentSlots
              caseId={id}
              eventType={f.eventType}
              treatment={f.treatment}
              severity={f.severity}
              attachments={f.attachments || []}
              canEdit={canEdit}
              canRespond={!!user?.canRespondToRequirements && !f.deletedAt}
              requirements={caseRequirements}
              canManageRequirements={!!user?.canManageRequirements && !f.deletedAt}
              canFulfilRequirements={!!user?.canRespondToRequirements && !f.deletedAt}
              typeSpecific={f.typeSpecific}
              canWaive={!!user?.canWaiveRequirements && !f.deletedAt}
              incidentDate={f.dateOfIncident ? String(f.dateOfIncident).slice(0, 10) : null}
              onChange={refreshAfterActivity}
              onBulkDelete={(rawIds) => {
                if (!rawIds?.length) return;
                requestSave(
                  { _attDelete: rawIds },
                  `Delete ${rawIds.length} attachment${rawIds.length > 1 ? 's' : ''}?`,
                  true
                );
              }}
              attDeleting={attDeleting}
            />

            {/* Attachments panel — the case's full file list (required-slot and
                requirement files appear here AND in their Required-docs slot).
                Hidden when empty; still shown on load error so the message
                stays visible. */}
            {(f.attachmentError || visibleAttachments.length > 0) && (
            <div className="panel">
              <div className="panel-head">
                <div>
                  <div className="panel-title">
                    All files
                    <span className="panel-count">{visibleAttachments.length}</span>
                    {attEditMode && attSelected.size > 0 && (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: 'var(--text-3)' }}>
                        {attSelected.size} selected
                      </span>
                    )}
                  </div>
                  <div className="panel-sub">
                    {attEditMode
                      ? 'Tick files to delete, then confirm.'
                      : 'Every file on this case — files in the slots above are listed here too.'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {attEditMode ? (
                    <>
                      {attSelected.size > 0 && (
                        <button
                          className="btn sm danger filled"
                          disabled={attDeleting}
                          onClick={() => requestSave(
                            { _attDelete: [...attSelected] },
                            `Delete ${attSelected.size} attachment${attSelected.size > 1 ? 's' : ''}?`,
                            true
                          )}
                        >
                          {attDeleting ? 'Deleting…' : `Delete ${attSelected.size}`}
                        </button>
                      )}
                      <button className="btn ghost sm" onClick={() => { setAttEditMode(false); setAttSelected(new Set()); }}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    f.attachments.length > 0 && canEdit &&
                      <EditBtn onClick={() => { setAttEditMode(true); setAttSelected(new Set()); }} label="Edit" />
                  )}
                </div>
              </div>
              <div className="panel-body">
                {f.attachmentError ? (
                  <div className="callout danger" style={{ margin: 0 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <div>Could not load attachments — {f.attachmentError}</div>
                  </div>
                ) : (
                  <div className="att-grid">
                    {visibleAttachments.map(att => {
                      const rawId = typeof att.id === 'string' ? parseInt(att.id.replace('att-', ''), 10) : att.id;
                      const checked = attSelected.has(rawId);
                      return (
                        <div key={att.id || att.name} style={{ position: 'relative' }}>
                          {/* Selection overlay in edit mode */}
                          {attEditMode && (
                            <div
                              onClick={() => {
                                setAttSelected(prev => {
                                  const next = new Set(prev);
                                  next.has(rawId) ? next.delete(rawId) : next.add(rawId);
                                  return next;
                                });
                              }}
                              style={{
                                position: 'absolute', inset: 0, zIndex: 10,
                                borderRadius: 8, cursor: 'pointer',
                                background: checked ? 'rgba(79,70,229,0.12)' : 'rgba(0,0,0,0.03)',
                                border: checked ? '2px solid var(--brand)' : '2px solid transparent',
                                transition: 'background .15s, border-color .15s',
                              }}
                            >
                              <div style={{
                                position: 'absolute', top: 8, left: 8,
                                width: 18, height: 18, borderRadius: 5,
                                background: checked ? 'var(--brand)' : 'var(--surface)',
                                border: `2px solid ${checked ? 'var(--brand)' : 'var(--border)'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all .15s',
                              }}>
                                {checked && (
                                  <svg viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round" style={{ width: 10, height: 10 }}>
                                    <polyline points="2 6 5 9 10 3"/>
                                  </svg>
                                )}
                              </div>
                            </div>
                          )}
                          <AttachmentItem attachment={att} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Comments panel */}
            <div className="panel">
              <div className="panel-head">
                <div className="panel-title">Comments</div>
              </div>
              <div className="panel-body">
                <Comments
                  caseId={id}
                  onAttachmentAdded={refreshAfterActivity}
                  onCommentPosted={() => setHistoryKey(k => k + 1)}
                  onAcknowledged={() => setHistoryKey(k => k + 1)}
                  refreshKey={commentsRefreshKey}
                  existingAttachmentNames={(f.attachments || []).map(a => a.name)}
                  caseRegion={f.region}
                  caseClinic={f.clinic}
                  caseTreatment={f.treatment}
                  caseSeverity={f.severity}
                  dateOfIncident={f.dateOfIncident ? String(f.dateOfIncident).slice(0, 10) : null}
                  canManageRequirements={!!user?.canManageRequirements && !f.deletedAt}
                  requirementTypes={config.requirementTypes || []}
                  requirements={caseRequirements}
                  onRequirementAdded={refreshAfterActivity}
                  slaEligible={slaEligible && !f.deletedAt}
                  canAcknowledge={canAcknowledge && !f.deletedAt}
                  sla={sla}
                />
              </div>
            </div>

          </div>

          {/* ── RIGHT column — the register: where, who, what changed ── */}
          <div className="detail-col">
            {/* Case information panel */}
            <div className="panel">
              <div className="panel-head">
                <div className="panel-title">Case information</div>
                {canEdit && <EditBtn onClick={() => openEdit('info', {
                  Severity:   f.severity   || '',
                  Clinic:     f.clinic     || '',
                  Region:     f.region     || '',
                  Country:    f.country    || '',
                  ReviewDate: f.reviewDate ? String(f.reviewDate).slice(0, 10) : '',
                })} />}
              </div>
              <div className="panel-body">
                <div className="info-list">
                  {/* Ref, treatment, severity, status and incident date are read
                      from the header / Incident details — not repeated here. */}
                  <InfoRow label="Clinic"           value={f.clinic} />
                  <InfoRow label="Region"           value={f.region} />
                  <InfoRow label="Country"          value={f.country} />
                  <InfoRow label="Date Reported"    value={f.reportedDate   ? formatDate(f.reportedDate)   : null} />
                  <InfoRow label="Review date"      value={f.reviewDate     ? formatDate(f.reviewDate)     : null} emptyText="Not set" />
                  <InfoRow label="Last Updated"     value={f.modifiedDate   ? formatDate(f.modifiedDate)   : null} />
                </div>
              </div>
            </div>

            {/* Staff panel */}
            <div className="panel">
              <div className="panel-head">
                <div className="panel-title">Staff</div>
                {canEdit && <EditBtn onClick={() => openEdit('staff', {
                  StaffMemberConcerned:     f.staffMember   || '',
                  ClinicManagerResponsible: f.clinicManager || '',
                  MedicalTeamMemberResponsible: f.medicalTeam || '',
                })} />}
              </div>
              <div className="panel-body">
                <div className="info-list">
                  <InfoRow label="Staff Member Concerned"     value={f.staffMember} />
                  <InfoRow label="Clinic Manager Responsible" value={f.clinicManager} />
                  <InfoRow label={medicalTeamLabel(f.eventType)} value={f.medicalTeam} />
                </div>
              </div>
            </div>

            {/* History */}
            <HistoryCard caseId={id} refreshKey={historyKey} onViewAll={() => setActiveTab('history')} role={user?.role} canSeeCosts={!!user?.canSeeCosts} />
          </div>
        </div>
      )}

      {/* ───────── Report tab ───────── */}
      {activeTab === 'report' && user?.canUseCaseReports && (
        <ReportPanel f={f} resolutionText={resolutionText} caseId={id} tags={c.tags || []} sla={sla}
          requirements={caseRequirements} role={user?.role} canSeeCosts={!!user?.canSeeCosts} />
      )}

      {/* ───────── Costs tab ───────── */}
      {activeTab === 'costs' && user?.canSeeCosts && (
        <Costs caseId={parseInt(id, 10)} country={f.country} onChange={() => setHistoryKey(k => k + 1)} />
      )}

      {/* ───────── History tab ───────── */}
      {activeTab === 'history' && <HistoryPanel caseId={id} refreshKey={historyKey} role={user?.role} canSeeCosts={!!user?.canSeeCosts} />}

      {/* ───────── Mark resolved modal ───────── */}
      {resolveOpen && (
        <ResolveCaseModal
          resolution={resolveText}
          setResolution={setResolveText}
          onClose={() => !saving && setResolveOpen(false)}
          onSubmit={submitResolve}
          saving={saving}
          error={saveError}
        />
      )}

      {/* ───────── Confirm dialog (all saves) ───────── */}
      {pendingAction && (
        <ConfirmDialog
          message={pendingAction.label}
          detail="This change will be saved and logged in the case history."
          confirmLabel={pendingAction.danger ? 'Yes, proceed' : 'Save changes'}
          danger={pendingAction.danger}
          saving={saving}
          onConfirm={executeSave}
          onCancel={cancelAction}
        />
      )}

      {/* ───────── Edit: Incident details ─────────── */}
      {editPanel === 'incident' && (
        <EditModal
          title="Edit Incident Details"
          onClose={() => setEditPanel(null)}
          onSave={() => {
            const updates = {
              DateofIncident:        editDraft.DateofIncident,
              Department:            editDraft.Department,
              TreatmentSubtype:      resolveTreatmentSubtypeValue(editDraft.TreatmentSubtype, editDraft.TreatmentSubtypeOther) || null,
              IncidentCategory:      editDraft.IncidentCategory || null,
              IncidentCategoryOther: editDraft.IncidentCategory === 'Other' ? editDraft.IncidentCategoryOther : null,
              ServiceCarriedOut:     editDraft.ServiceCarriedOut,
              ClientPhoneNumber:     editDraft.ClientPhoneNumber || null,
              Title:                 editDraft.Title,
            };
            // Incident-specific extras → type_specific bag (server partial-merges;
            // a blank value deletes that key). Only the fields shown in the modal
            // are sent, so requirements + any other bag keys are untouched.
            if (modalIncidentKeys.length > 0) {
              const ts = {};
              for (const k of modalIncidentKeys) {
                const v = (editDraft.ts || {})[k];
                ts[k] = v === undefined ? '' : v;
              }
              updates.TypeSpecific = ts;
            }
            requestSave(updates, 'Save changes to Incident Details?');
          }}
          saving={saving}
          error={saveError}
        >
          <div className="field">
            <label>Date of Incident</label>
            <DateInput
              value={editDraft.DateofIncident || ''}
              max={todayIso()}
              onChange={e => setEditDraft(p => ({ ...p, DateofIncident: e.target.value }))} />
          </div>
          <div className="field-row">
            <div className="field">
              <label>Treatment / Department</label>
              {/* Locked after submission — switching treatment changes the whole
                  data structure (subtype/category cascade + type-specific fields),
                  which would orphan captured data. If it's wrong, re-submit. */}
              {(() => {
                const opts = filterByEventType(config.treatmentTypes, f.eventType, { includeValue: editDraft.Department });
                const cur = opts.find(t => t.value === editDraft.Department);
                const label = cur ? `${cur.label}${cur.description ? ` — ${cur.description}` : ''}` : (editDraft.Department || '—');
                return (
                  <div className="input" style={{ background: 'var(--surface-2)', color: 'var(--text-3)', cursor: 'not-allowed' }}>
                    {label}
                  </div>
                );
              })()}
              <div className="field-hint">Locked after submission — re-submit the case to change the treatment.</div>
            </div>
            <div className="field">
              <label>Service Carried Out</label>
              <CharCountInput fieldKey="ServiceCarriedOut" type="text" placeholder="e.g. Full legs laser"
                value={editDraft.ServiceCarriedOut || ''}
                onChange={e => setEditDraft(p => ({ ...p, ServiceCarriedOut: e.target.value }))} />
            </div>
          </div>
          {/* Treatment subtype — only when the treatment defines subtypes (e.g. AWI → Toxin/Filler).
              Stays editable: the edit form is admin/supervisor-only and a subtype change is
              within-treatment (fields are mainly the same), so it doesn't switch structures. */}
          {(() => {
            const subtypeOpts = subtypesForTreatment(
              config.treatmentTypes, editDraft.Department,
              config.incidentCategoriesByTreatmentAndCode, config.severityCodes,
            );
            if (!subtypeOpts.length) return null;
            return (
              <div className="field">
                <label htmlFor="TreatmentSubtype">Treatment subtype</label>
                <SearchableSelect
                  id="TreatmentSubtype" name="TreatmentSubtype"
                  value={editDraft.TreatmentSubtype || ''}
                  onChange={e => setEditDraft(p => ({
                    ...p,
                    TreatmentSubtype: e.target.value,
                    TreatmentSubtypeOther: e.target.value === SUBTYPE_OTHER ? p.TreatmentSubtypeOther : '',
                    IncidentCategory: '',
                    IncidentCategoryOther: '',
                  }))}
                  placeholder="Select subtype…"
                  options={[
                    ...subtypeOpts.map(s => ({ value: s, label: s })),
                    { value: SUBTYPE_OTHER, label: SUBTYPE_OTHER },
                  ]}
                />
                {editDraft.TreatmentSubtype === SUBTYPE_OTHER && (
                  <CharCountInput fieldKey="TreatmentSubtypeOther" style={{ marginTop: 8 }} placeholder="Describe the subtype…"
                    value={editDraft.TreatmentSubtypeOther || ''}
                    onChange={e => setEditDraft(p => ({ ...p, TreatmentSubtypeOther: e.target.value }))} />
                )}
              </div>
            );
          })()}
          {/* Incident category — editable for EVERY treatment (subtype or not). Even when
              the cascade has no configured options for this treatment/subtype/code, 'Other'
              is always available (free-text fallback) and any existing value is preserved in
              the list so editing can never silently drop it. */}
          {(() => {
            const effectiveSubtype = resolveTreatmentSubtypeValue(
              editDraft.TreatmentSubtype, editDraft.TreatmentSubtypeOther,
            );
            const catOpts = getIncidentCategories(config, editDraft.Department, effectiveSubtype, f.severity);
            const opts = [...new Set([
              ...(editDraft.IncidentCategory && editDraft.IncidentCategory !== 'Other' ? [editDraft.IncidentCategory] : []),
              ...catOpts,
              'Other',
            ])];
            return (
              <div className="field">
                <label>Incident category</label>
                <select className="select"
                  value={editDraft.IncidentCategory || ''}
                  onChange={e => setEditDraft(p => ({ ...p, IncidentCategory: e.target.value }))}>
                  <option value="">Select…</option>
                  {opts.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {editDraft.IncidentCategory === 'Other' && (
                  <CharCountInput fieldKey="IncidentCategoryOther" style={{ marginTop: 8 }} placeholder="Describe the incident category…"
                    value={editDraft.IncidentCategoryOther || ''}
                    onChange={e => setEditDraft(p => ({ ...p, IncidentCategoryOther: e.target.value }))} />
                )}
              </div>
            );
          })()}
          <div className="field-row">
            <div className="field">
              <label>Client Name</label>
              <CharCountInput fieldKey="Title" type="text"
                value={editDraft.Title || ''}
                onChange={e => setEditDraft(p => ({ ...p, Title: e.target.value }))} />
            </div>
            <div className="field">
              <label>Client phone</label>
              <CharCountInput fieldKey="ClientPhoneNumber" type="text" placeholder="Phone number"
                value={editDraft.ClientPhoneNumber || ''}
                onChange={e => setEditDraft(p => ({ ...p, ClientPhoneNumber: e.target.value }))} />
            </div>
          </div>
          {/* Incident-specific details — fill in the treatment/code requirement
              fields (laser serial no., skin type, training date, …), including
              any left blank at submission. */}
          {modalIncidentKeys.length > 0 && (
            <>
              <div className="edit-subhead">Incident-specific details</div>
              {modalIncidentKeys.map(k => (
                <IncidentExtraField key={k} fieldKey={k}
                  optional={isOptionalIncidentField(modalTreatment, f.severity, k)}
                  value={(editDraft.ts || {})[k]}
                  onChange={v => setEditDraft(p => ({ ...p, ts: { ...(p.ts || {}), [k]: v } }))} />
              ))}
            </>
          )}
        </EditModal>
      )}

      {/* ───────── Edit: Near Miss / Clinic Incident details ─────────── */}
      {editPanel === 'ntDetails' && (
        <EditModal
          title={isRefusalRegisterCase({ eventType: f.eventType, incidentCategory: f.incidentCategory })
            ? 'Edit Refusal Details'
            : f.eventType === 'NearMiss' ? 'Edit Near Miss Details' : 'Edit Clinic Incident Details'}
          onClose={() => setEditPanel(null)}
          onSave={() => requestSave(
            {
              // A register entry has no separate date field — the date of incident
              // follows the recorded date and time of the request.
              DateofIncident:   refusalDateOf((editDraft.ts || {})[REFUSAL_TS_KEYS.requestedAt]) || editDraft.DateofIncident,
              Title:            editDraft.Title,
              IncidentCategory: editDraft.IncidentCategory || null,
              TypeSpecific:     editDraft.ts || {},
            },
            'Save changes to incident details?'
          )}
          saving={saving}
          error={saveError}
        >
          {nonTreatmentEditFields(f.eventType, f.incidentCategory).map(fld => {
            const val = fld.kind === 'top' ? (editDraft[fld.key] || '') : ((editDraft.ts || {})[fld.key] || '');
            const set = (v) => setEditDraft(p => fld.kind === 'top'
              ? { ...p, [fld.key]: v }
              : { ...p, ts: { ...(p.ts || {}), [fld.key]: v } });
            return (
              <div className="field" key={`${fld.kind}-${fld.key}`}>
                <label>{fld.label}</label>
                {fld.type === 'select' ? (
                  <select className="select" value={val} onChange={e => set(e.target.value)}>
                    <option value="">Select…</option>
                    {fld.options.map(o => { const { value, label } = opt(o); return <option key={value} value={value}>{label}</option>; })}
                  </select>
                ) : fld.type === 'textarea' ? (
                  <CharCountTextarea fieldKey={fld.key} value={val} onChange={e => set(e.target.value)} style={{ minHeight: 80 }} />
                ) : fld.type === 'datetime-local' ? (
                  <DateTimeExtraField fieldKey={fld.key} value={val} onChange={(_k, v) => set(v)} />
                ) : fld.type === 'date' ? (
                  <DateInput value={val}
                    max={fld.key === 'DateofIncident' ? todayIso() : undefined}
                    onChange={e => set(e.target.value)} />
                ) : (
                  <CharCountInput fieldKey={fld.key} type={fld.type} value={val}
                    onChange={e => set(e.target.value)} />
                )}
              </div>
            );
          })}
        </EditModal>
      )}

      {/* ───────── Edit: Staff ─────────── */}
      {editPanel === 'staff' && (
        <EditModal
          title="Edit Staff"
          onClose={() => setEditPanel(null)}
          onSave={() => requestSave(
            { StaffMemberConcerned: editDraft.StaffMemberConcerned, ClinicManagerResponsible: editDraft.ClinicManagerResponsible, MedicalTeamMemberResponsible: editDraft.MedicalTeamMemberResponsible },
            'Save changes to Staff?'
          )}
          saving={saving}
          error={saveError}
        >
          <div className="field">
            <label>Staff Member Concerned</label>
            <CharCountInput fieldKey="StaffMemberConcerned" type="text" placeholder="Full name"
              value={editDraft.StaffMemberConcerned || ''}
              onChange={e => setEditDraft(p => ({ ...p, StaffMemberConcerned: e.target.value }))} />
          </div>
          <div className="field">
            <label>Clinic Manager Responsible</label>
            <CharCountInput fieldKey="ClinicManagerResponsible" type="text" placeholder="Full name"
              value={editDraft.ClinicManagerResponsible || ''}
              onChange={e => setEditDraft(p => ({ ...p, ClinicManagerResponsible: e.target.value }))} />
          </div>
          <div className="field">
            <label>{medicalTeamLabel(f.eventType)}</label>
            <StaffPicker
              value={editDraft.MedicalTeamMemberResponsible || ''}
              onChange={v => setEditDraft(p => ({ ...p, MedicalTeamMemberResponsible: v }))}
              options={hsrcStaff}
              placeholder={hsrcStaff.length ? 'Start typing to search staff…' : 'Full name'}
            />
            {hsrcStaff.length > 0 && (
              <div className="field-hint">Suggestions from the Health Safety &amp; Compliance and Injectables Growth directory — or type any name.</div>
            )}
          </div>
        </EditModal>
      )}

      {/* ───────── Edit: Case information ─────────── */}
      {editPanel === 'info' && (
        <EditModal
          title="Edit Case Information"
          onClose={() => setEditPanel(null)}
          onSave={() => {
            // Only send what actually changed. Location fields are rejected
            // outright for a clinic/region-scoped caller (server-side), so an
            // untouched Clinic must not ride along on a severity-only edit.
            const same = (a, b) => (a || '').trim() === (b || '').trim();
            const updates = {};
            if (treatmentHasCodes(f.treatment) && !same(editDraft.Severity, f.severity)) updates.Severity = editDraft.Severity;
            if (!same(editDraft.Clinic,  f.clinic))  updates.Clinic  = editDraft.Clinic;
            if (!same(editDraft.Region,  f.region))  updates.Region  = editDraft.Region;
            if (!same(editDraft.Country, f.country)) updates.Country = editDraft.Country;
            // Review date — a cleared picker sends null so the column is unset.
            const curReview = f.reviewDate ? String(f.reviewDate).slice(0, 10) : '';
            if (!same(editDraft.ReviewDate, curReview)) updates.ReviewDate = editDraft.ReviewDate || null;
            if (!Object.keys(updates).length) { setEditPanel(null); return; }
            requestSave(updates, 'Save changes to Case Information?');
          }}
          saving={saving}
          error={saveError}
        >
          {treatmentHasCodes(f.treatment) && (
            <div className="field">
              <label>Severity</label>
              <select className="select"
                value={editDraft.Severity || ''}
                onChange={e => setEditDraft(p => ({ ...p, Severity: e.target.value }))}>
                <option value="">Select severity…</option>
                {filterByEventType(config.severityCodes, f.eventType, { includeValue: editDraft.Severity }).map(c => (
                  <option key={c.key} value={c.value}>{c.value}{c.description ? ` — ${c.description}` : ''}</option>
                ))}
              </select>
            </div>
          )}
          <CaseLocationFields
            draft={editDraft}
            onChange={patch => setEditDraft(p => ({ ...p, ...patch }))}
            clinics={clinicDir}
            loadError={clinicDirError}
          />
          {/* Review date — when the client was / will be seen for review. Past
              and future dates are both allowed (a review may already have
              happened); required at submission for Code 3–5, optional here. */}
          <div className="field">
            <label>Review date</label>
            <DateInput
              value={editDraft.ReviewDate || ''}
              clearable
              onChange={e => setEditDraft(p => ({ ...p, ReviewDate: e.target.value }))} />
            <div className="field-hint">
              When the client was, or will be, seen for review. Once the review has taken place, add a comment to the case.
            </div>
          </div>
        </EditModal>
      )}

      {/* ───────── Edit: Notes (replace) ─────────── */}
      {editPanel === 'notes' && (
        <EditModal
          title={`${f.medicalNotes ? 'Edit' : 'Add'} ${notesLabel(f.eventType, { caps: true })}`}
          onClose={() => setEditPanel(null)}
          onSave={() => requestSave(
            { MedicalNotes: editDraft.medicalNotes },
            f.medicalNotes ? `Save changes to ${notesLabel(f.eventType, { caps: true })}?` : `Add ${notesLabel(f.eventType, { caps: true })}?`
          )}
          saving={saving}
          error={saveError}
        >
          <div className="field">
            <label>{notesLabel(f.eventType, { caps: true })}{f.eventType === 'NearMiss' || f.eventType === 'ClinicIncident' ? '' : ' / Clinical Description'}</label>
            <CharCountTextarea
              fieldKey="MedicalNotes"
              style={{ minHeight: 180 }}
              placeholder="Describe the adverse event, symptoms, and any actions taken…"
              value={editDraft.medicalNotes || ''}
              onChange={e => setEditDraft(p => ({ ...p, medicalNotes: e.target.value }))}
              autoFocus
            />
            <div className="field-hint">Be factual · avoid speculation · no client names or contact details</div>
          </div>
        </EditModal>
      )}

      {/* ───────── Add: Append a new dated note ─────────── */}
      {editPanel === 'notes-append' && (
        <EditModal
          title="Add Note"
          onClose={() => setEditPanel(null)}
          onSave={() => {
            const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            const separator = `\n\n• ${today}\n`;
            const combined = (f.medicalNotes || '').trimEnd() + separator + (editDraft.newNote || '').trim();
            requestSave({ MedicalNotes: combined }, `Append new note to ${notesLabel(f.eventType, { caps: true })}?`);
          }}
          saving={saving}
          error={saveError}
        >
          {/* Existing notes — read-only context */}
          {f.medicalNotes && (
            <div style={{
              marginBottom: 16, padding: '10px 12px',
              background: 'var(--surface-2)', border: '1px solid var(--border)',
              borderRadius: 6, fontSize: 12.5, lineHeight: 1.7,
              color: 'var(--text-3)', whiteSpace: 'pre-line',
              maxHeight: 120, overflowY: 'auto',
            }}>
              {f.medicalNotes}
            </div>
          )}
          <div className="field">
            <label>
              New note
              <span style={{ fontWeight: 400, color: 'var(--text-3)', marginLeft: 6, fontSize: 11 }}>
                — will be appended with today's date
              </span>
            </label>
            <CharCountTextarea
              fieldKey="newNote"
              style={{ minHeight: 120 }}
              placeholder="Add a clinical update, follow-up notes, or additional information…"
              value={editDraft.newNote || ''}
              onChange={e => setEditDraft(p => ({ ...p, newNote: e.target.value }))}
              autoFocus
            />
            <div className="field-hint">Be factual · avoid speculation · no client names or contact details</div>
          </div>
        </EditModal>
      )}
    </>
  );
}
